import { Kcc20MintAvailabilityFlowService } from '../../services/token-actions/kcc20-mint-availability-flow.service';
import { Dialog } from '@angular/cdk/dialog';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  AlertVariant,
  KcAlertComponent,
  KcButtonComponent,
  KcIconComponent,
  KcInputComponent,
  KcLoaderBarComponent,
  KcLoaderComponent,
  KcLoaderRingComponent,
  KcLoaderSkeletonComponent,
  KcNumberInputComponent,
  KcSkeletonComponent,
  KcSwitchComponent,
  KcTableComponent,
  KcToggleComponent,
  KcTooltipDirective,
  NotificationService,
  SwitchOption,
  TableColumn,
} from '@kaspacom/ui-kit';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  distinctUntilChanged,
  finalize,
  firstValueFrom,
  interval,
  map,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { calculateKcc20MaximumBuyTokenBaseUnits } from '@kaspacom/kcc20-tx-builder/trade-fee';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';
import {
  LadderLimitReviewDialogComponent,
  LadderLimitReviewDialogData,
} from '../../components/ladder-limit-review-dialog/ladder-limit-review-dialog.component';
import {
  KCC20_DEFAULT_CHART_INTERVAL_MS,
  Kcc20ChartIntervalMs,
  candleLimitFor,
} from '../../components/price-chart/kcc20-chart-intervals';
import { PriceChartComponent } from '../../components/price-chart/price-chart.component';
import { TokenLogoComponent } from '../../components/token-logo/token-logo.component';
import { NotificationGroupSync } from '../../helpers/notification-group-sync';
import { WalletManager } from '../../managers/wallet.manager';
import { Kcc20TokenAmountPipe } from '../../pipes/kcc20-token-amount.pipe';
import { TruncateWalletAddressPipe } from '../../pipes/truncate-wallet-address.pipe';
import {
  Kcc20MetadataApiService,
  Kcc20TokenMetadata,
  Kcc20TokenMetadataPermission,
} from '../../services/metadata/kcc20-metadata-api.service';
import { Kcc20HoldingsService } from '../../services/portfolio/kcc20-holdings.service';
import {
  Kcc20SweepActionDraft,
  Kcc20TokenActionApiService,
  Kcc20UnsignedTokenActionPayload,
} from '../../services/token-actions/kcc20-token-action-api.service';
import { Kcc20TokenConsolidationFlowService } from '../../services/token-actions/kcc20-token-consolidation-flow.service';
import {
  Kcc20FeeTicketAvailable,
  Kcc20RewardsApiService,
} from '../../services/rewards/kcc20-rewards-api.service';
import {
  Kcc20TokenApiService,
  Kcc20TokenDetail,
  Kcc20TokenHolder,
  Kcc20TokenPageActions,
  Kcc20TokenPageActionsResponse,
  Kcc20TokenHoldersSummary,
  Kcc20TokenSupply,
  Kcc20TokenSupplySummary,
  Kcc20TokenTradingSummary,
  Kcc20WrapperSummary,
} from '../../services/tokens/kcc20-token-api.service';
import { Kcc20TokenStaticProfileCacheService } from '../../services/tokens/kcc20-token-static-profile-cache.service';
import {
  Kcc20WrapperFlowService,
  wrapperReadyToBuild,
} from '../../services/wrappers/kcc20-wrapper-flow.service';
import {
  Kcc20AccountPosition,
  Kcc20Activity,
  Kcc20ActivityScope,
  Kcc20AddressOrderSortBy,
  Kcc20Balance,
  Kcc20Candle,
  Kcc20DepthLevel,
  Kcc20MarketSummary,
  Kcc20Order,
  Kcc20Orderbook,
  Kcc20Quote,
  Kcc20Stats,
  Kcc20TradingApiService,
} from '../../services/trading/kcc20-trading-api.service';
import { Kcc20SortDirection } from '../../services/core/kcc20-page-api.models';
import { Kcc20WatchlistStateService } from '../../services/user-state/kcc20-watchlist-state.service';
import {
  Kcc20WalletOperationTask,
  Kcc20WalletOperationTaskTrackerService,
} from '../../services/wallet-operations/kcc20-wallet-operation-task-tracker.service';
import { Kcc20WalletOperationQueueService } from '../../services/wallet-operations/kcc20-wallet-operation-queue.service';
import { logWalletOperation } from '../../services/wallet-operations/wallet-operation-diagnostics.util';
import {
  WALLET_OPERATION_LABELS,
  limitOrderLabel,
  marketOrderLabel,
  mintAvailabilityLabel,
} from '../../services/wallet-operations/wallet-operation-title.util';
import {
  formatCount,
  formatDateMs,
  formatDateOnlyMs,
  formatSompi,
  formatSpreadPercent,
  formatSpreadPercentFromSpread,
  formatTimeOnlyMs,
  shortId,
  tokenIdentityDisplay,
  tokenLabel,
} from '../../shared/kcc20-formatters';
import {
  kcc20ActivityAction,
  kcc20ActivityDirection,
  kcc20ActivitySemanticSide,
  kcc20ActivityType,
} from '../../shared/kcc20-activity-labels';
import { kaspaExplorerTxUrl } from '../../shared/kaspa-explorer';
import {
  KCC20_FIXED_DECIMALS,
  KCC20_MIN_PAID_MINT_KAS_LABEL,
  MINT_AMOUNT_WARNING_DEBOUNCE_MS,
  KCC20_MIN_PROTOCOL_FEE_KAS_LABEL,
  KCC20_MIN_PROTOCOL_FEE_SOMPI,
  KCC20_PUBLIC_MINT_MAX_AMOUNT_ERROR,
  KCC20_PUBLIC_MINT_MAX_DISPLAY_TOKENS_INPUT,
  formatKcc20BaseUnitsCompact,
  formatKcc20BaseUnitsForDisplay,
  formatKcc20BaseUnitsValue,
  formatKcc20BaseUnitsValueGrouped,
  calculateKcc20OrderTotalSompi,
  calculateKcc20PaidMintAmountSompi,
  clampKcc20PublicMintDisplayAmount,
  minPaidMintTokenDisplayAmount,
  calculateKcc20ProtocolFeeSompi,
  exceedsKcc20PublicMintMaxAmount,
  isPositiveKcc20DisplayAmount,
  kcc20FillGrossSompi,
  kcc20FillProtocolFeeSompi,
  kcc20FillSettlementSompi,
  kcc20PaidMintAdjustmentMessage,
  normalizeKcc20Decimals,
  parseKcc20DisplayAmountToBaseUnits,
  scaleKcc20DecimalBaseValue,
} from '../../shared/kcc20-token-amount';
import {
  feeTicketInsufficientReason,
  feeTicketRequiredCountForExecution,
  kcc20FeeTicketFeatureEnabled,
} from '../../shared/kcc20-fee-ticket.util';
import {
  StatusTagStatus,
  statusTagClass,
  statusTagSelected,
} from '../../shared/status-tag';
import { kcc20SideLabel } from '../../shared/kcc20-trade-side';
import {
  LADDER_MAX_ORDERS,
  LadderDirection,
  ladderMeetsMinOrderValue,
  parseLadderPercent,
  planLadderLimitOrders,
} from '../../shared/ladder-limit-orders.util';
import {
  readTokenTicketLadderPreferences,
  writeTokenTicketLadderPreferences,
} from './token-ticket-ladder-preferences';
import {
  isValidMetadataUrl,
  isKaspaAddressFormat,
  clipMetadataDescription,
} from '../../utils/form-validators.util';
import {
  formatPrettyNumber,
  kasToSompi,
  sompiToKas,
} from '../../utils/number-format.util';

type QuoteSide = 'buy' | 'sell';
type OrderMode = 'limit' | 'market';
export const KCC20_MAX_SINGLE_HOLDER_SWEEP_BID_LEGS = 10;
const ORDERBOOK_POLL_INTERVAL_MS = 5000;
type TokenActionTab = 'book' | 'mint' | 'wrap';
type TokenLowerTab =
  | 'tradingActivity'
  | 'tokenActivity'
  | 'holders'
  | 'position'
  | 'orders';
type WrapMode = 'wrap' | 'unwrap';
type OrderbookSide = 'ask' | 'bid';
const TOKEN_ACTION_AVAILABILITY_PENDING_REASON =
  'Token action availability is still loading.';
const MARKET_QUOTE_REFRESH_MESSAGE =
  'Refresh the market quote before building a market fill.';

/** Backend disable copy for deployWrapper when a market is already live. */
export function isExistingWrapperReason(
  reason: string | null | undefined,
): boolean {
  return /wrapper already exists/i.test(reason?.trim() ?? '');
}

/** Activity API scope for Trading Activity vs Token Activity tabs. */
export function activityScopeForTab(
  tab: 'tradingActivity' | 'tokenActivity',
): Extract<Kcc20ActivityScope, 'trading' | 'token'> {
  return tab === 'tokenActivity' ? 'token' : 'trading';
}

/** Resolved holder count for the Holders column badge (undefined until known). */
export function resolveHoldersCount(
  holderCount: number | null | undefined,
  holdersTotal = 0,
): number | undefined {
  const count = holderCount ?? (holdersTotal > 0 ? holdersTotal : undefined);
  if (count === undefined || count === null) {
    return undefined;
  }
  return count;
}

/** Column key → backend sort field for the token-page open-orders table. */
const TOKEN_OPEN_ORDER_COLUMN_SORT_BY: Partial<
  Record<string, Kcc20AddressOrderSortBy>
> = {
  side: 'side',
  orderId: 'orderId',
  createdAtMs: 'createdAtMs',
};

export interface Kcc20DepthDisplayRow {
  level: Kcc20DepthLevel;
  depthPct: number;
  cumulativeTokenAmount: string;
}

function orderUnitPriceSompi(
  order: Kcc20Order | undefined,
): string | undefined {
  const price = (order?.unitPriceSompi || order?.priceSompi || '').trim();
  return price || undefined;
}

function orderRemainingTokenAmount(
  order: Kcc20Order | undefined,
): string | undefined {
  const amount = (
    order?.remainingTokenAmount ||
    order?.lockedTokenAmount ||
    order?.tokenAmount ||
    ''
  ).trim();
  return amount || undefined;
}

export function kcc20DepthRowsForDisplay(
  orderbook: Kcc20Orderbook | undefined,
  side: OrderbookSide,
  rowLimit: number,
): Kcc20DepthDisplayRow[] {
  const serverLevels =
    side === 'ask' ? orderbook?.askDepth : orderbook?.bidDepth;
  const rawOrders = side === 'ask' ? orderbook?.asks : orderbook?.bids;
  const levels = serverLevels?.length
    ? normalizeDepthLevels(serverLevels)
    : aggregateDepthFromOrders(rawOrders);
  const sorted = levels.sort((left, right) =>
    side === 'ask'
      ? compareBigIntStrings(left.unitPriceSompi, right.unitPriceSompi)
      : compareBigIntStrings(right.unitPriceSompi, left.unitPriceSompi),
  );
  const cumulativeByPrice = new Map<string, string>();
  let cumulative = 0n;
  for (const level of sorted) {
    cumulative += parsePositiveBigInt(level.tokenAmount) ?? 0n;
    cumulativeByPrice.set(level.unitPriceSompi, cumulative.toString());
  }
  const visible = sorted.slice(0, rowLimit);
  const displayLevels = side === 'ask' ? [...visible].reverse() : visible;
  const max = displayLevels.reduce((current, level) => {
    const amount = parsePositiveBigInt(level.tokenAmount) ?? 0n;
    return amount > current ? amount : current;
  }, 0n);
  return displayLevels.map((level) => ({
    level,
    depthPct: depthPercent(level.tokenAmount, max),
    cumulativeTokenAmount:
      cumulativeByPrice.get(level.unitPriceSompi) ?? level.tokenAmount,
  }));
}

function normalizeDepthLevels(
  levels: Kcc20DepthLevel[] | undefined,
): Kcc20DepthLevel[] {
  const byPrice = new Map<
    string,
    { tokenAmount: bigint; orderCount: number }
  >();
  for (const level of levels ?? []) {
    const price = parsePositiveBigInt(level.unitPriceSompi);
    const amount = parsePositiveBigInt(level.tokenAmount);
    if (price === undefined || amount === undefined) {
      continue;
    }
    const key = price.toString();
    const existing = byPrice.get(key);
    byPrice.set(key, {
      tokenAmount: (existing?.tokenAmount ?? 0n) + amount,
      orderCount: (existing?.orderCount ?? 0) + Math.max(level.orderCount, 1),
    });
  }
  return [...byPrice.entries()].map(([unitPriceSompi, level]) => ({
    unitPriceSompi,
    tokenAmount: level.tokenAmount.toString(),
    orderCount: level.orderCount,
  }));
}

function aggregateDepthFromOrders(
  orders: Kcc20Order[] | undefined,
): Kcc20DepthLevel[] {
  const byPrice = new Map<
    string,
    { tokenAmount: bigint; orderCount: number }
  >();
  for (const order of orders ?? []) {
    const price = parsePositiveBigInt(order.unitPriceSompi || order.priceSompi);
    const amount = parsePositiveBigInt(
      order.remainingTokenAmount ||
        order.lockedTokenAmount ||
        order.tokenAmount,
    );
    if (price === undefined || amount === undefined) {
      continue;
    }
    const key = price.toString();
    const existing = byPrice.get(key);
    byPrice.set(key, {
      tokenAmount: (existing?.tokenAmount ?? 0n) + amount,
      orderCount: (existing?.orderCount ?? 0) + 1,
    });
  }
  return [...byPrice.entries()].map(([unitPriceSompi, level]) => ({
    unitPriceSompi,
    tokenAmount: level.tokenAmount.toString(),
    orderCount: level.orderCount,
  }));
}

function compareBigIntStrings(left: string, right: string): number {
  const leftValue = parsePositiveBigInt(left) ?? 0n;
  const rightValue = parsePositiveBigInt(right) ?? 0n;
  if (leftValue === rightValue) return 0;
  return leftValue > rightValue ? 1 : -1;
}

function parsePositiveBigInt(
  value: string | null | undefined,
): bigint | undefined {
  const raw = value?.trim();
  if (!raw || !/^\d+$/.test(raw)) return undefined;
  const parsed = BigInt(raw);
  return parsed > 0n ? parsed : undefined;
}

function depthPercent(value: string, max: bigint): number {
  const amount = parsePositiveBigInt(value) ?? 0n;
  if (amount <= 0n || max <= 0n) return 0;
  return Number((amount * 1000n) / max) / 10;
}

export function activityDisplayOwner(
  event: Pick<
    Kcc20Activity,
    'displayOwner' | 'ownerAddress' | 'owner' | 'taker' | 'maker'
  >,
): string {
  return (
    event.displayOwner?.trim() ||
    event.ownerAddress?.trim() ||
    event.taker?.trim() ||
    event.maker?.trim() ||
    event.owner?.trim() ||
    ''
  );
}

export function activityDisplayOwnerLabel(owner: string): string {
  if (!owner || isKaspaAddressFormat(owner)) {
    return owner;
  }
  return /^[a-fA-F0-9]{64}$/.test(owner) ? shortId(owner) : owner;
}

interface TokenActivityRow {
  key: string;
  tokenId: string;
  type: string;
  /** Standard action label, e.g. "Buy", "Sell", "Create Sell", "Wrap". */
  action: string;
  /**
   * Directional intent of the action, used to tint the row: rows carrying a
   * buy/bid or sell/ask side (trade fills and order placements) read `buy`
   * (green) / `sell` (red) like the DeFi activity feed, non-trade operations
   * (mint, wrap, unwrap, transfer) read `info` (blue), and metadata-only
   * actions (cancel) stay `neutral`.
   */
  direction: 'buy' | 'sell' | 'info' | 'neutral';
  /** Address-aware label rendered in the Who column. */
  who: string;
  /** Full address or identifier retained for the cell title. */
  whoFull: string;
  /** Whether the Who label should use wallet-address truncation. */
  whoUsesAddressTruncate: boolean;
  side: string;
  amount: string;
  /** Bare KAS unit price (no suffix), or '--' for non-priced activity (mint/wrap/transfer). */
  unitPrice: string;
  /** Bare KAS total value (unit price x amount), or '--' for non-priced activity. */
  totalValue: string;
  txid: string;
  timeMs: number;
}

type TokenActivityCellTemplate = TemplateRef<{
  $implicit: TokenActivityRow;
  row: TokenActivityRow;
  index: number;
}>;

type TokenOpenOrderCellTemplate = TemplateRef<{
  $implicit: Kcc20Order;
  row: Kcc20Order;
  index: number;
}>;

type TokenSocialIcon =
  | 'website'
  | 'x'
  | 'telegram'
  | 'discord'
  | 'github'
  | 'medium';

interface TokenSocialLink {
  label: string;
  href: string;
  icon: TokenSocialIcon;
}

export const WRAP_TO_TRADE_MESSAGE =
  'Wrap to trade first: this wallet holds KCC20 assets but no DEX Orderbook trading balance yet.';

function normalizeKcc20OwnerKey(
  value: string | undefined | null,
): string | undefined {
  const trimmed = value?.trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.startsWith('0x') ? trimmed.slice(2) : trimmed;
}

export function isKcc20TokenCreator(
  detail: Kcc20TokenDetail | undefined,
  walletOwner: string | undefined,
): boolean {
  const owner = normalizeKcc20OwnerKey(walletOwner);
  if (!owner) {
    return false;
  }
  const creatorOwner = normalizeKcc20OwnerKey(
    detail?.creatorOwner ??
      (detail?.stateOwnerType === 0 ? detail.stateOwner : undefined),
  );
  return creatorOwner === owner;
}

export function isKcc20PublicMintOwner(
  detail: Kcc20TokenDetail | undefined,
  walletOwner: string | undefined,
): boolean {
  return isKcc20TokenCreator(detail, walletOwner);
}

export function canManageToken(
  detail: Kcc20TokenDetail | undefined,
  walletOwner: string | undefined,
  permission: { canEdit?: boolean } | undefined,
): boolean {
  if (!walletOwner) {
    return false;
  }
  return (
    permission?.canEdit === true || isKcc20TokenCreator(detail, walletOwner)
  );
}

@Component({
  selector: 'kcc20-token-page',
  standalone: true,
  imports: [
    FormsModule,
    KcNumberInputComponent,
    RouterLink,
    KcAlertComponent,
    KcIconComponent,
    KcInputComponent,
    EmptyStateComponent,
    PriceChartComponent,
    KcButtonComponent,
    KcLoaderBarComponent,
    KcLoaderComponent,
    KcLoaderRingComponent,
    KcLoaderSkeletonComponent,
    KcSkeletonComponent,
    KcSwitchComponent,
    KcTableComponent,
    KcToggleComponent,
    KcTooltipDirective,
    TokenLogoComponent,
    Kcc20TokenAmountPipe,
    TruncateWalletAddressPipe,
  ],
  templateUrl: './token-page.component.html',
  styleUrl: './token-page.component.scss',
})
export class TokenPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tokenApi = inject(Kcc20TokenApiService);
  private readonly tokenStaticProfiles = inject(
    Kcc20TokenStaticProfileCacheService,
  );
  private readonly tokenActionApi = inject(Kcc20TokenActionApiService);
  private readonly wrapperFlow = inject(Kcc20WrapperFlowService);
  private readonly tokenConsolidationFlow = inject(
    Kcc20TokenConsolidationFlowService,
  );
  private readonly rewardsApi = inject(Kcc20RewardsApiService);
  private readonly tradingApi = inject(Kcc20TradingApiService);
  private readonly holdingsService = inject(Kcc20HoldingsService);
  private readonly metadataApi = inject(Kcc20MetadataApiService);
  private readonly walletOperationTasks = inject(
    Kcc20WalletOperationTaskTrackerService,
  );
  private readonly mintAvailabilityFlow = inject(
    Kcc20MintAvailabilityFlowService,
  );
  private readonly walletOperationQueue = inject(
    Kcc20WalletOperationQueueService,
  );
  private readonly watchlistState = inject(Kcc20WatchlistStateService);
  private readonly notificationService = inject(NotificationService);
  readonly walletManager = inject(WalletManager);
  private readonly dialog = inject(Dialog, { optional: true });
  private loadSequence = 0;
  private ownerHoldingsLoadSequence = 0;
  private openOrdersLoadSequence = 0;
  private tradingSummaryHealSequence = 0;
  private readonly handledWalletOperationTaskIds = new Set<string>();
  private readonly loggedTokenIdMismatchTaskIds = new Set<string>();
  private mintRemainingSupplyErrorNotified = false;
  /** Order-ticket tasks we've already cleared on submit (one reset per task). */
  private readonly resetOrderTicketOnSubmitTaskIds = new Set<string>();
  /** Order-ticket queue actions by action id, scoped to their route token. */
  private readonly orderTicketActionTokenIds = signal<
    ReadonlyMap<string, string>
  >(new Map());
  /** Pending mint-availability updates keyed by covenant id so route changes
   * cannot leak optimistic state or disable controls on another token. */
  private readonly pendingPublicMintAvailability = signal<
    ReadonlyMap<string, boolean>
  >(new Map());
  /**
   * Single quote-refresh trigger. Typing and slider drags use a debounce
   * delay; blur / side / mode / percent buttons / slider release use delay 0.
   * Each new trigger cancels any pending timer and any in-flight /quote HTTP
   * call (switchMap), so a drag cannot stack requests. 1.2s is long enough
   * that typing a multi-digit amount (e.g. 45000) does not quote each pause
   * between digits the way 500ms did.
   */
  private readonly quoteRefresh$ = new Subject<number>();
  private readonly quoteRefreshDebounceMs = 1200;
  private lastQuoteRequestKey: string | undefined;

  readonly environment = environment;
  readonly TagStatus = StatusTagStatus;
  readonly statusTagClass = statusTagClass;
  readonly statusTagSelected = statusTagSelected;
  readonly covenantId = signal(environment.proofTokenId);
  readonly tokenDetail = signal<Kcc20TokenDetail | undefined>(undefined);
  readonly tokenWrappers = signal<Kcc20WrapperSummary[]>([]);
  readonly tokenActions = signal<Kcc20TokenPageActions | undefined>(undefined);
  readonly markets = signal<Kcc20MarketSummary[]>([]);
  readonly metadata = signal<Kcc20TokenMetadata | undefined>(undefined);
  readonly metadataPermission = signal<
    Kcc20TokenMetadataPermission | undefined
  >(undefined);
  readonly orderbook = signal<Kcc20Orderbook | undefined>(undefined);
  readonly stats = signal<Kcc20Stats | undefined>(undefined);
  readonly candles = signal<Kcc20Candle[]>([]);
  readonly activity = signal<Kcc20Activity[]>([]);
  /**
   * Trading Activity feed is append-paged (infinite scroll + "Load more"):
   * each fetch uses limit/offset against the merged feed.
   */
  readonly activityHasNextPage = signal(false);
  readonly activityPageSize = 10;
  /** True while a "Load more" / scroll-append request is in flight. */
  readonly activityLoadingMore = signal(false);
  /** Host for the trading-activity table — scroll-near-bottom loading. */
  readonly activityBody = viewChild<ElementRef<HTMLElement>>('activityBody');
  /** Token-ops activity (mint/transfer/wrap/…) — separate feed + scroll host. */
  readonly tokenActivity = signal<Kcc20Activity[]>([]);
  readonly tokenActivityHasNextPage = signal(false);
  readonly tokenActivityLoadingMore = signal(false);
  readonly tokenActivityBody =
    viewChild<ElementRef<HTMLElement>>('tokenActivityBody');
  readonly openOrdersBody =
    viewChild<ElementRef<HTMLElement>>('openOrdersBody');
  private activityScrollCleanup?: () => void;
  private tokenActivityScrollCleanup?: () => void;
  private openOrdersScrollCleanup?: () => void;
  /** Guards stale activity responses from clobbering a newer load/append. */
  private activityLoadGeneration = 0;
  private tokenActivityLoadGeneration = 0;
  /** Lazy-load gate so Token Activity is fetched on first tab visit. */
  private tokenActivityHydrated = false;
  /** Lazy-load gate so Holders are fetched on first tab visit. */
  private holdersHydrated = false;
  readonly ownerTokenBalance = signal<Kcc20Balance | undefined>(undefined);
  readonly ownerPosition = signal<Kcc20AccountPosition | undefined>(undefined);
  readonly ownerHoldingsWarnings = signal<string[]>([]);
  readonly openOrders = signal<Kcc20Order[]>([]);
  readonly openOrdersTotal = signal(0);
  readonly openOrdersLoading = signal(false);
  readonly openOrdersLoadingMore = signal(false);
  readonly openOrdersHasNextPage = signal(false);
  readonly openOrdersError = signal<string | undefined>(undefined);
  readonly openOrdersSortBy = signal<Kcc20AddressOrderSortBy>('createdAtMs');
  readonly openOrdersSortDirection = signal<Kcc20SortDirection>('desc');
  readonly openOrdersPageSize = 10;
  /**
   * Order ids removed locally after a successful cancel but that the address
   * orders endpoint may still return until indexer catch-up. Kept out of the
   * table until a refresh confirms they are gone.
   */
  private readonly suppressedOpenOrderIds = signal<ReadonlySet<string>>(
    new Set(),
  );
  readonly displayedOpenOrders = computed(() => {
    const suppressed = this.suppressedOpenOrderIds();
    if (!suppressed.size) {
      return this.openOrders();
    }
    return this.openOrders().filter((order) => !suppressed.has(order.orderId));
  });
  readonly feeTicketAvailable = signal<Kcc20FeeTicketAvailable | undefined>(
    undefined,
  );
  readonly feeTicketBalanceLoading = signal(false);
  readonly quote = signal<Kcc20Quote | undefined>(undefined);
  readonly tokenDecimals = computed(() =>
    normalizeKcc20Decimals(this.tokenDetail()?.decimals),
  );
  /**
   * Whether the token's decimal scale is confirmed, not merely present on
   * `tokenDetail()`. A legacy identity response omits `decimals` entirely
   * (confirmed 0-decimal token), so gating on `tokenDetail().decimals` being
   * non-null left legacy tokens permanently unconfirmed and every action
   * draft (order/mint/wrap/unwrap) unavailable. Read the static profile
   * cache instead: it distinguishes an authoritative `indexed` value and a
   * resolved `legacy` (0-decimal) identity from `unknown` (not fetched yet)
   * and `unsupported` (an out-of-range value came back and must not be
   * trusted). Purely-display `kcc20TokenAmount` pipe usages already fail
   * safe on `undefined` decimals, so they don't need this gate.
   */
  readonly tokenDecimalsConfirmed = computed(() => {
    this.tokenStaticProfiles.revision();
    const profile = this.tokenStaticProfiles.peek(this.covenantId());
    return (
      !!profile &&
      profile.hasIdentity &&
      (profile.decimalsSource === 'indexed' ||
        profile.decimalsSource === 'legacy')
    );
  });
  readonly quoteSide = signal<QuoteSide>('buy');
  readonly quoteSideOptions: SwitchOption[] = [
    { label: 'Buy', value: 'buy' },
    { label: 'Sell', value: 'sell' },
  ];
  readonly orderModeOptions: SwitchOption[] = [
    { label: 'Limit', value: 'limit' },
    { label: 'Market', value: 'market' },
  ];
  readonly wrapModeOptions: SwitchOption[] = [
    { label: 'Wrap to trade', value: 'wrap' },
    { label: 'Unwrap to hold', value: 'unwrap' },
  ];
  readonly ladderDirectionOptions: SwitchOption[] = [
    { label: 'Increment', value: 'increment' },
    { label: 'Decrement', value: 'decrement' },
  ];
  readonly ladderPercentPresets = [1, 5, 10] as const;
  readonly wrapToTradeMessage = WRAP_TO_TRADE_MESSAGE;
  /** Env-gated coupon / FeeTicket UX (fail-closed). */
  readonly feeTicketFeatureEnabled = kcc20FeeTicketFeatureEnabled;
  readonly actionTab = signal<TokenActionTab>('book');
  readonly lowerTab = signal<TokenLowerTab>('tradingActivity');
  readonly showWatchButton = computed(() => !!this.owner());
  readonly watching = computed(() =>
    this.watchlistState.isWatching(this.covenantId()),
  );
  readonly watchButtonDisabled = computed(
    () =>
      this.watchlistState.loading() ||
      this.watchlistState.isMutating(this.covenantId()),
  );
  readonly wrapMode = signal<WrapMode>('wrap');
  readonly orderMode = signal<OrderMode>('limit');
  /** Limit-ticket ladder: 0 = a single order (today's flow). */
  private readonly ladderPreferenceState = readTokenTicketLadderPreferences();
  readonly ladderExpanded = signal(this.ladderPreferenceState.expanded);
  readonly ladderCount = signal(0);
  readonly ladderDirection = signal<LadderDirection>(
    this.ladderPreferenceState.direction,
  );
  readonly ladderPercent = signal(this.ladderPreferenceState.percent);
  readonly quoteAmount = signal('');
  readonly orderUnitPriceKas = signal('');
  readonly orderUnitPriceSompi = signal('');
  readonly selectedOrderId = signal<string | undefined>(undefined);
  readonly selectedOrderSide = signal<'ask' | 'bid' | undefined>(undefined);
  readonly publicMintMaxAmount = KCC20_PUBLIC_MINT_MAX_DISPLAY_TOKENS_INPUT;
  readonly mintAmount = signal('1');
  /** Flips true the first time the user edits the mint amount — the field
   * defaults to a non-empty '1', so this stops the balance-shortfall
   * snackbar from firing on page load before the user has done anything. */
  readonly mintAmountTouched = signal(false);
  /**
   * False while the mint amount is still being typed so "Minimum …" / cap /
   * remaining-supply alerts don't fire on every keystroke.
   */
  readonly mintAmountWarningsSettled = signal(true);
  private mintAmountWarningTimer: ReturnType<typeof setTimeout> | undefined;
  readonly wrapAmount = signal('1');
  readonly unwrapAmount = signal('1');
  readonly wrapperDeployRootAmount = signal('0');
  readonly chartIntervalMs = signal<Kcc20ChartIntervalMs>(
    KCC20_DEFAULT_CHART_INTERVAL_MS,
  );
  /**
   * Per-section loading flags. The token page no longer waits for every
   * request before rendering: each section flips its own flag off as soon as
   * its data arrives, and post-action refreshes touch only the affected
   * sections so unaffected content never resets to a full-page skeleton.
   */
  readonly tokenInfoLoading = signal(true);
  /**
   * True once trading-summary has settled for the current `load()` request.
   * That endpoint owns the wrapper/market row; `wrapStatus` gates its
   * "not available yet" branch on this so we don't toast while identity has
   * resolved but summary (with the real marketId) is still in flight.
   */
  readonly wrapperInfoLoaded = signal(false);
  readonly orderbookLoading = signal(true);
  readonly activityLoading = signal(true); // initial / refresh only (not append)
  readonly tokenActivityLoading = signal(false);
  readonly quoteLoading = signal(false);
  readonly ownerHoldingsLoading = signal(false);
  readonly chartLoading = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly warning = signal<string | undefined>(undefined);
  readonly tradeMessage = signal<string | undefined>(undefined);
  readonly tradeError = signal<string | undefined>(undefined);
  // Wrap, unwrap, and sell no longer lock their buttons while in flight: every
  // click queues a flow into the wallet-operations scheduler (#237), the same
  // way mint, wrapper-deploy, and buy already queue actions (#206). Only
  // validation gates the buttons now; the scheduler serializes per token.
  /** FeeTicket discount toggle on the trade ticket (on when eligible). */
  readonly useFeeTicket = signal(true);

  /**
   * Page/action feedback (page-level error/warning, mint, wrap, position)
   * renders as `kc-snackbar` toasts instead of inline `kc-alert`s, so none
   * of it shifts the surrounding layout. The Buy/Sell ticket never shows
   * inline banners — blockers disable the button without jumping the form.
   * Quote failures still toast (see {@link orderTicketToast}).
   * `notifications.sync(key, ...)` tracks the
   * notification id(s) currently shown for that slot so a change can remove
   * the stale toast and push a fresh one — and so an unchanged value (e.g.
   * re-typing the same invalid amount) is a no-op rather than a flicker.
   * Each toast auto-dismisses after its duration; it does not persist for
   * as long as the underlying condition holds. Cleared automatically on
   * destroy (leaving the page, or switching to a different token) so no
   * toast survives past the condition that raised it.
   */
  private readonly notifications = new NotificationGroupSync(
    this.notificationService,
    this.destroyRef,
  );

  constructor() {
    effect(() => {
      const covenantId = this.covenantId().toLowerCase();
      const visible = this.walletOperationTasks.visibleTasks();
      const completedTask = visible.find(
        (task) =>
          task.status !== 'submitted' &&
          !this.handledWalletOperationTaskIds.has(task.id) &&
          this.walletOperationTaskBelongsToToken(task, covenantId),
      );

      if (!completedTask) {
        // Diagnostic breadcrumb for "order finished but the page never
        // refreshed" reports: shows whether any completed task even reached
        // this page (a `tokenId` mismatch is the most likely reason one
        // wouldn't) versus none existing at all.
        const unmatched = visible.filter(
          (task) =>
            task.status !== 'submitted' &&
            !this.handledWalletOperationTaskIds.has(task.id) &&
            !this.loggedTokenIdMismatchTaskIds.has(task.id),
        );
        if (unmatched.length) {
          unmatched.forEach((task) =>
            this.loggedTokenIdMismatchTaskIds.add(task.id),
          );
          logWalletOperation('token-page.completedTask.tokenIdMismatch', {
            covenantId,
            unmatched: unmatched.map((task) => ({
              id: task.id,
              operation: task.operation,
              status: task.status,
              tokenId: task.tokenId,
            })),
          });
        }
        return;
      }

      logWalletOperation('token-page.completedTask.matched', {
        covenantId,
        id: completedTask.id,
        operation: completedTask.operation,
        status: completedTask.status,
      });
      this.handledWalletOperationTaskIds.add(completedTask.id);
      void this.handleWalletOperationTaskCompletion(completedTask);
    });

    // Clear the buy/sell ticket as soon as a create/fill is submitted so the
    // form does not keep showing the filled order's amount/price/target.
    // Cancel/sweep must NOT wipe the ticket — those actions are independent
    // of the trade form the user may still be editing.
    effect(() => {
      const covenantId = this.covenantId().toLowerCase();
      const submittedOrderTasks = this.walletOperationTasks
        .visibleTasks()
        .filter(
          (task) =>
            task.status === 'submitted' &&
            this.walletOperationTaskBelongsToToken(task, covenantId) &&
            this.isOrderTicketKind(this.orderOperationKind(task.operation)) &&
            !this.resetOrderTicketOnSubmitTaskIds.has(task.id),
        );
      if (!submittedOrderTasks.length) {
        return;
      }
      untracked(() =>
        submittedOrderTasks.forEach((task) =>
          this.handleOrderTicketSubmitted(task),
        ),
      );
    });

    effect(() => {
      const items: { variant: AlertVariant; text: string }[] = [];
      const errorText = this.error();
      if (errorText) {
        items.push({ variant: 'error', text: errorText });
      }
      const warningText = this.warning();
      if (warningText) {
        items.push({ variant: 'warning', text: warningText });
      }
      this.notifications.sync('page', 'Token page', items);
    });

    effect(() => {
      const items = this.ownerHoldingsWarnings().map((text) => ({
        variant: 'warning' as const,
        text,
      }));
      this.notifications.sync('holdings', 'Holdings', items);
    });

    effect(() => {
      const status = this.mintStatus();
      this.notifications.sync('mint', 'Mint', status ? [status] : []);
    });

    effect(() => {
      const status = this.wrapStatus();
      this.notifications.sync('wrap', 'Wrap', status ? [status] : []);
    });

    // Buy/Sell blockers still disable the button without inline banners.
    // Quote failures (no liquidity, unusable route) also toast so the
    // disabled button is not silent. KAS/token shortfalls and wrap-to-trade
    // stay off the snackbar — the button label already says why, and a
    // post-trade UTXO-settle false-low must not flash a warning.
    effect(() => {
      const status = this.orderTicketToast();
      this.notifications.sync('order', 'Trade', status ? [status] : []);
    });

    // Infinite-scroll Trading Activity: near-bottom of kc-table's container.
    effect(() => {
      const tab = this.lowerTab();
      const body = this.activityBody()?.nativeElement;
      const activityCount = this.activity().length;
      const hasMore = this.activityHasNextPage();

      untracked(() => {
        this.activityScrollCleanup?.();
        this.activityScrollCleanup = undefined;
        if (
          tab !== 'tradingActivity' ||
          !body ||
          !hasMore ||
          activityCount === 0
        ) {
          return;
        }
        const container = body.querySelector('.kc-table-container');
        if (!(container instanceof HTMLElement)) {
          return;
        }
        const onScroll = (): void => {
          const remaining =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          if (remaining <= 48) {
            this.maybeLoadMoreActivity();
          }
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        this.activityScrollCleanup = () =>
          container.removeEventListener('scroll', onScroll);
      });
    });
    this.destroyRef.onDestroy(() => this.activityScrollCleanup?.());

    // Infinite-scroll Token Activity the same way (scope=token feed).
    effect(() => {
      const tab = this.lowerTab();
      const body = this.tokenActivityBody()?.nativeElement;
      const activityCount = this.tokenActivity().length;
      const hasMore = this.tokenActivityHasNextPage();

      untracked(() => {
        this.tokenActivityScrollCleanup?.();
        this.tokenActivityScrollCleanup = undefined;
        if (
          tab !== 'tokenActivity' ||
          !body ||
          !hasMore ||
          activityCount === 0
        ) {
          return;
        }
        const container = body.querySelector('.kc-table-container');
        if (!(container instanceof HTMLElement)) {
          return;
        }
        const onScroll = (): void => {
          const remaining =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          if (remaining <= 48) {
            this.maybeLoadMoreTokenOpsActivity();
          }
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        this.tokenActivityScrollCleanup = () =>
          container.removeEventListener('scroll', onScroll);
      });
    });
    this.destroyRef.onDestroy(() => this.tokenActivityScrollCleanup?.());

    // Infinite-scroll Holders list (server pages appended).
    effect(() => {
      const tab = this.lowerTab();
      const body = this.holdersBody()?.nativeElement;
      const holderCount = this.holders().length;
      const hasMore = this.holdersHasNextPage();

      untracked(() => {
        this.holdersScrollCleanup?.();
        this.holdersScrollCleanup = undefined;
        if (tab !== 'holders' || !body || !hasMore || holderCount === 0) {
          return;
        }
        const onScroll = (): void => {
          const remaining =
            body.scrollHeight - body.scrollTop - body.clientHeight;
          if (remaining <= 48) {
            this.maybeLoadMoreHolders();
          }
        };
        body.addEventListener('scroll', onScroll, { passive: true });
        this.holdersScrollCleanup = () =>
          body.removeEventListener('scroll', onScroll);
      });
    });
    this.destroyRef.onDestroy(() => this.holdersScrollCleanup?.());

    // Infinite-scroll open orders the same way as activity.
    effect(() => {
      const tab = this.lowerTab();
      const body = this.openOrdersBody()?.nativeElement;
      const orderCount = this.displayedOpenOrders().length;
      const hasMore = this.openOrdersHasNextPage();

      untracked(() => {
        this.openOrdersScrollCleanup?.();
        this.openOrdersScrollCleanup = undefined;
        if (tab !== 'orders' || !body || !hasMore || orderCount === 0) {
          return;
        }
        const container = body.querySelector('.kc-table-container');
        if (!(container instanceof HTMLElement)) {
          return;
        }
        const onScroll = (): void => {
          const remaining =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          if (remaining <= 48) {
            this.maybeLoadMoreOpenOrders();
          }
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        this.openOrdersScrollCleanup = () =>
          container.removeEventListener('scroll', onScroll);
      });
    });
    this.destroyRef.onDestroy(() => this.openOrdersScrollCleanup?.());
    this.destroyRef.onDestroy(() => this.clearMintAmountWarningTimer());

    // Remaining mintable supply: snackbar on rising edge + button relabel.
    // KAS shortfalls are not toasted — same as buy/sell. Spent UTXOs vs.
    // not-yet-settled change can read as a false-low balance; the mint
    // button uses `mintBalanceChecking` / `mintBalanceError` instead.
    effect(() => {
      const message = this.mintRemainingSupplyError();
      const touched = this.mintAmountTouched();
      const settled = this.mintAmountWarningsSettled();
      untracked(() => {
        const shouldNotify = touched && !!message;
        if (settled && shouldNotify && !this.mintRemainingSupplyErrorNotified) {
          this.notificationService.warning(
            'Unavailable mint amount',
            message ?? '',
            undefined,
            true,
          );
        }
        if (settled) {
          this.mintRemainingSupplyErrorNotified = shouldNotify;
        }
      });
    });

    // Seed paid-mint amount to the 0.55 KAS UI floor when the default '1' is dust.
    effect(() => {
      const minAmount = this.minMintTokenAmount();
      const touched = this.mintAmountTouched();
      const belowFloor = this.mintAmountQuote()?.belowMinimumKas === true;
      const shouldClamp = this.publicMintAmountCapEnabled();
      untracked(() => {
        if (!minAmount || touched || !belowFloor) {
          return;
        }
        this.mintAmount.set(
          shouldClamp
            ? clampKcc20PublicMintDisplayAmount(minAmount)
            : minAmount,
        );
      });
    });

    // Load this wallet's open orders for the current token when the tab is
    // selected (lazy), and again if the owner/token changes while it is open.
    effect(() => {
      const tab = this.lowerTab();
      const owner = this.owner();
      const covenantId = this.covenantId();
      untracked(() => {
        if (tab !== 'orders') {
          return;
        }
        if (!owner || !covenantId) {
          this.resetOpenOrders();
          return;
        }
        void this.loadOpenOrders();
      });
    });
  }

  onMintAmountChange(value: string): void {
    const nextValue = this.publicMintAmountCapEnabled()
      ? clampKcc20PublicMintDisplayAmount(value)
      : value;
    if (nextValue === this.mintAmount() && nextValue === value) {
      return;
    }
    this.mintAmountTouched.set(true);
    if (nextValue === this.mintAmount()) {
      // Input diverged past the cap while the model is already at 100000 —
      // bounce so kc-number-input rewrites the visible value.
      this.mintAmount.set('');
      queueMicrotask(() => this.mintAmount.set(nextValue));
    } else {
      this.mintAmount.set(nextValue);
    }
    this.postponeMintAmountWarnings();
  }

  private publicMintAmountCapEnabled(): boolean {
    return this.tokenDetail()?.mintPolicy?.policy === 'public';
  }

  private postponeMintAmountWarnings(): void {
    this.mintAmountWarningsSettled.set(false);
    this.clearMintAmountWarningTimer();
    this.mintAmountWarningTimer = setTimeout(() => {
      this.mintAmountWarningsSettled.set(true);
      this.mintAmountWarningTimer = undefined;
    }, MINT_AMOUNT_WARNING_DEBOUNCE_MS);
  }

  private clearMintAmountWarningTimer(): void {
    if (this.mintAmountWarningTimer !== undefined) {
      clearTimeout(this.mintAmountWarningTimer);
      this.mintAmountWarningTimer = undefined;
    }
  }

  readonly selectedMarket = computed(() => {
    const marketId = this.marketId();
    const detailMarket = this.tokenDetail()?.market;
    if (detailMarket && (!marketId || detailMarket.tokenIdHex === marketId)) {
      return detailMarket;
    }
    return this.markets().find((market) => market.tokenIdHex === marketId);
  });

  /**
   * Cap of visible order book rows per side. The book only shows the orders
   * closest to the mid so the panel stays compact on desktop instead of
   * stretching into an empty gray area (KCC20-41).
   */
  static readonly ORDERBOOK_ROWS_PER_SIDE = 15;

  /**
   * KAS held back from a "Max" buy preset to cover the on-chain network fee /
   * required tx reserve (0.2 KAS in sompi). The backend quote applies the
   * precise fee at build time; this keeps the preset from spending the wallet
   * to zero (KCC20-47).
   */
  static readonly BUY_NETWORK_RESERVE_SOMPI = 20_000_000n;
  /** Trade ticket amount precision (input, presets, slider, fill/summary labels). */
  static readonly TRADE_AMOUNT_MAX_DECIMALS = 3;
  /** KAS display cap for ticket totals / mint cost (trailing zeros stripped). */
  static readonly KAS_DISPLAY_MAX_DECIMALS = 5;
  readonly kasDisplayMaxDecimals = TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS;

  /**
   * Orderbook rows enriched with a depth percentage for the depth-bar
   * background. Public orderbooks are price-level depth; raw UTXO orders remain
   * available through the API but are not rendered as duplicate price rows.
   */
  readonly depthAsksDisplay = computed(() =>
    kcc20DepthRowsForDisplay(
      this.orderbook(),
      'ask',
      TokenPageComponent.ORDERBOOK_ROWS_PER_SIDE,
    ),
  );
  readonly depthBids = computed(() =>
    kcc20DepthRowsForDisplay(
      this.orderbook(),
      'bid',
      TokenPageComponent.ORDERBOOK_ROWS_PER_SIDE,
    ),
  );
  /** True when the book has loaded and neither side has resting depth. */
  readonly orderbookEmpty = computed(
    () =>
      !!this.orderbook() &&
      !this.depthAsksDisplay().length &&
      !this.depthBids().length,
  );

  /**
   * Every order book refresh (silent background poll after a create/fill/
   * cancel is indexed, see `refreshAfterOperation`) replaces `orderbook()`
   * with a brand-new response object, which in turn makes `depthAsksDisplay`
   * / `depthBids` return brand-new row objects even for orders that didn't
   * change. Without a `track` expression, `@for`'s default identity diffing sees
   * every row as removed-and-re-added and tears down/rebuilds the whole
   * list's DOM on every refresh — the "whole component flashes" feeling.
   * Tracking by price lets Angular patch bindings on existing depth rows and
   * only add/remove DOM nodes for prices that entered/left the book.
   */
  readonly trackByDepthPrice = (
    _index: number,
    row: Kcc20DepthDisplayRow,
  ): string => row.level.unitPriceSompi;
  /**
   * Shown on disabled Mint / Wrap / Trade CTAs while disconnected, or while
   * decimals aren't confirmed yet (so a wallet-connected user still sees why
   * the action is disabled instead of an unexplained no-op).
   */
  readonly connectWalletTooltip = computed(() => {
    if (!this.owner()) {
      return 'Connect wallet to continue';
    }
    return this.tokenDecimalsConfirmed() ? '' : 'Loading token details…';
  });

  readonly owner = computed(
    () => this.walletManager.signedWalletSignal()?.kcc20Owner,
  );
  readonly publicMintControlSupported = computed(
    () =>
      this.tokenDetail()?.mintPolicy?.policy === 'public' &&
      this.tokenDetail()?.mintPolicy?.publicMintControlSupported === true,
  );
  readonly publicMintActive = computed(
    () => this.tokenDetail()?.mintPolicy?.publicMintActive === true,
  );
  readonly publicMintPaused = computed(
    () => this.publicMintControlSupported() && !this.publicMintActive(),
  );
  readonly publicMintOwner = computed(() =>
    isKcc20PublicMintOwner(this.tokenDetail(), this.owner()),
  );
  readonly mintAvailabilityUpdating = computed(() =>
    this.pendingPublicMintAvailability().has(this.covenantId()),
  );
  readonly displayedPublicMintActive = computed(
    () =>
      this.pendingPublicMintAvailability().get(this.covenantId()) ??
      this.publicMintActive(),
  );
  readonly activeWrapper = computed(
    () => this.tokenDetail()?.wrapper ?? this.tokenWrappers()[0] ?? null,
  );
  readonly wrapperEnabled = computed(
    () => this.activeWrapper()?.enabled ?? false,
  );
  readonly wrapperFeeTicketSupported = computed(
    () => this.activeWrapper()?.feeTicketSupported ?? false,
  );
  readonly marketId = computed(
    () =>
      this.tokenDetail()?.market?.tokenIdHex ?? this.activeWrapper()?.marketId,
  );
  readonly wrappedMarketId = computed(
    () =>
      this.activeWrapper()?.marketId ?? this.tokenDetail()?.market?.tokenIdHex,
  );
  // A market *existing* (`wrappedMarketId`) isn't the same as it being ready
  // to build against — right after creation it's still indexing and building
  // against it fails with the backend's `WRAPPER_MARKET_NOT_READY` error.
  // `tokenActions` (`/tokens/:id/page-actions`) is the backend's own
  // authoritative "can this action build right now" check, matching what the
  // build endpoint itself validates — prefer it, and only fall back to
  // `wrapperWrapHeuristicReady`/`wrapperUnwrapHeuristicReady` (reconstructed
  // from the wrapper summary fields) while that call hasn't resolved yet.
  // Wrap and unwrap have *different* backend rules while a wrapper is still
  // `pendingReveal` (wrap can proceed if already `buildable`; unwrap can't),
  // so these must stay two separate heuristics rather than one shared one.
  readonly wrapActionReady = computed(
    () =>
      this.tokenActions()?.wrap.enabled ??
      wrapperWrapHeuristicReady(this.activeWrapper()),
  );
  readonly unwrapActionReady = computed(
    () =>
      this.tokenActions()?.unwrap.enabled ??
      wrapperUnwrapHeuristicReady(this.activeWrapper()),
  );
  readonly wrapperNotReadyReason = computed(() => {
    if (!this.wrappedMarketId()) {
      return undefined;
    }
    const unwrapping = this.wrapMode() === 'unwrap';
    if (unwrapping ? this.unwrapActionReady() : this.wrapActionReady()) {
      return undefined;
    }
    const actions = this.tokenActions();
    // `tokenActions()` undefined means the actions call is still in flight —
    // not that the wrapper is unavailable. Emitting the heuristic fallback
    // here flashes a yellow toast on every page load until /actions resolves.
    if (actions === undefined) {
      return undefined;
    }
    const reason =
      (unwrapping ? actions.unwrap.reason : actions.wrap.reason) ??
      this.activeWrapper()?.buildableReason ??
      "Wrapped market isn't ready for this action yet. Refresh and try again.";
    // "Wrapper already exists" is an expected deploy disable, not a wrap
    // warning. The pending sentinel is the same one `visibleWrapperTradingBlock`
    // suppresses: still-loading availability isn't a real blocker.
    if (isExistingWrapperReason(reason)) {
      return undefined;
    }
    return this.suppressPendingActionReason(reason);
  });
  /**
   * Tooltip for the disabled Wrap/Unwrap button, covering both reasons it
   * can be disabled: wallet not connected, or the backend reporting the
   * wrapper/market isn't ready for this action yet ({@link
   * wrapperNotReadyReason}). Previously the button's tooltip only ever
   * carried {@link connectWalletTooltip}, so a real "not ready" reason from
   * the backend was silent on the button itself — only visible via the
   * `wrap` toast synced from {@link wrapStatus} (easy to miss/dismiss).
   * Wallet-not-connected takes priority since it's the more actionable state.
   */
  readonly wrapActionTooltip = computed(() => {
    if (!this.owner()) return 'Connect wallet to continue';
    return this.wrapperNotReadyReason() || '';
  });
  readonly appMetadata = computed(
    () => this.metadata() ?? this.tokenDetail()?.metadata,
  );
  // On-chain deploy ticker/name. These are only a *fallback*: curated metadata
  // (owner/admin editable, same as logo/description/socials) takes priority in
  // every display below, so an edited name/ticker doesn't get overwritten by
  // the native value once the token identity call resolves.
  readonly nativeTicker = computed(() => this.tokenDetail()?.ticker);
  readonly nativeTokenName = computed(() => this.tokenDetail()?.tokenName);
  readonly tokenLogoUrl = computed(() => this.appMetadata()?.logoUrl);
  readonly tokenLinks = computed<TokenSocialLink[]>(() => {
    const metadata = this.appMetadata();
    if (!metadata) {
      return [];
    }
    return [
      this.toTokenLink('Website', metadata.websiteUrl, 'website'),
      this.toTokenLink('X / Twitter', metadata.xUrl, 'x'),
      this.toTokenLink('Telegram', metadata.telegramUrl, 'telegram'),
      this.toTokenLink('Discord', metadata.discordUrl, 'discord'),
      this.toTokenLink('GitHub', metadata.githubUrl, 'github'),
      this.toTokenLink('Medium', metadata.mediumUrl, 'medium'),
    ].filter((link): link is TokenSocialLink => link !== undefined);
  });
  readonly wrapperStatusText = computed(() => {
    const wrapper = this.activeWrapper();
    if (!wrapper?.marketId) {
      return 'No wrapped trading mode';
    }
    const tradingBlock = this.wrapperTradingBlockedReason();
    if (tradingBlock) {
      return wrapper.enabled ? 'Wrapped trading read-only' : tradingBlock;
    }
    return wrapper.enabled
      ? 'Wrapped trading active'
      : 'Wrapped trading disabled';
  });
  readonly wrapperTradingBlockedReason = computed(() => {
    const tradeActionBlock = this.tokenActionBlockedReason('trade');
    if (tradeActionBlock) {
      return tradeActionBlock;
    }
    const wrapper = this.activeWrapper();
    if (!wrapper?.marketId) {
      return 'No wrapped trading market is available yet.';
    }
    if (wrapper.pendingReveal) {
      return 'Wrapped trading is still indexing.';
    }
    if (!wrapper.enabled) {
      return 'Wrapped trading is disabled for this market.';
    }
    if (wrapper.buildable === false) {
      return (
        wrapper.buildableReason?.trim() ||
        'This wrapped market uses an older contract artifact.'
      );
    }
    if (!wrapper.activeCovenantId) {
      return 'Wrapped trading source is still indexing.';
    }
    return undefined;
  });
  readonly wrapperTradingReady = computed(
    () => this.wrapperTradingBlockedReason() === undefined,
  );
  /**
   * Ticket-side copy for real trading blockers. The Buy/Sell form never
   * renders this as a banner (that shifted the ticket); {@link
   * wrapperTradingReady} still disables the button. The transient
   * "availability is still loading" state is filtered out so callers that
   * still read this don't treat a pending `/actions` fetch as a real block.
   */
  readonly visibleWrapperTradingBlock = computed(() =>
    this.suppressPendingActionReason(this.wrapperTradingBlockedReason()),
  );
  readonly mintActionBlockedReason = computed(() => {
    if (this.publicMintPaused()) {
      return this.publicMintOwner()
        ? undefined
        : 'Public mint is paused by the token creator.';
    }
    return this.tokenActionBlockedReason('mint');
  });
  /**
   * Alert copy for real mint blockers, same treatment as
   * {@link visibleWrapperTradingBlock}: the transient "availability is still
   * loading" state still disables the button via `mintActionBlockedReason`,
   * but isn't shown as a warning banner.
   */
  readonly visibleMintActionBlock = computed(() =>
    this.suppressPendingActionReason(this.mintActionBlockedReason()),
  );
  readonly wrapperDeployActionBlockedReason = computed(() => {
    // "Wrapper already exists" is an expected deploy disable, not a warning —
    // hide it even before wrappedMarketId has landed on the page.
    if (this.wrappedMarketId()) {
      return undefined;
    }
    const reason = this.tokenActionBlockedReason('deployWrapper');
    if (!reason || isExistingWrapperReason(reason)) {
      return undefined;
    }
    return reason;
  });
  /** Toast copy for the deploy-wrapper button, folded into {@link wrapStatus}; same pending-state suppression as {@link visibleWrapperTradingBlock}. */
  readonly visibleWrapperDeployActionBlock = computed(() =>
    this.suppressPendingActionReason(this.wrapperDeployActionBlockedReason()),
  );
  /**
   * Filters the "availability is still loading" sentinel out of ticket/panel
   * alert copy. Buttons still gate on the raw `*ActionBlockedReason` (which
   * keeps them disabled during the pending window); only the warning-banner
   * text suppresses it, so a healthy action doesn't flash a spurious amber
   * alert while `/actions` is still in flight.
   */
  private suppressPendingActionReason(
    reason: string | undefined,
  ): string | undefined {
    return !reason || reason === TOKEN_ACTION_AVAILABILITY_PENDING_REASON
      ? undefined
      : reason;
  }
  readonly mintPolicyText = computed(() => {
    const policy = this.tokenDetail()?.mintPolicy;
    if (!policy?.mintable) {
      return 'Fixed supply';
    }
    const mode = policy.policy || 'mintable';
    const remaining = policy.remainingSupply
      ? `, ${policy.remainingSupply} remaining`
      : '';
    return `${mode}${remaining}`;
  });
  readonly displayName = computed(() => {
    const label = tokenIdentityDisplay(
      {
        ticker:
          this.appMetadata()?.ticker ||
          this.selectedMarket()?.metadata?.ticker ||
          this.nativeTicker(),
        name:
          this.appMetadata()?.name ||
          this.selectedMarket()?.metadata?.name ||
          this.nativeTokenName(),
      },
      this.covenantId(),
    );
    return label === tokenLabel(this.covenantId()) ? 'Unknown token' : label;
  });
  readonly tokenSubtitle = computed(() => {
    const description = clipMetadataDescription(
      this.appMetadata()?.description,
    );
    return description || undefined;
  });
  readonly mintable = computed(
    () => this.tokenDetail()?.mintPolicy?.mintable ?? false,
  );
  readonly needsWrapToTrade = computed(() => {
    const canonicalBalance = this.ownerCanonicalBalance();
    const wrappedBalance = this.ownerWrappedBalance();
    return (
      !!this.wrappedMarketId() &&
      this.isPositiveBaseAmount(canonicalBalance) &&
      !this.isPositiveBaseAmount(wrappedBalance)
    );
  });
  /**
   * Single wrap-panel status slot: at most one alert renders at a time.
   * "No wrapper yet" and "wrapper not ready" are already mutually exclusive
   * (the latter's own guard requires a wrapper to exist); the wrap-to-trade
   * nudge is the lowest-priority, informational fallback.
   */
  readonly wrapStatus = computed<
    { variant: AlertVariant; text: string } | undefined
  >(() => {
    if (!this.wrappedMarketId()) {
      // Don't report the wrapper missing until trading-summary has settled —
      // identity alone used to mark loaded too early and briefly toast
      // "not available yet" before the market row arrived.
      if (!this.wrapperInfoLoaded()) {
        return undefined;
      }
      return {
        variant: 'warning',
        text:
          this.visibleWrapperDeployActionBlock() ??
          'Wrapped trading mode is not available for this token yet.',
      };
    }
    const notReadyReason = this.wrapperNotReadyReason();
    if (notReadyReason) {
      return { variant: 'warning', text: notReadyReason };
    }
    if (this.needsWrapToTrade()) {
      return { variant: 'info', text: this.wrapToTradeMessage };
    }
    return undefined;
  });
  readonly canManageMetadata = computed(() =>
    canManageToken(this.tokenDetail(), this.owner(), this.metadataPermission()),
  );
  readonly openOrderCount = computed(() => {
    const market = this.selectedMarket();
    if (market) {
      return (market.openAskCount ?? 0) + (market.openBidCount ?? 0);
    }
    const orderbook = this.orderbook();
    return (orderbook?.asks?.length ?? 0) + (orderbook?.bids?.length ?? 0);
  });
  /** Trades that actually matched and settled — distinct from openOrderCount's still-resting orders. */
  readonly totalTradesLabel = computed(() =>
    this.formatCount(this.selectedMarket()?.totalTrades),
  );
  readonly bestAskLabel = computed(() =>
    formatSompi(this.selectedMarket()?.bestAskUnitPriceSompi),
  );
  readonly bestBidLabel = computed(() =>
    formatSompi(this.selectedMarket()?.bestBidUnitPriceSompi),
  );
  /**
   * Mid price as a *bare* number (no " KAS" unit): the hero appends its own
   * muted KAS suffix and the orderbook mid row shows the bare figure, matching
   * the design handoff.
   */
  readonly midPriceLabel = computed(() => {
    const ask = Number(this.selectedMarket()?.bestAskUnitPriceSompi ?? 0);
    const bid = Number(this.selectedMarket()?.bestBidUnitPriceSompi ?? 0);
    const raw =
      ask > 0 && bid > 0
        ? formatSompi(String(Math.round((ask + bid) / 2)))
        : formatSompi(
            this.stats()?.closeUnitPriceSompi ||
              this.selectedMarket()?.bestAskUnitPriceSompi ||
              this.selectedMarket()?.bestBidUnitPriceSompi,
          );
    return raw.replace(/ KAS$/, '');
  });
  readonly spreadLabel = computed(() => {
    const asks = this.depthAsksDisplay();
    const bids = this.depthBids();
    const bid =
      bids[0]?.level.unitPriceSompi ??
      this.selectedMarket()?.bestBidUnitPriceSompi;
    const ask =
      asks[asks.length - 1]?.level.unitPriceSompi ??
      this.selectedMarket()?.bestAskUnitPriceSompi;
    const fromBook = formatSpreadPercent(bid, ask);
    if (fromBook !== '--') {
      return fromBook;
    }
    return formatSpreadPercentFromSpread(
      this.selectedMarket()?.spreadSompi,
      this.stats()?.closeUnitPriceSompi ||
        this.selectedMarket()?.bestAskUnitPriceSompi ||
        this.selectedMarket()?.bestBidUnitPriceSompi,
    );
  });
  readonly spreadAmountLabel = computed(() => {
    const labeled = formatSompi(this.selectedMarket()?.spreadSompi);
    return labeled === '--' ? undefined : labeled;
  });
  /** Hero title prefers the proper-case token name; the ticker shows as a chip. */
  readonly heroTitle = computed(() => {
    const name = (
      this.appMetadata()?.name ||
      this.selectedMarket()?.metadata?.name ||
      this.nativeTokenName()
    )?.trim();
    if (name) return name;
    const ticker = (
      this.appMetadata()?.ticker ||
      this.selectedMarket()?.metadata?.ticker ||
      this.nativeTicker()
    )?.trim();
    if (ticker) return ticker;
    return this.displayName();
  });
  /** Short mint badge for the hero pill row (detailed copy lives in the Mint tab). */
  readonly mintBadgeText = computed(() => {
    if (!this.mintable()) {
      return 'Fixed supply';
    }
    if (this.tokenDetail()?.mintPolicy?.policy === 'controlled') {
      return 'Controlled mint';
    }
    return this.publicMintPaused() ? 'Public mint paused' : 'Public mint';
  });
  /**
   * 24h volume from trading-summary (`selectedMarket`). Volume fields are
   * already KAS notional in sompi (sum of trade totals) — do not descale by
   * token decimals (that path is for market-cap floor / similar inflated
   * price×base-units products). Descaling made real volume render as "0 KAS".
   */
  readonly volume24hLabel = computed(() => {
    const raw =
      this.selectedMarket()?.volume24hSompi ||
      this.tokenDetail()?.market?.volume24hSompi;
    return formatSompi(raw, 0);
  });
  readonly marketCapFloorLabel = computed(() =>
    this.tokenScaledSompiLabel(this.tokenDetail()?.marketCapFloorAskSompi),
  );
  readonly holdersLabel = computed(() =>
    this.formatCount(this.tokenDetail()?.holderCount),
  );
  readonly ownerCanonicalBalance = computed(
    () => this.ownerTokenBalance()?.amount ?? this.positionCanonicalBalance(),
  );
  readonly ownerWrappedBalance = computed(
    () =>
      this.ownerPosition()?.orderbookBalance ??
      this.ownerPosition()?.wrappedBalance,
  );
  readonly ownerTotalBalance = computed(
    () =>
      this.ownerPosition()?.totalBalance ??
      this.addTokenAmounts(
        this.ownerCanonicalBalance(),
        this.ownerWrappedBalance(),
      ),
  );
  readonly ownerTokenBalanceLabel = computed(() =>
    this.owner()
      ? formatKcc20BaseUnitsCompact(
          this.ownerCanonicalBalance(),
          this.tokenDecimals(),
        )
      : '--',
  );
  readonly ownerHasTokenHoldings = computed(() => {
    const position = this.ownerPosition();
    return (
      this.isPositiveBaseAmount(this.ownerCanonicalBalance()) ||
      this.isPositiveBaseAmount(this.ownerWrappedBalance()) ||
      (position?.openOrderCount ?? 0) > 0
    );
  });
  readonly ownerLastChangeMs = computed(
    () =>
      this.ownerPosition()?.lastChangeMs ??
      this.ownerTokenBalance()?.lastChangeMs,
  );

  readonly quoteErrors = computed(() => this.quote()?.errors ?? []);
  private readonly ownRestingOrderIds = computed(() => {
    const ids = new Set<string>();
    for (const order of this.openOrders()) {
      const orderId = this.normalizedOrderId(order.orderId);
      if (orderId && this.isOpenOrder(order)) {
        ids.add(orderId);
      }
    }

    const owner = this.normalizedOwner(this.owner());
    if (!owner) {
      return ids;
    }
    const orderbook = this.orderbook();
    for (const order of [
      ...(orderbook?.asks ?? []),
      ...(orderbook?.bids ?? []),
    ]) {
      const orderId = this.normalizedOrderId(order.orderId);
      if (
        orderId &&
        this.isOpenOrder(order) &&
        this.orderBelongsToOwner(order, owner)
      ) {
        ids.add(orderId);
      }
    }
    return ids;
  });
  readonly activeFillOrderId = computed(() => {
    const selected = this.selectedOrderId();
    if (selected) {
      return this.ownRestingOrderIds().has(selected.trim().toLowerCase())
        ? undefined
        : selected;
    }
    const quote = this.quote();
    if (
      (this.orderMode() !== 'market' && this.orderMode() !== 'limit') ||
      !quote?.valid ||
      quote.side !== this.quoteSide() ||
      this.isMultiSweepQuote(quote)
    ) {
      return undefined;
    }
    const quotedOrderId = quoteFillOrderId(
      this.quoteSide(),
      quote,
      this.orderMode() === 'limit'
        ? this.orderUnitPriceSompi()
        : quote.unitPriceSompi,
      this.quoteAmount(),
      this.tokenDecimals(),
    );
    return quotedOrderId &&
      this.ownRestingOrderIds().has(quotedOrderId.trim().toLowerCase())
      ? undefined
      : quotedOrderId;
  });
  readonly activeSweepQuote = computed(() => {
    const quote = this.quote();
    if (
      (this.orderMode() !== 'market' && this.orderMode() !== 'limit') ||
      sweepQuoteBlockedReason(this.quoteSide(), quote)
    ) {
      return undefined;
    }
    if (this.quoteIncludesOwnRestingOrder(quote)) {
      return undefined;
    }
    if (
      this.orderMode() === 'limit' &&
      !this.isPositiveInteger(this.orderUnitPriceSompi())
    ) {
      return undefined;
    }
    return quote;
  });
  readonly marketQuoteBlockedReason = computed(() => {
    if (this.orderMode() !== 'market') {
      return undefined;
    }
    if (this.quoteLoading()) {
      return undefined;
    }
    if (!this.isPositiveDecimal(this.quoteAmount())) {
      return undefined;
    }
    const quote = this.quote();
    if (!quote) {
      return MARKET_QUOTE_REFRESH_MESSAGE;
    }
    if (!quote.valid) {
      return (
        humanizeQuoteError(quote.errors[0]) ||
        'No matching order is available right now.'
      );
    }
    if (quote.executionMode === 'multi') {
      return sweepQuoteBlockedReason(this.quoteSide(), quote);
    }
    if (!this.activeFillOrderId()) {
      return 'No single matching order is available right now.';
    }
    return undefined;
  });
  readonly limitCrossesVisibleBook = computed(() => {
    if (
      this.orderMode() !== 'limit' ||
      !this.isPositiveDecimal(this.quoteAmount())
    ) {
      return false;
    }
    const unitPrice = parsePositiveBigInt(this.orderUnitPriceSompi());
    if (unitPrice === undefined) {
      return false;
    }
    const levels =
      this.quoteSide() === 'buy' ? this.depthAsksDisplay() : this.depthBids();
    return levels.some((row) => {
      const levelPrice = parsePositiveBigInt(row.level.unitPriceSompi);
      if (levelPrice === undefined) return false;
      return this.quoteSide() === 'buy'
        ? levelPrice <= unitPrice
        : levelPrice >= unitPrice;
    });
  });
  readonly limitQuoteBlockedReason = computed(() => {
    if (this.orderMode() !== 'limit' || !this.limitCrossesVisibleBook()) {
      return undefined;
    }
    if (!this.activeMarketExecution()) {
      return undefined;
    }
    if (this.quoteLoading()) {
      return undefined;
    }
    const quote = this.quote();
    if (!quote) {
      return 'Refresh the limit quote before building this fill.';
    }
    if (!quote.valid) {
      return (
        humanizeQuoteError(quote.errors[0]) ||
        'No matching order is available right now.'
      );
    }
    if (quote.executionMode === 'multi') {
      return sweepQuoteBlockedReason(this.quoteSide(), quote);
    }
    if (!this.activeFillOrderId()) {
      return 'No single matching order is available right now.';
    }
    return undefined;
  });
  readonly activeMarketExecution = computed(
    () => !!this.activeFillOrderId() || !!this.activeSweepQuote(),
  );
  readonly marketableLimitExecution = computed(
    () => this.orderMode() === 'limit' && this.activeMarketExecution(),
  );
  readonly activeExecutionActionLabel = computed(() => {
    if (this.quoteSide() === 'sell' && this.needsWrapToTrade()) {
      return 'Wrap to trade';
    }
    if (this.orderBalanceChecking()) {
      return 'Checking balance…';
    }
    if (this.orderInsufficientBalance()) {
      return 'Insufficient balance';
    }
    if (this.marketableLimitExecution()) {
      return 'Fill now';
    }
    if (this.activeSweepQuote()) {
      return 'Sweep';
    }
    if (this.activeFillOrderId()) {
      return 'Fill';
    }
    const ladderCount = this.ladderPlan()?.unitPriceSompi.length ?? 0;
    if (ladderCount > 1) {
      return this.quoteSide() === 'buy'
        ? `Buy ${ladderCount} orders`
        : `Sell ${ladderCount} orders`;
    }
    return this.quoteSide() === 'buy' ? 'Buy' : 'Sell';
  });
  // `marketQuoteBlockedReason` (surfaced via `orderBalanceError`) already
  // repeats `quote.errors[0]` as its market-mode fallback text, so drop it
  // here to avoid showing the same message in two stacked alerts.
  readonly visibleQuoteErrors = computed(() => {
    const errors = this.quoteErrors();
    if (!errors.length) {
      return [];
    }
    if (this.orderMode() === 'limit' && !this.limitCrossesVisibleBook()) {
      return [];
    }
    const blocked = this.marketQuoteBlockedReason();
    return blocked ? errors.filter((error) => error !== blocked) : errors;
  });
  readonly activeFillUnitPriceSompi = computed(() => {
    if (!this.activeFillOrderId()) {
      return undefined;
    }
    if (this.selectedOrderId()) {
      return this.orderUnitPriceSompi().trim();
    }
    return this.quote()?.unitPriceSompi?.trim();
  });

  /**
   * `Available` semantics switch with the trade side (KCC20-46):
   *  - buying → the KAS balance the wallet can spend;
   *  - selling → the amount of this token (orderbook balance) the wallet can sell.
   */
  readonly availableLabel = computed(() =>
    this.quoteSide() === 'buy'
      ? 'Available KAS'
      : `Available ${this.tradeTicker()}`,
  );
  /**
   * Display-only cap of 2 decimals for the "Available" hint — this is a
   * balance readout, not an editable amount, so it doesn't need (or want)
   * the full 8-decimal precision `formatPrettyNumber` would otherwise show.
   */
  readonly availableValue = computed(() => {
    if (this.quoteSide() === 'buy') {
      return this.owner()
        ? formatPrettyNumber(this.availableKas(), { maxDecimals: 2 })
        : '--';
    }
    if (!this.owner()) {
      return '--';
    }
    // This must always reflect the wallet's own orderbook balance. A fill quote's
    // `availableAmount` is the remaining fillable size of one specific order,
    // which is typically far smaller than everything the wallet actually holds.
    const amount = this.ownerWrappedBalance() || '0';
    const grouped = formatKcc20BaseUnitsValueGrouped(
      amount,
      this.tokenDecimals(),
    );
    return formatPrettyNumber(grouped, { maxDecimals: 2 });
  });

  /**
   * Max amount the ticket's percent slider drags against — same "available"
   * balance the preset buttons use, expressed as a plain display-unit
   * number rather than a base-units string (see `tokenPresetAmount`).
   */
  private readonly quoteAmountSliderMax = computed(() => {
    if (this.quoteSide() === 'buy') {
      return this.maxBuyableTokens();
    }
    const capAmount = this.activeFillOrderId()
      ? this.quote()?.availableAmount
      : this.maxSellableTokenBaseUnits()?.toString();
    const max = this.tokenPresetAmount(capAmount, 100);
    return max ? Number(max) : 0;
  });

  readonly quoteAmountSliderDisabled = computed(
    () => this.quoteAmountSliderMax() <= 0,
  );

  /** Current typed amount, expressed as a 0–100 percent of the slider max. */
  readonly quoteAmountSliderValue = computed(() => {
    const max = this.quoteAmountSliderMax();
    if (!max) {
      return 0;
    }
    const current = Number(this.quoteAmount());
    if (!Number.isFinite(current) || current <= 0) {
      return 0;
    }
    return Math.min(100, (current / max) * 100);
  });

  /**
   * Ladder UI is for brand-new resting limits only — a fill/sweep already
   * has a target, and market mode has no typed unit price to step.
   */
  readonly ladderOrdersAvailable = computed(
    () =>
      this.orderMode() === 'limit' &&
      !this.activeFillOrderId() &&
      !this.activeSweepQuote(),
  );

  readonly ladderPlan = computed(() => {
    if (!this.ladderOrdersAvailable() || this.ladderCount() <= 1) {
      return undefined;
    }
    const percent = parseLadderPercent(this.ladderPercent());
    if (percent === undefined) {
      return undefined;
    }
    return planLadderLimitOrders({
      tokenAmount: this.quoteAmount().trim(),
      baseUnitPriceSompi: this.orderUnitPriceSompi().trim(),
      decimals: this.tokenDecimals(),
      count: this.ladderCount(),
      direction: this.ladderDirection(),
      percent,
      protocolFeeBps: this.protocolFeeBps(),
    });
  });

  readonly ladderPreview = computed(() => {
    const plan = this.ladderPlan();
    if (!plan || plan.unitPriceSompi.length < 2) {
      return undefined;
    }
    const step = this.ladderPercent().trim() || '0';
    const dir = this.ladderDirection() === 'increment' ? 'higher' : 'lower';
    const first = formatSompi(
      plan.unitPriceSompi[0],
      this.kasDisplayMaxDecimals,
    );
    const last = formatSompi(
      plan.unitPriceSompi[plan.unitPriceSompi.length - 1],
      this.kasDisplayMaxDecimals,
    );
    return `${plan.unitPriceSompi.length} orders · same amount · ${step}% ${dir} (${first} → ${last})`;
  });

  readonly ladderSteps = computed(() => {
    const plan = this.ladderPlan();
    if (!plan) {
      return [];
    }
    return plan.unitPriceSompi.map((unitPriceSompi, index) => ({
      index: index + 1,
      unitPriceSompi,
      notionalSompi: plan.notionalsSompi[index].toString(),
    }));
  });

  /** Wallet KAS balance available to spend on a buy (whole KAS units). */
  private readonly availableKas = computed(() =>
    this.trimDecimal(Math.max(0, this.walletManager.walletKaspaBalance())),
  );

  /** Ticker shown for the token side of the ticket. */
  readonly tradeTicker = computed(
    () =>
      this.appMetadata()?.ticker ||
      this.selectedMarket()?.metadata?.ticker ||
      this.nativeTicker() ||
      'TOKEN',
  );
  readonly orderDraftReady = computed(() => {
    if (
      !this.owner() ||
      !this.tokenDecimalsConfirmed() ||
      !this.wrapperTradingReady() ||
      !this.isPositiveDecimal(this.quoteAmount())
    ) {
      return false;
    }
    if (this.orderMode() === 'market') {
      return (
        ((!!this.activeFillOrderId() && !!this.activeFillUnitPriceSompi()) ||
          !!this.activeSweepQuote()) &&
        !this.marketQuoteBlockedReason()
      );
    }
    if (this.limitCrossesVisibleBook()) {
      const canRest =
        this.isPositiveInteger(this.orderUnitPriceSompi()) &&
        this.orderNotionalSompi() !== undefined;
      if (!this.activeMarketExecution()) {
        return !this.quoteLoading() && canRest;
      }
      return (
        !this.quoteLoading() &&
        this.activeMarketExecution() &&
        !this.limitQuoteBlockedReason()
      );
    }
    return (
      this.isPositiveInteger(this.orderUnitPriceSompi()) &&
      this.orderNotionalSompi() !== undefined
    );
  });
  readonly mintDraftReady = computed(
    () =>
      !!this.owner() &&
      this.tokenDecimalsConfirmed() &&
      !this.mintActionBlockedReason() &&
      this.mintable() &&
      this.mintAmountQuote() !== undefined &&
      !this.mintAmountQuote()?.belowMinimumKas &&
      !this.mintAmountCapError() &&
      !this.mintRemainingSupplyError(),
  );
  readonly wrapDraftReady = computed(
    () =>
      !!this.owner() &&
      this.tokenDecimalsConfirmed() &&
      this.wrapActionReady() &&
      this.isPositiveDecimal(this.wrapAmount()),
  );
  readonly unwrapDraftReady = computed(
    () =>
      !!this.owner() &&
      this.tokenDecimalsConfirmed() &&
      this.unwrapActionReady() &&
      this.isPositiveDecimal(this.unwrapAmount()),
  );

  /**
   * Reason the wallet can't fund the wrap amount, or `undefined` when it can
   * (or when there isn't enough context to tell yet). Mirrors
   * {@link orderBalanceError}'s sell-side check, but against the canonical
   * (unwrapped) balance, since wrapping moves canonical KCC20 into the
   * wrapper reserve.
   */
  readonly wrapBalanceError = computed<string | undefined>(() => {
    if (!this.owner()) {
      return undefined;
    }
    const amount = parseKcc20DisplayAmountToBaseUnits(
      this.wrapAmount().trim(),
      this.tokenDecimals(),
    );
    if (!amount || amount <= 0n) {
      return undefined;
    }
    const raw = this.ownerCanonicalBalance()?.trim();
    // Holdings not loaded (or unparseable) yet — not enough context to tell.
    if (!raw || !/^\d+$/.test(raw)) {
      return undefined;
    }
    if (amount <= BigInt(raw)) {
      return undefined;
    }
    return `Not enough ${this.tradeTicker()} to wrap this amount.`;
  });

  /**
   * Same as {@link wrapBalanceError} but for unwrapping, checked against the
   * wrapped (orderbook) balance since unwrapping moves it back out.
   */
  readonly unwrapBalanceError = computed<string | undefined>(() => {
    if (!this.owner()) {
      return undefined;
    }
    const amount = parseKcc20DisplayAmountToBaseUnits(
      this.unwrapAmount().trim(),
      this.tokenDecimals(),
    );
    if (!amount || amount <= 0n) {
      return undefined;
    }
    const raw = this.ownerWrappedBalance()?.trim();
    if (!raw || !/^\d+$/.test(raw)) {
      return undefined;
    }
    if (amount <= BigInt(raw)) {
      return undefined;
    }
    return `Not enough ${this.tradeTicker()} to unwrap this amount.`;
  });

  /**
   * These gate the wrap/unwrap percentage-preset buttons: with no balance the
   * preset would silently no-op, so the button is disabled instead.
   */
  readonly wrapPresetAvailable = computed(() =>
    Boolean(this.tokenPresetAmount(this.ownerCanonicalBalance(), 100)),
  );
  readonly unwrapPresetAvailable = computed(() =>
    Boolean(this.tokenPresetAmount(this.ownerWrappedBalance(), 100)),
  );
  readonly wrapperDeployReady = computed(
    () =>
      !!this.owner() &&
      !this.wrapperDeployActionBlockedReason() &&
      !this.wrappedMarketId() &&
      // Fail closed (not `?? true`): without a resolved `tokenActions()` we
      // have no local signal at all for deploy readiness — the deploy button
      // stays disabled for that brief window rather than being briefly
      // enabled ahead of a backend check we haven't seen yet.
      this.tokenActions()?.deployWrapper.enabled === true &&
      this.isNonNegativeInteger(this.wrapperDeployRootAmount()),
  );
  readonly hasCandles = computed(() => this.candles().length > 0);
  readonly activityRows = computed(() =>
    this.activity()
      .map((event) => this.toActivityRow(event))
      .sort((a, b) => b.timeMs - a.timeMs),
  );
  readonly tokenActivityRows = computed(() =>
    this.tokenActivity()
      .map((event) => this.toActivityRow(event))
      .sort((a, b) => b.timeMs - a.timeMs),
  );

  /** Holders: append-paged via GET /tokens/{id}/holders (infinite scroll). */
  static readonly HOLDERS_PAGE_SIZE = 12;
  readonly holders = signal<Kcc20TokenHolder[]>([]);
  readonly holdersTotal = signal(0);
  readonly holdersHasNextPage = signal(false);
  readonly holdersLoading = signal(false);
  readonly holdersLoadingMore = signal(false);
  readonly holdersBody = viewChild<ElementRef<HTMLElement>>('holdersBody');
  private holdersScrollCleanup?: () => void;
  private holdersLoadGeneration = 0;
  private readonly copiedHolderRank = signal<number | null>(null);
  private readonly copiedActivityWhoKey = signal<string | null>(null);
  private readonly copiedHeaderId = signal<'token' | 'market' | null>(null);
  /** Accumulated holders with 1-based global ranks. */
  readonly rankedHolders = computed(() =>
    this.holders().map((holder, index) => ({
      holder,
      rank: index + 1,
    })),
  );
  /** Highlighted count next to the Holders column title. */
  readonly holdersCountLabel = computed(() => {
    const count = resolveHoldersCount(
      this.tokenDetail()?.holderCount,
      this.holdersTotal(),
    );
    return count === undefined ? undefined : formatCount(count);
  });

  /**
   * Session price change as a percent, derived from the stats window
   * (open → close), falling back to the loaded candles. Real data only —
   * returns undefined when neither source has a usable open/close pair.
   */
  readonly priceChangePct = computed(() => {
    const stats = this.stats();
    let open = Number(stats?.openUnitPriceSompi ?? 0);
    let close = Number(stats?.closeUnitPriceSompi ?? 0);
    if (!(open > 0 && close > 0)) {
      const candles = this.candles();
      if (candles.length) {
        open = Number(candles[0].openUnitPriceSompi || 0);
        close = Number(candles[candles.length - 1].closeUnitPriceSompi || 0);
      }
    }
    if (!(open > 0 && close > 0)) {
      return undefined;
    }
    return ((close - open) / open) * 100;
  });
  readonly priceChangeLabel = computed(() => {
    const pct = this.priceChangePct();
    if (pct === undefined) {
      return undefined;
    }
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  });
  readonly priceChangeUp = computed(() => (this.priceChangePct() ?? 0) >= 0);

  /** Percent of max supply already minted (0–100), for the Mint progress bar. */
  readonly mintedPct = computed(() => {
    const supply = this.tokenDetail()?.supply;
    const minted = Number(supply?.mintedSupply ?? 0);
    const max = Number(supply?.maxSupply ?? 0);
    if (max > 0 && minted >= 0) {
      return Math.max(0, Math.min(100, Math.round((minted / max) * 100)));
    }
    return 0;
  });

  readonly mintAmountQuote = computed(() =>
    calculateKcc20PaidMintAmountSompi(
      this.mintAmount().trim(),
      this.tokenDetail()?.mintPolicy?.mintPricePerTokenSompi,
      this.tokenDecimals(),
      this.tokenDetail()?.mintPolicy?.protocolFeeBps,
    ),
  );

  /** Smallest token amount that clears the paid-mint 0.55 KAS UI floor. */
  readonly minMintTokenAmount = computed(() =>
    minPaidMintTokenDisplayAmount(
      this.tokenDetail()?.mintPolicy?.mintPricePerTokenSompi,
      this.tokenDecimals(),
      this.tokenDetail()?.mintPolicy?.protocolFeeBps,
    ),
  );
  readonly minPaidMintKasLabel = KCC20_MIN_PAID_MINT_KAS_LABEL;

  readonly mintTotalCostSompi = computed<bigint | undefined>(() => {
    const totalSompi = this.mintAmountQuote()?.totalSompi;
    return totalSompi === undefined ? undefined : BigInt(totalSompi);
  });

  readonly mintAdjustedAmountMessage = computed(() => {
    const quote = this.mintAmountQuote();
    if (!quote) {
      return undefined;
    }
    return kcc20PaidMintAdjustmentMessage(quote, {
      unitPriceSompi: this.tokenDetail()?.mintPolicy?.mintPricePerTokenSompi,
      decimals: this.tokenDecimals(),
      protocolFeeBps: this.tokenDetail()?.mintPolicy?.protocolFeeBps,
      ticker: this.displayName(),
    });
  });

  readonly mintAmountCapError = computed(() => {
    if (this.tokenDetail()?.mintPolicy?.policy !== 'public') {
      return undefined;
    }
    const quote = this.mintAmountQuote();
    if (quote?.belowMinimumKas) {
      return undefined;
    }
    if (
      !exceedsKcc20PublicMintMaxAmount(
        quote?.adjustedTokenAmountBaseUnits,
        this.tokenDecimals(),
      )
    ) {
      return undefined;
    }
    return KCC20_PUBLIC_MINT_MAX_AMOUNT_ERROR;
  });

  readonly mintRemainingSupplyError = computed(() => {
    const quote = this.mintAmountQuote();
    if (!quote || quote.belowMinimumKas) {
      return undefined;
    }
    const remainingRaw =
      this.tokenDetail()?.supply?.remainingMintSupply?.trim();
    if (!remainingRaw || !/^\d+$/.test(remainingRaw)) {
      return undefined;
    }
    if (BigInt(quote.adjustedTokenAmountBaseUnits) > BigInt(remainingRaw)) {
      return 'Mint amount exceeds remaining mintable supply.';
    }
    return undefined;
  });

  /** Mint CTA label — shortfall reasons beat the default "Mint". */
  readonly mintActionLabel = computed(() => {
    if (this.mintRemainingSupplyError()) {
      return 'Unavailable mint amount';
    }
    if (this.mintBalanceError()) {
      return 'Insufficient balance';
    }
    return 'Mint';
  });

  /**
   * Single mint-panel status slot: at most one alert renders at a time
   * instead of the three underlying signals stacking/unstacking
   * independently as the typed amount changes. Hard limits win over the
   * soft, informational adjustment note.
   */
  readonly mintStatus = computed<
    { variant: AlertVariant; text: string } | undefined
  >(() => {
    if (!this.mintAmountWarningsSettled()) {
      return undefined;
    }
    // Cap / remaining shortfalls stay off the snackbar until the user edits
    // the amount — the default/'seeded' mint input must not toast on load
    // when remaining supply is 0 (or smaller than the default).
    const touched = this.mintAmountTouched();
    const text =
      (touched ? this.mintAmountCapError() : undefined) ??
      (touched ? this.mintRemainingSupplyError() : undefined) ??
      this.mintAdjustedAmountMessage();
    return text ? { variant: 'warning', text } : undefined;
  });

  /**
   * Blocks minting when the total mint cost exceeds the wallet's KAS balance,
   * mirroring {@link orderBalanceError}'s buy-side check. Not toasted: after a
   * mint/trade the wallet can read low until change UTXOs settle. Callers
   * should treat {@link mintBalanceChecking} as loading, not a shortfall.
   */
  readonly mintBalanceError = computed<string | undefined>(() => {
    if (!this.owner()) {
      return undefined;
    }
    // Mirror `mintActionBlockedReason`: a non-owner can't mint while public
    // mint is paused, so a balance shortfall isn't a real blocker for them.
    if (this.publicMintPaused() && !this.publicMintOwner()) {
      return undefined;
    }
    // KaspaCom's balance can read as a false low right after connect/restore
    // and after a mint/trade indexes (spent UTXOs vs. not-yet-settled change —
    // see WalletManager.walletBalanceConfirmed). Treat that window as loading,
    // not as an authoritative shortfall.
    if (!this.walletManager.walletBalanceConfirmed()) {
      return undefined;
    }
    const total = this.mintTotalCostSompi();
    if (total === undefined) {
      return undefined;
    }
    // `walletKaspaBalance()` is a number; `toFixed(8)` avoids scientific
    // notation (e.g. `1e-8`) that `kasToSompi` would reject as `0`.
    const balanceSompi = BigInt(
      kasToSompi(this.walletManager.walletKaspaBalance().toFixed(8)),
    );
    return total > balanceSompi
      ? `This mint costs ${formatSompi(
          total.toString(),
          TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS,
        )}; wallet has ${formatSompi(
          balanceSompi.toString(),
          TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS,
        )}.`
      : undefined;
  });

  /**
   * True while the wallet's balance is still settling after connect (see
   * {@link mintBalanceError}) — the mint button should show a neutral
   * loading state instead of "Mint" or a false "Insufficient balance".
   */
  readonly mintBalanceChecking = computed(
    () => !!this.owner() && !this.walletManager.walletBalanceConfirmed(),
  );

  /**
   * Coupons discount active fills. A brand-new resting order has no fill to
   * discount yet, so the toggle is only meaningful for single fills or market
   * sweeps backed by a coupon-enabled quote.
   */
  readonly activeSweepFeeTicketRootId = computed(() =>
    feeTicketRootIdForSweepQuote(
      this.activeSweepQuote(),
      this.activeWrapper()?.feeTicketId,
    ),
  );
  readonly feeTicketApplicable = computed(() => {
    if (!kcc20FeeTicketFeatureEnabled()) {
      return false;
    }
    if (!this.activeMarketExecution()) {
      return false;
    }
    const quote = this.quote();
    if (!quote?.valid) {
      return false;
    }
    if (this.activeSweepQuote()) {
      return !!this.activeSweepFeeTicketRootId();
    }
    return (
      !!quote.feeTicketEligible &&
      quote.feeTicketDiscountedProtocolFeeSompi != null
    );
  });
  /** True when the coupon discount is toggled on AND the wallet can spend one. */
  readonly feeTicketActive = computed(
    () =>
      this.useFeeTicket() &&
      this.canUseFeeTicket() &&
      this.effectiveFeeTicketDiscountedProtocolFeeSompi(this.quote()) != null,
  );
  /** Coupons needed for the current fill/sweep quote (independent of toggle). */
  readonly requiredFeeTicketCount = computed(() =>
    this.feeTicketApplicable()
      ? feeTicketRequiredCountForExecution(
          this.quote(),
          this.activeFillOrderId(),
        )
      : 0,
  );
  readonly availableFeeTicketCount = computed(
    () => this.feeTicketAvailable()?.availableTickets,
  );
  /**
   * Wallet has enough active coupons for this quote. When false the toggle
   * stays visible (with "0 left") but cannot be turned on.
   */
  readonly canUseFeeTicket = computed(() => {
    if (!this.feeTicketApplicable()) {
      return false;
    }
    const available = this.availableFeeTicketCount();
    if (available === undefined || available === null) {
      return false;
    }
    const required = Math.max(this.requiredFeeTicketCount(), 1);
    return available >= required;
  });
  readonly feeTicketUsageMessage = computed(() =>
    this.feeTicketActive()
      ? feeTicketInsufficientReason(
          this.requiredFeeTicketCount(),
          this.availableFeeTicketCount(),
          { loading: this.feeTicketBalanceLoading() },
        )
      : undefined,
  );
  /** Compact leftover count shown next to the coupon toggle. */
  readonly feeTicketLeftLabel = computed(() => {
    if (
      this.feeTicketBalanceLoading() &&
      this.availableFeeTicketCount() === undefined
    ) {
      return '…';
    }
    const available = this.availableFeeTicketCount();
    if (available === undefined) {
      return undefined;
    }
    const required = this.requiredFeeTicketCount();
    return required > 0
      ? `Uses ${formatCount(required)} · ${formatCount(available)} left`
      : `${formatCount(available)} left`;
  });
  /** KAS saved on protocol fee when the coupon toggle is on. */
  readonly feeTicketDiscountLabel = computed(() => {
    if (!this.feeTicketActive()) {
      return '—';
    }
    const quote = this.quote();
    const fullFee = this.parseSompi(quote?.protocolFeeSompi);
    const discountedFee = this.parseSompi(
      this.effectiveFeeTicketDiscountedProtocolFeeSompi(quote),
    );
    if (fullFee === undefined || discountedFee === undefined) {
      return '—';
    }
    const savings = fullFee > discountedFee ? fullFee - discountedFee : 0n;
    return savings > 0n
      ? `−${formatSompi(savings.toString(), TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS)}`
      : '—';
  });
  readonly feePayerLabel = computed(() => {
    const payer = this.quote()?.feePayer;
    if (payer === 'buyer') return 'Buyer';
    if (payer === 'seller') return 'Seller';
    if (payer === 'matcher') return 'Matcher';
    if (payer === 'none') return 'None';
    return undefined;
  });

  /**
   * Amount typed into the ticket, parsed to base units. `null` while the
   * field is empty/invalid for the token's decimals.
   */
  private readonly quoteAmountBaseUnits = computed(() =>
    parseKcc20DisplayAmountToBaseUnits(
      this.quoteAmount().trim(),
      this.tokenDecimals(),
    ),
  );
  /**
   * Order notional (amount × unit price), computed straight from the
   * ticket's own inputs. Brand-new orders use the backend-aligned exact
   * gross-sompi helper so the UI total matches the amount the build request
   * will use after rounding down. Existing fills still compute directly from
   * the selected fill amount and order price.
   */
  readonly orderNotionalSompi = computed<bigint | undefined>(() => {
    if (!this.activeMarketExecution()) {
      const plan = this.ladderPlan();
      if (plan) {
        return plan.totalNotionalSompi;
      }
      const total = calculateKcc20OrderTotalSompi(
        this.quoteAmount(),
        this.orderUnitPriceSompi(),
        this.tokenDecimals(),
      )?.totalSompi;
      return total === undefined ? undefined : BigInt(total);
    }

    const quotedTotalSompi = kcc20FillGrossSompi(this.quote());
    if (quotedTotalSompi !== undefined) {
      return quotedTotalSompi;
    }

    const unitPriceSompi = this.parseSompi(
      this.activeFillUnitPriceSompi() ?? this.orderUnitPriceSompi(),
    );
    const amountBaseUnits = this.quoteAmountBaseUnits();
    if (
      unitPriceSompi === undefined ||
      !amountBaseUnits ||
      amountBaseUnits <= 0n
    ) {
      return undefined;
    }
    const scale = 10n ** BigInt(this.tokenDecimals());
    return (unitPriceSompi * amountBaseUnits) / scale;
  });

  /** Token-level protocol fee rate, used to estimate the fee on a brand-new
   *  order (there's no per-fill quote to read it from yet). */
  private readonly protocolFeeBps = computed(() => {
    const detail = this.tokenDetail();
    const bps = detail?.protocolFeeBps ?? detail?.mintPolicy?.protocolFeeBps;
    return Number.isInteger(bps) && (bps ?? 0) >= 0 ? (bps ?? 0) : 0;
  });

  /** Bare percentage (e.g. "2%"), shown inline next to the fee line item
   *  instead of a separate row — it's the same `protocolFeeBps` the fee
   *  itself is computed from, so it always tracks the token's real rate. */
  readonly protocolFeeRateLabel = computed(
    () => `${this.trimDecimal(this.protocolFeeBps() / 100)}%`,
  );

  /**
   * Protocol fee shown in the ticket. Filling an existing order uses the
   * backend quote — authoritative for that exact fill, and the only case
   * Coupon discounts apply to. Creating a brand-new order has no such
   * per-order quote, so the fee is estimated locally from the token's
   * protocol fee bps against the notional above.
   */
  readonly orderProtocolFeeSompi = computed<bigint | undefined>(() => {
    if (this.activeMarketExecution()) {
      return kcc20FillProtocolFeeSompi(
        this.quoteWithEffectiveFeeTicketDiscount(this.quote()),
        this.feeTicketActive(),
      );
    }
    const plan = this.ladderPlan();
    if (plan) {
      return plan.totalProtocolFeeSompi;
    }
    const notional = this.orderNotionalSompi();
    if (notional === undefined) {
      return undefined;
    }
    return this.estimatedProtocolFeeSompi(notional);
  });

  /** Plain-language recap of the order draft, e.g. "Sell 500 TOKEN @ 30 KAS
   *  each" — shown above the fee breakdown so the ticket is readable at a
   *  glance instead of only as raw line items. */
  readonly orderSummaryLine = computed(() => {
    const amount = this.quoteAmountBaseUnits();
    const unitPriceSompi = this.orderSummaryUnitPriceSompi();
    if (!amount || amount <= 0n || !this.isPositiveInteger(unitPriceSompi)) {
      return undefined;
    }
    const verb = this.quoteSide() === 'buy' ? 'Buy' : 'Sell';
    const amountLabel = formatPrettyNumber(this.quoteAmount().trim(), {
      maxDecimals: TokenPageComponent.TRADE_AMOUNT_MAX_DECIMALS,
    });
    const plan = this.ladderPlan();
    if (plan && plan.unitPriceSompi.length > 1) {
      const first = formatSompi(
        plan.unitPriceSompi[0],
        TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS,
      ).replace(/ KAS$/, '');
      const last = formatSompi(
        plan.unitPriceSompi[plan.unitPriceSompi.length - 1],
        TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS,
      ).replace(/ KAS$/, '');
      return `${verb} ${amountLabel} ${this.tradeTicker()} × ${plan.unitPriceSompi.length} @ ${first} → ${last} KAS each`;
    }
    const priceLabel = formatSompi(
      unitPriceSompi,
      TokenPageComponent.KAS_DISPLAY_MAX_DECIMALS,
    ).replace(/ KAS$/, '');
    return `${verb} ${amountLabel} ${this.tradeTicker()} @ ${priceLabel} KAS each`;
  });

  private orderSummaryUnitPriceSompi(): string {
    const quote = this.quote();
    if (this.activeMarketExecution()) {
      return (
        quote?.averageUnitPriceSompi?.trim() ||
        this.activeFillUnitPriceSompi() ||
        quote?.unitPriceSompi?.trim() ||
        ''
      );
    }
    return this.orderUnitPriceSompi().trim();
  }

  readonly settlementLabel = computed(() => {
    const prefix = this.activeMarketExecution() ? 'You' : 'On fill you';
    return this.quoteSide() === 'buy' ? `${prefix} pay` : `${prefix} receive`;
  });
  readonly settlementSompi = computed(() => {
    if (this.activeMarketExecution()) {
      return kcc20FillSettlementSompi(
        this.quoteWithEffectiveFeeTicketDiscount(this.quote()),
        this.feeTicketActive(),
      );
    }
    const plan = this.ladderPlan();
    if (plan) {
      return this.quoteSide() === 'buy'
        ? plan.totalBuySettlementSompi.toString()
        : (plan.totalNotionalSompi > plan.totalProtocolFeeSompi
            ? plan.totalNotionalSompi - plan.totalProtocolFeeSompi
            : 0n
          ).toString();
    }
    const notional = this.orderNotionalSompi();
    if (notional === undefined) {
      return undefined;
    }
    const fee = this.orderProtocolFeeSompi() ?? 0n;
    return this.quoteSide() === 'buy'
      ? (notional + fee).toString()
      : (notional > fee ? notional - fee : 0n).toString();
  });

  /**
   * Reason the wallet can't fund the ticket order, or `undefined` when it can
   * (or when there isn't enough context to tell yet). A buy is blocked when the
   * order total — notional + protocol fee, i.e. {@link settlementSompi} — is
   * more than the wallet's KAS balance; a sell is blocked when the token amount
   * exceeds the wallet's tradable (V3) token balance. This stops the user from
   * building (and being sent to sign) an order the backend would reject for
   * insufficient funds. It's advisory only for a disconnected wallet.
   */
  readonly orderBalanceError = computed<string | undefined>(() => {
    if (!this.owner()) {
      return undefined;
    }
    // Native-only sells cannot execute until wrapped. Surface this before market
    // quote blockers so a doomed /quote never replaces wrap guidance (or spins
    // the Sell button while the request is in flight).
    if (this.quoteSide() === 'sell' && this.needsWrapToTrade()) {
      return WRAP_TO_TRADE_MESSAGE;
    }
    const marketBlock = this.marketQuoteBlockedReason();
    if (marketBlock) {
      return marketBlock;
    }
    if (this.quoteSide() === 'buy') {
      // The wallet's reported balance can read as a transient false low right
      // after a prior trade indexes (spent UTXOs vs. not-yet-settled change —
      // see WalletManager.walletBalanceConfirmed) as well as right after
      // KaspaCom connect/restore. Treat that window as loading, not as an
      // authoritative shortfall.
      if (!this.walletManager.walletBalanceConfirmed()) {
        return undefined;
      }
      const total = this.settlementSompi();
      if (total === undefined) {
        return undefined;
      }
      // `walletKaspaBalance()` is a number; `toFixed(8)` avoids scientific
      // notation (e.g. `1e-8`) that `kasToSompi` would reject as `0`.
      const balanceSompi = BigInt(
        kasToSompi(this.walletManager.walletKaspaBalance().toFixed(8)),
      );
      return BigInt(total) > balanceSompi
        ? 'Not enough KAS to cover this order total.'
        : undefined;
    }
    const amount = this.quoteAmountBaseUnits();
    if (!amount || amount <= 0n) {
      return undefined;
    }
    const raw = this.ownerWrappedBalance()?.trim();
    // Holdings not loaded (or unparseable) yet — not enough context to tell.
    if (!raw || !/^\d+$/.test(raw)) {
      return undefined;
    }
    const sellCount = BigInt(this.ladderPlan()?.unitPriceSompi.length ?? 1);
    if (amount * sellCount <= BigInt(raw)) {
      return undefined;
    }
    return `Not enough ${this.tradeTicker()} to sell this amount.`;
  });

  /**
   * Single trade-ticket bottom status slot: at most one alert renders at a
   * time instead of `orderBalanceError`, `orderMinValueError`, and
   * `visibleQuoteErrors` stacking independently. The wallet-side funding
   * blocker is the most actionable single fact, so it wins over the order
   * value floor, which in turn wins over a generic quote error.
   */
  readonly orderStatus = computed<
    { variant: AlertVariant; text: string } | undefined
  >(() => {
    const balanceError = this.orderBalanceError();
    if (balanceError) {
      if (this.orderTicketActionInFlight()) {
        return undefined;
      }
      return {
        variant:
          balanceError === MARKET_QUOTE_REFRESH_MESSAGE ? 'info' : 'warning',
        text: balanceError,
      };
    }
    const minValueError = this.orderMinValueError();
    if (minValueError) {
      return { variant: 'warning', text: minValueError };
    }
    const quoteErrors = this.visibleQuoteErrors();
    return quoteErrors.length
      ? {
          variant: 'warning',
          text: quoteErrors
            .map((error) => humanizeQuoteError(error) ?? error)
            .join(', '),
        }
      : undefined;
  });

  /**
   * Quote / ticket warnings that should snackbar. Excludes the refresh
   * prompt (would toast on every keystroke before /quote lands), wrap-to-
   * trade (the button already relabels), and wallet-balance shortfalls
   * (UTXO-settle false lows).
   */
  readonly orderTicketToast = computed(() => {
    const status = this.orderStatus();
    if (!status) {
      return undefined;
    }
    if (
      status.text === MARKET_QUOTE_REFRESH_MESSAGE ||
      status.text === WRAP_TO_TRADE_MESSAGE ||
      this.orderInsufficientBalance()
    ) {
      return undefined;
    }
    return status;
  });

  private readonly orderTicketActionInFlight = computed(() => {
    const currentTokenId = this.covenantId().toLowerCase();
    for (const [actionId, tokenId] of this.orderTicketActionTokenIds()) {
      if (tokenId !== currentTokenId) {
        continue;
      }
      const phase = this.walletOperationQueue.phaseOf(actionId);
      if (
        phase === 'awaiting-signature' ||
        phase === 'signing' ||
        phase === 'submitting'
      ) {
        return true;
      }
    }
    return false;
  });

  /**
   * Brand-new limit orders must meet the 0.5 KAS order-value floor. Market
   * fills/sweeps are quoted by the backend and skip this client check.
   */
  readonly orderMinValueError = computed<string | undefined>(() => {
    if (this.orderMode() !== 'limit' || this.activeMarketExecution()) {
      return undefined;
    }
    const plan = this.ladderPlan();
    if (plan) {
      return ladderMeetsMinOrderValue(plan)
        ? undefined
        : `Each order must be at least ${KCC20_MIN_PROTOCOL_FEE_KAS_LABEL} KAS.`;
    }
    const notional = this.orderNotionalSompi();
    if (notional === undefined) {
      return undefined;
    }
    if (notional < KCC20_MIN_PROTOCOL_FEE_SOMPI) {
      return `Order value must be at least ${KCC20_MIN_PROTOCOL_FEE_KAS_LABEL} KAS.`;
    }
    return undefined;
  });

  /**
   * True only when {@link orderBalanceError} is a real funds shortfall
   * (not the unrelated `marketQuoteBlockedReason` it also surfaces) — used
   * to swap the ticket button's label for a clearer "Insufficient balance".
   */
  readonly orderInsufficientBalance = computed(
    () => !!this.orderBalanceError() && !this.marketQuoteBlockedReason(),
  );

  /**
   * True while the wallet's KAS balance is still settling (see
   * {@link orderBalanceError}) on a buy ticket — the ticket button should
   * show a neutral loading state instead of "Buy"/"Fill"/etc. or a false
   * "Insufficient balance".
   */
  readonly orderBalanceChecking = computed(
    () =>
      !!this.owner() &&
      this.quoteSide() === 'buy' &&
      !this.walletManager.walletBalanceConfirmed(),
  );

  /**
   * Exact reason the order CTA is disabled. FeeTicket support is deliberately
   * absent from these gates: coupons are optional and a wrapper without them
   * remains tradable when the indexed trade action says it is enabled.
   */
  readonly orderActionDisabledReason = computed<string | undefined>(() => {
    if (!this.owner()) return 'Connect wallet to continue';

    const wrapperBlock = this.wrapperTradingBlockedReason();
    if (wrapperBlock) return wrapperBlock;

    if (!this.isPositiveDecimal(this.quoteAmount())) {
      return 'Enter a token amount greater than zero.';
    }
    if (this.orderBalanceChecking()) {
      return 'Waiting for the wallet balance to settle.';
    }
    if (this.quoteLoading()) return 'Refreshing the order quote.';

    const balanceError = this.orderBalanceError();
    if (balanceError) return balanceError;
    const minValueError = this.orderMinValueError();
    if (minValueError) return minValueError;

    if (
      this.ladderOrdersAvailable() &&
      this.ladderCount() > 1 &&
      !this.ladderPlan()
    ) {
      return 'Check the ladder count, percentage, amount, and unit price.';
    }

    if (this.orderMode() === 'market') {
      return (
        this.marketQuoteBlockedReason() ??
        (!this.activeFillOrderId() && !this.activeSweepQuote()
          ? 'No matching order is available for this amount.'
          : undefined)
      );
    }

    if (!this.isPositiveInteger(this.orderUnitPriceSompi())) {
      return 'Enter a valid unit price greater than zero.';
    }
    if (this.limitCrossesVisibleBook()) {
      const limitBlock = this.limitQuoteBlockedReason();
      if (limitBlock) return limitBlock;
    }
    if (this.orderNotionalSompi() === undefined) {
      return 'Enter a valid token amount and unit price.';
    }
    return this.orderDraftReady()
      ? undefined
      : 'This order is not ready to build yet.';
  });

  ngOnInit(): void {
    this.quoteRefresh$
      .pipe(
        // Cancel any pending debounce when a newer keystroke / immediate
        // refresh arrives, then take a fresh snapshot of the ticket.
        switchMap((delayMs) => timer(delayMs)),
        map(() => this.quoteRequestSnapshot()),
        // Mirrors the inner guard below, which already re-fetches on a repeat
        // key when the quote is gone. See the helper for why the key alone is
        // not a sufficient skip condition (#238).
        distinctUntilChanged((prev, next) =>
          shouldSkipRepeatQuoteRequest(prev?.key, next?.key, !!this.quote()),
        ),
        switchMap((snapshot) => {
          if (!snapshot) {
            this.quote.set(undefined);
            this.quoteLoading.set(false);
            this.lastQuoteRequestKey = undefined;
            return EMPTY;
          }
          if (snapshot.key === this.lastQuoteRequestKey && this.quote()) {
            this.quoteLoading.set(false);
            return EMPTY;
          }
          this.lastQuoteRequestKey = snapshot.key;
          this.quoteLoading.set(true);
          return this.tradingApi
            .getQuote(snapshot.marketId, {
              side: snapshot.side,
              amount: snapshot.amount,
              orderId: snapshot.orderId,
              mode: snapshot.orderId ? undefined : snapshot.mode,
              limitUnitPriceSompi: snapshot.limitUnitPriceSompi,
            })
            .pipe(
              tap((quote) => {
                if (snapshot.marketId !== this.marketId()) {
                  return;
                }
                this.quote.set(quote);
                if (quote.unitPriceSompi && this.orderMode() === 'market') {
                  this.setOrderUnitPriceSompi(quote.unitPriceSompi);
                }
              }),
              catchError(() => {
                if (snapshot.marketId !== this.marketId()) {
                  return EMPTY;
                }
                this.quote.set({
                  tokenIdHex: snapshot.marketId,
                  side: snapshot.side,
                  requestedAmount: snapshot.amount,
                  valid: false,
                  feeTicketEligible: false,
                  errors: ['Quote unavailable'],
                });
                return EMPTY;
              }),
              finalize(() => {
                if (snapshot.marketId === this.marketId()) {
                  this.quoteLoading.set(false);
                }
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.covenantId.set(
          (params.get('covenantId') || environment.proofTokenId).toLowerCase(),
        );
        void this.load();
      });
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((qp) => {
        const section = qp.get('section');
        if (section === 'mint' || section === 'wrap' || section === 'book') {
          this.actionTab.set(section);
          setTimeout(() => {
            document
              .getElementById('token-orderbook')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      });
    this.walletManager.onWalletChangedNotifier
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.metadataPermission.set(undefined);
        this.ownerTokenBalance.set(undefined);
        this.ownerPosition.set(undefined);
        this.ownerHoldingsWarnings.set([]);
        this.feeTicketAvailable.set(undefined);
        this.resetOpenOrders();
        this.syncWatchlistState();
        void this.loadMetadataPermission();
        void this.loadOwnerHoldings();
        void this.loadFeeTicketBalance();
        if (this.lowerTab() === 'orders') {
          void this.loadOpenOrders();
        }
      });

    // Silently keep the order book fresh while it's the visible tab.
    interval(ORDERBOOK_POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.marketId() && this.actionTab() === 'book') {
          void this.loadOrderbook({ refresh: true, silent: true });
        }
      });
  }

  /**
   * Full page (re)load used on first render and route/token changes. The route
   * covenant id is enough to start metadata, identity, supply, canonical
   * activity, and owner holdings immediately. Trading sections only wait for
   * the split trading-summary endpoint, which returns the one wrapper + market
   * row this page needs without calling the full /trading/markets list.
   */
  async load(): Promise<void> {
    const requestId = ++this.loadSequence;
    const covenantId = this.covenantId();
    this.syncWatchlistState();
    this.tokenInfoLoading.set(true);
    this.wrapperInfoLoaded.set(false);
    this.orderbookLoading.set(true);
    this.activityLoading.set(true);
    this.chartLoading.set(true);
    this.holdersLoadGeneration += 1;
    this.holdersHydrated = false;
    this.holders.set([]);
    this.holdersTotal.set(0);
    this.holdersHasNextPage.set(false);
    this.holdersLoading.set(false);
    this.holdersLoadingMore.set(false);
    this.error.set(undefined);
    this.warning.set(undefined);
    this.tokenDetail.set(undefined);
    this.tokenWrappers.set([]);
    this.tokenActions.set(undefined);
    this.metadata.set(undefined);
    this.metadataPermission.set(undefined);
    this.orderbook.set(undefined);
    this.stats.set(undefined);
    this.candles.set([]);
    this.activityLoadGeneration += 1;
    this.activity.set([]);
    this.activityHasNextPage.set(false);
    this.activityLoadingMore.set(false);
    this.tokenActivityLoadGeneration += 1;
    this.tokenActivityHydrated = false;
    this.tokenActivity.set([]);
    this.tokenActivityHasNextPage.set(false);
    this.tokenActivityLoadingMore.set(false);
    this.tokenActivityLoading.set(false);
    this.ownerTokenBalance.set(undefined);
    this.ownerPosition.set(undefined);
    this.ownerHoldingsWarnings.set([]);
    this.resetOpenOrders();
    this.feeTicketAvailable.set(undefined);
    this.quote.set(undefined);
    this.tradeMessage.set(undefined);
    this.tradeError.set(undefined);

    // Instant hero from session cache while live identity/metadata load.
    this.hydrateFromStaticProfile(covenantId);

    this.loadAuxiliaryCore(requestId, covenantId);
    void this.loadTokenIdentityForRequest(requestId, covenantId);
    // Supply and holder counts are the most time-sensitive numbers on this
    // page. Load them from their dedicated split endpoints and bypass cache on
    // full page loads so a stale summary cannot sit on screen indefinitely.
    void this.loadTokenSummariesForRequest(requestId, covenantId, {
      refresh: true,
    });
    void this.loadOwnerHoldings(covenantId);
    void this.loadFeeTicketBalance();
    void this.loadTokenActionsForRequest(requestId, covenantId, {
      refresh: true,
    });

    const marketId = await this.loadTradingSummary(requestId, covenantId);

    if (requestId !== this.loadSequence) return;

    // Trading-summary owns the wrapper/market row. Mark settled only after that
    // call finishes — identity alone used to flip this early and flash
    // "Wrapped trading mode is not available…" until summary arrived.
    this.wrapperInfoLoaded.set(true);

    await Promise.all([
      this.loadOrderbook(),
      this.loadStats(),
      this.loadCandles(),
      this.loadActivity(),
      this.loadOwnerHoldings(covenantId, marketId),
    ]);
    // Lazy tabs: only hydrate if the user is already on them (e.g. deep link).
    if (this.lowerTab() === 'tokenActivity') {
      void this.loadTokenOpsActivity();
    }
    if (this.lowerTab() === 'holders') {
      void this.loadHolders();
    }
    // Only quote when the ticket already has a valid amount (e.g. restored
    // selection). An empty default amount must not hit /quote on page load.
    this.requestQuoteRefresh(true);
  }

  private async loadTokenIdentityForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean } = {},
  ): Promise<string | undefined> {
    const identity = await this.optional(() =>
      firstValueFrom(this.tokenApi.getTokenIdentity(covenantId, options)),
    );

    if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
      return undefined;
    }

    if (!identity) {
      const fallbackMarketId = await this.loadTokenFallbackForRequest(
        requestId,
        covenantId,
        options,
        { includeSupplyFields: false, includeHolderFields: false },
      );
      if (!fallbackMarketId && !this.tokenDetail()) {
        this.warning.set('Token detail is temporarily unavailable.');
      }
      return fallbackMarketId;
    }

    // Identity is intentionally lightweight. If a legacy backend still returns
    // summary fields here, discard them so split summary responses stay the
    // only source of truth for supply/holders/market-cap values.
    this.patchTokenDetail(this.stripSummaryOwnedFields(identity));
    this.metadata.set(this.metadata() ?? identity.metadata);
    this.seedStaticProfileFromDetail(identity);
    return identity.wrapper?.marketId;
  }

  private stripSummaryOwnedFields(detail: Kcc20TokenDetail): Kcc20TokenDetail {
    const {
      supply: _supply,
      holderCount: _holderCount,
      topHolders: _topHolders,
      marketCapFloorAskSompi: _marketCapFloorAskSompi,
      ...rest
    } = detail;
    return rest;
  }

  private stripSupplyOwnedFields(detail: Kcc20TokenDetail): Kcc20TokenDetail {
    const {
      supply: _supply,
      marketCapFloorAskSompi: _marketCapFloorAskSompi,
      ...rest
    } = detail;
    return rest;
  }

  private stripHolderOwnedFields(detail: Kcc20TokenDetail): Kcc20TokenDetail {
    const {
      holderCount: _holderCount,
      topHolders: _topHolders,
      ...rest
    } = detail;
    return rest;
  }

  private async loadTradingSummary(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean } = {},
    healId?: number,
  ): Promise<string | undefined> {
    const summary = await this.optional(() =>
      firstValueFrom(this.tokenApi.getTokenTradingSummary(covenantId, options)),
    );

    if (
      requestId !== this.loadSequence ||
      covenantId !== this.covenantId() ||
      (healId !== undefined && healId !== this.tradingSummaryHealSequence)
    ) {
      return undefined;
    }

    if (!summary) {
      return this.loadTokenFallbackForRequest(
        requestId,
        covenantId,
        options,
        {
          includeSupplyFields: false,
          includeHolderFields: false,
        },
        healId,
      );
    }
    this.applyTradingSummary(summary);
    return summary.wrapper?.marketId ?? summary.market?.tokenIdHex;
  }

  /**
   * The backend's own "can this action build right now" check for this
   * token's wrapper (mint/wrap/unwrap/trade/deployWrapper), each with an
   * `enabled` + human `reason`. This is what actually gates the Wrap/Unwrap
   * buttons — see `wrapActionReady`/`unwrapActionReady` — so a build can't be
   * submitted while the backend still considers the wrapped market not ready.
   */
  private async loadTokenActionsForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean } = {},
  ): Promise<void> {
    const response = await this.optional(() =>
      firstValueFrom(this.tokenApi.getTokenPageActions(covenantId, options)),
    );
    if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
      return;
    }
    if (response) {
      this.applyTokenPageActions(response);
      return;
    }
    this.tokenActions.set(undefined);
  }

  private async loadSupplySummaryForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean; settleLoading?: boolean } = {},
  ): Promise<void> {
    const summary = await this.optional(() =>
      firstValueFrom(this.tokenApi.getTokenSupplySummary(covenantId, options)),
    );

    try {
      if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
        return;
      }
      if (!summary) {
        await this.loadTokenFallbackForRequest(requestId, covenantId, options, {
          includeHolderFields: false,
        });
        return;
      }
      this.applySupplySummary(summary);
    } finally {
      if (options.settleLoading !== false && requestId === this.loadSequence) {
        this.tokenInfoLoading.set(false);
      }
    }
  }

  private async loadHoldersSummaryForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean; settleLoading?: boolean } = {},
  ): Promise<void> {
    const summary = await this.optional(() =>
      firstValueFrom(this.tokenApi.getTokenHoldersSummary(covenantId, options)),
    );

    try {
      if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
        return;
      }
      if (!summary) {
        await this.loadTokenFallbackForRequest(requestId, covenantId, options, {
          includeSupplyFields: false,
        });
        return;
      }
      this.applyHoldersSummary(summary);
    } finally {
      if (options.settleLoading !== false && requestId === this.loadSequence) {
        this.tokenInfoLoading.set(false);
      }
    }
  }

  private async loadTokenSummariesForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean } = {},
  ): Promise<void> {
    try {
      await Promise.all([
        this.loadSupplySummaryForRequest(requestId, covenantId, {
          ...options,
          settleLoading: false,
        }),
        this.loadHoldersSummaryForRequest(requestId, covenantId, {
          ...options,
          settleLoading: false,
        }),
      ]);
    } finally {
      if (requestId === this.loadSequence) {
        this.tokenInfoLoading.set(false);
      }
    }
  }

  /**
   * Starts non-blocking core reads. These update hero/metric enrichments when
   * they arrive, but they must not delay token detail or market-section loads.
   */
  private loadAuxiliaryCore(requestId: number, covenantId: string): void {
    void this.loadMetadata(requestId, covenantId);
    void this.loadMetadataPermissionForRequest(requestId, covenantId);
  }

  private async loadMetadata(
    requestId: number,
    covenantId: string,
  ): Promise<void> {
    const metadata = await this.optional(() =>
      firstValueFrom(this.metadataApi.getOptionalByCovenantId(covenantId)),
    );
    if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
      return;
    }
    this.metadata.set(metadata ?? this.tokenDetail()?.metadata);
    if (metadata) {
      this.tokenStaticProfiles.putFromMetadata(metadata);
    }
  }

  private async loadMetadataPermissionForRequest(
    requestId: number,
    covenantId: string,
  ): Promise<void> {
    const permission = await this.loadMetadataPermissionValue(covenantId);
    if (requestId !== this.loadSequence || covenantId !== this.covenantId()) {
      return;
    }
    this.metadataPermission.set(permission);
  }

  private applyTradingSummary(summary: Kcc20TokenTradingSummary): void {
    this.tokenWrappers.set(summary.wrapper ? [summary.wrapper] : []);
    this.markets.set(summary.market ? [summary.market] : []);
    this.patchTokenDetail({
      covenantId: summary.canonicalTokenId,
      wrapper: summary.wrapper,
      market: summary.market ?? undefined,
    });
    this.tokenStaticProfiles.putFromTokenDetail(
      {
        covenantId: summary.canonicalTokenId,
        wrapper: summary.wrapper ?? undefined,
        market: summary.market ?? undefined,
        metadata: summary.market?.metadata,
      },
      true,
      false,
    );
    if (summary.market?.metadata) {
      this.tokenStaticProfiles.putFromMetadata(summary.market.metadata);
    }
  }

  private applyTokenPageActions(response: Kcc20TokenPageActionsResponse): void {
    this.tokenActions.set(response.actions);
    if (response.wrappers.length || !response.wrapper) {
      this.tokenWrappers.set(response.wrappers);
    } else {
      this.tokenWrappers.set([response.wrapper]);
    }
    this.patchTokenDetail({
      covenantId: this.covenantId(),
      wrapper: response.wrapper ?? undefined,
    });
  }

  private tokenActionBlockedReason(
    action: keyof Kcc20TokenPageActions,
  ): string | undefined {
    const actions = this.tokenActions();
    if (!actions) {
      return TOKEN_ACTION_AVAILABILITY_PENDING_REASON;
    }
    const availability = actions[action];
    if (availability.enabled) {
      return undefined;
    }
    return availability.reason?.trim() || 'This token action is not available.';
  }

  private applySupplySummary(summary: Kcc20TokenSupplySummary): void {
    this.patchTokenDetail({
      covenantId: this.covenantId(),
      supply: summary.supply,
      marketCapFloorAskSompi: summary.marketCapFloorAskSompi,
    });
  }

  private applyHoldersSummary(summary: Kcc20TokenHoldersSummary): void {
    this.patchTokenDetail({
      covenantId: this.covenantId(),
      holderCount: summary.holderCount,
      topHolders: summary.topHolders,
    });
  }

  private async loadTokenFallbackForRequest(
    requestId: number,
    covenantId: string,
    options: { refresh?: boolean } = {},
    patchOptions: {
      includeSupplyFields?: boolean;
      includeHolderFields?: boolean;
    } = {},
    healId?: number,
  ): Promise<string | undefined> {
    const token = await this.optional(() =>
      firstValueFrom(this.tokenApi.getToken(covenantId, options)),
    );
    if (
      requestId !== this.loadSequence ||
      covenantId !== this.covenantId() ||
      (healId !== undefined && healId !== this.tradingSummaryHealSequence) ||
      !token
    ) {
      return undefined;
    }

    let tokenPatch = token;
    if (patchOptions.includeSupplyFields === false) {
      tokenPatch = this.stripSupplyOwnedFields(tokenPatch);
    }
    if (patchOptions.includeHolderFields === false) {
      tokenPatch = this.stripHolderOwnedFields(tokenPatch);
    }
    this.patchTokenDetail(tokenPatch);
    this.metadata.set(this.metadata() ?? token.metadata);
    this.seedStaticProfileFromDetail(token);
    // Same "never regress" rule as patchTokenDetail: only replace these with
    // the fallback's data when it actually has some — an already-succeeded
    // trading-summary call may have populated them correctly already.
    if (token.wrapper) this.tokenWrappers.set([token.wrapper]);
    if (token.market) this.markets.set([token.market]);
    return token.wrapper?.marketId ?? token.market?.tokenIdHex;
  }

  /** Warm hero labels/logo from session cache (never invents rich metadata). */
  private hydrateFromStaticProfile(covenantId: string): void {
    const profile = this.tokenStaticProfiles.peek(covenantId);
    if (!profile) {
      return;
    }
    if (
      profile.curatedTicker?.trim() ||
      profile.curatedName?.trim() ||
      profile.logoUrl?.trim()
    ) {
      this.metadata.set({
        covenantId,
        ticker: profile.curatedTicker,
        name: profile.curatedName,
        logoUrl: profile.logoUrl,
        decimals: profile.decimals,
        source: 'cache',
        verificationStatus: 'unknown',
        submittedByOwner: '',
      });
    }
    if (profile.nativeTicker?.trim() || profile.nativeTokenName?.trim()) {
      this.patchTokenDetail({
        covenantId,
        ticker: profile.nativeTicker,
        tokenName: profile.nativeTokenName,
      });
    }
  }

  private seedStaticProfileFromDetail(detail: Kcc20TokenDetail): void {
    this.tokenStaticProfiles.putFromTokenDetail(detail, true);
    if (detail.metadata) {
      this.tokenStaticProfiles.putFromMetadata(detail.metadata);
    }
  }

  /**
   * Merges a patch into `tokenDetail`, field by field, never letting a
   * `null`/`undefined` value regress a field that's already populated.
   *
   * The identity/trading-summary/supply-summary/holders-summary split calls
   * run in parallel and each independently falls back to the legacy `getToken()`
   * endpoint on failure (see `loadTokenFallbackForRequest`). That legacy
   * response may not compute fields (holderCount, topHolders,
   * marketCapFloorAskSompi, orderbookTradeableSupply, ...) that a *different*,
   * already-succeeded split call already delivered — a naive `{...current,
   * ...patch}` spread would let a transient failure on one endpoint silently
   * blank out good data another endpoint just loaded. Only an explicit *newer,
   * non-empty* value should ever replace an existing one.
   */
  private patchTokenDetail(patch: Partial<Kcc20TokenDetail>): void {
    const current = this.tokenDetail();
    const covenantId =
      patch.covenantId ?? current?.covenantId ?? this.covenantId();

    const merged = { ...(current ?? { covenantId }) } as Record<
      string,
      unknown
    >;
    (Object.keys(patch) as (keyof Kcc20TokenDetail)[]).forEach((key) => {
      if (key === 'supply') return;
      const value = patch[key];
      if (value !== null && value !== undefined) {
        merged[key] = value;
      }
    });
    merged['covenantId'] = covenantId;
    merged['metadata'] = patch.metadata ?? current?.metadata;

    if (patch.supply) {
      const nextSupply = { ...(current?.supply ?? {}) } as Record<
        string,
        unknown
      >;
      (Object.keys(patch.supply) as (keyof Kcc20TokenSupply)[]).forEach(
        (key) => {
          const value = patch.supply![key];
          if (value !== null && value !== undefined) {
            nextSupply[key] = value;
          }
        },
      );
      merged['supply'] = nextSupply;
    }

    this.tokenDetail.set(merged as unknown as Kcc20TokenDetail);
  }

  /** Loads the order book section (and the "Open orders" metric). */
  private async loadOrderbook(
    options: { refresh?: boolean; silent?: boolean } = {},
  ): Promise<void> {
    const marketId = this.marketId();
    if (!marketId) {
      this.orderbook.set(undefined);
      this.orderbookLoading.set(false);
      return;
    }

    // Keep the current book (or empty state) painted during background polls —
    // flipping loading clears empty/depth rows for a frame every interval.
    const silent = options.silent === true || !!this.orderbook();
    if (!silent) {
      this.orderbookLoading.set(true);
    }
    try {
      const orderbook = await firstValueFrom(
        this.tradingApi.getOrderbook(marketId, {
          depthLimit: 100,
          limit: 20,
          refresh: options.refresh,
        }),
      );
      if (marketId !== this.marketId()) return;
      this.orderbook.set(orderbook);
    } catch {
      // Keep any previously loaded book visible on a refresh failure.
    } finally {
      if (marketId === this.marketId()) {
        this.orderbookLoading.set(false);
      }
    }
  }

  /** Loads the trading stats that feed price-change and volume fallbacks. */
  private async loadStats(options: { refresh?: boolean } = {}): Promise<void> {
    const marketId = this.marketId();
    if (!marketId) {
      this.stats.set(undefined);
      return;
    }

    const stats = await this.optional(() =>
      firstValueFrom(
        this.tradingApi.getStats(marketId, { refresh: options.refresh }),
      ),
    );
    if (marketId !== this.marketId()) return;
    this.stats.set(stats);
  }

  /**
   * Loads Trading Activity (`scope=trading`).
   * - Default / `refresh`: replace the feed from offset 0.
   * - `append`: next page (infinite scroll / Load more).
   * - `silent`: keep the current feed or empty state painted (no skeleton).
   */
  private async loadActivity(
    options: { refresh?: boolean; append?: boolean; silent?: boolean } = {},
  ): Promise<void> {
    const append = !!options.append && !options.refresh;
    if (append) {
      if (
        this.activityLoading() ||
        this.activityLoadingMore() ||
        !this.activityHasNextPage()
      ) {
        return;
      }
      this.activityLoadingMore.set(true);
    } else {
      // Background refreshes must not flip loading — that swaps the empty
      // state for a skeleton table for every retry.
      const silent = options.silent === true;
      if (!silent) {
        this.activityLoading.set(true);
      }
      this.activityLoadingMore.set(false);
    }

    const generation = ++this.activityLoadGeneration;
    const covenantId = this.covenantId();
    const marketId = this.marketId();
    const offset = append ? this.activity().length : 0;

    try {
      const activity = await this.loadScopedActivity(
        covenantId,
        marketId,
        activityScopeForTab('tradingActivity'),
        {
          limit: this.activityPageSize,
          offset,
          refresh: options.refresh,
        },
      );
      if (
        generation !== this.activityLoadGeneration ||
        covenantId !== this.covenantId() ||
        marketId !== this.marketId()
      ) {
        return;
      }
      // `undefined` means every underlying request failed: keep the stale feed
      // on screen instead of blanking it (stale-while-revalidate).
      if (activity) {
        const page = activity.slice(0, this.activityPageSize);
        this.activityHasNextPage.set(activity.length > this.activityPageSize);
        this.activity.set(append ? [...this.activity(), ...page] : page);
      }
    } finally {
      if (generation === this.activityLoadGeneration) {
        this.activityLoading.set(false);
        this.activityLoadingMore.set(false);
      }
    }
  }

  /**
   * Loads Token Activity (`scope=token`) — mint/transfer/wrap/burn/deploy, etc.
   * Lazy on first tab visit; same append/infinite-scroll contract as trading.
   */
  private async loadTokenOpsActivity(
    options: { refresh?: boolean; append?: boolean; silent?: boolean } = {},
  ): Promise<void> {
    const append = !!options.append && !options.refresh;
    if (append) {
      if (
        this.tokenActivityLoading() ||
        this.tokenActivityLoadingMore() ||
        !this.tokenActivityHasNextPage()
      ) {
        return;
      }
      this.tokenActivityLoadingMore.set(true);
    } else {
      const silent = options.silent === true;
      if (!silent) {
        this.tokenActivityLoading.set(true);
      }
      this.tokenActivityLoadingMore.set(false);
    }

    const generation = ++this.tokenActivityLoadGeneration;
    const covenantId = this.covenantId();
    const marketId = this.marketId();
    const offset = append ? this.tokenActivity().length : 0;

    try {
      const activity = await this.loadScopedActivity(
        covenantId,
        marketId,
        activityScopeForTab('tokenActivity'),
        {
          limit: this.activityPageSize,
          offset,
          refresh: options.refresh,
        },
      );
      if (
        generation !== this.tokenActivityLoadGeneration ||
        covenantId !== this.covenantId()
      ) {
        return;
      }
      this.tokenActivityHydrated = true;
      if (activity) {
        const page = activity.slice(0, this.activityPageSize);
        this.tokenActivityHasNextPage.set(
          activity.length > this.activityPageSize,
        );
        this.tokenActivity.set(
          append ? [...this.tokenActivity(), ...page] : page,
        );
      }
    } finally {
      if (generation === this.tokenActivityLoadGeneration) {
        this.tokenActivityLoading.set(false);
        this.tokenActivityLoadingMore.set(false);
      }
    }
  }

  private maybeLoadMoreActivity(): void {
    if (
      this.activityLoading() ||
      this.activityLoadingMore() ||
      !this.activityHasNextPage()
    ) {
      return;
    }
    void this.loadActivity({ append: true });
  }

  private maybeLoadMoreTokenOpsActivity(): void {
    if (
      this.tokenActivityLoading() ||
      this.tokenActivityLoadingMore() ||
      !this.tokenActivityHasNextPage()
    ) {
      return;
    }
    void this.loadTokenOpsActivity({ append: true });
  }

  /** Explicit "Load more" under Trading Activity. */
  loadMoreActivity(): void {
    this.maybeLoadMoreActivity();
  }

  /** Explicit "Load more" under Token Activity. */
  loadMoreTokenOpsActivity(): void {
    this.maybeLoadMoreTokenOpsActivity();
  }

  setQuoteSide(side: QuoteSide): void {
    this.quoteSide.set(side);
    this.quote.set(undefined);
    this.selectedOrderId.set(undefined);
    this.selectedOrderSide.set(undefined);
    this.syncWrapPrompt();
    if (this.orderMode() === 'market') {
      this.requestQuoteRefresh(true);
    }
  }

  setLadderCount(value: string | number): void {
    const parsed = Math.floor(Number(value));
    if (!Number.isFinite(parsed)) {
      return;
    }
    this.ladderCount.set(Math.min(LADDER_MAX_ORDERS, Math.max(0, parsed)));
  }

  setLadderExpanded(expanded: boolean): void {
    this.ladderExpanded.set(expanded);
    this.persistLadderPreferences();
  }

  onLadderSpoilerToggle(event: Event): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLDetailsElement)) {
      return;
    }
    this.setLadderExpanded(target.open);
  }

  setLadderDirection(direction: LadderDirection): void {
    this.ladderDirection.set(direction);
    this.persistLadderPreferences();
  }

  setLadderPercent(value: string | number): void {
    this.ladderPercent.set(String(value).trim());
    this.persistLadderPreferences();
  }

  ladderPercentPresetSelected(preset: number): boolean {
    return parseLadderPercent(this.ladderPercent()) === preset;
  }

  private persistLadderPreferences(): void {
    writeTokenTicketLadderPreferences({
      expanded: this.ladderExpanded(),
      direction: this.ladderDirection(),
      percent: this.ladderPercent(),
    });
  }

  /**
   * When the user asked for more than one resting limit, enqueue every
   * stepped price into the wallet-operations scheduler immediately — the
   * same as clicking Buy/Sell N times, without waiting between clicks.
   */
  private tryEnqueueLadderLimitOrders(
    wrapperId: string,
    covenantId: string,
  ): boolean {
    if (!this.ladderOrdersAvailable() || this.ladderCount() <= 1) {
      return false;
    }
    const plan = this.ladderPlan();
    if (!plan || plan.unitPriceSompi.length < 2) {
      return true;
    }
    const side = this.quoteSide();
    const tokenAmount = this.quoteAmount().trim();
    const enqueue = (unitPriceSompi: string[]): void => {
      this.enqueueLadderRestingLimits({
        covenantId,
        wrapperId,
        side,
        tokenAmount,
        unitPriceSompi,
      });
    };
    if (!this.dialog) {
      enqueue(plan.unitPriceSompi);
      return true;
    }
    this.dialog.open<
      void,
      LadderLimitReviewDialogData,
      LadderLimitReviewDialogComponent
    >(LadderLimitReviewDialogComponent, {
      width: 'min(520px, 94vw)',
      data: {
        side,
        ticker: this.tradeTicker(),
        tokenAmount,
        direction: this.ladderDirection(),
        percent: this.ladderPercent(),
        rows: plan.unitPriceSompi.map((unitPriceSompi, index) => ({
          unitPriceSompi,
          notionalSompi: plan.notionalsSompi[index].toString(),
        })),
        totalNotionalSompi: plan.totalNotionalSompi.toString(),
        settlementSompi:
          this.settlementSompi() ?? plan.totalNotionalSompi.toString(),
        confirmLabel: this.activeExecutionActionLabel(),
        kasDisplayMaxDecimals: this.kasDisplayMaxDecimals,
        onConfirm: enqueue,
      },
    });
    return true;
  }

  /**
   * Ladder sells cannot call {@link enqueueRestingLimitOrder} per step:
   * consolidation treats same-token+same-title clicks within 300ms as a
   * fumbled double-click and drops them, so only the first sell reached
   * the scheduler. Prepare the orderbook balance once for the full size,
   * then enqueue every resting limit like the buy path.
   */
  private enqueueLadderRestingLimits(input: {
    covenantId: string;
    wrapperId: string;
    side: QuoteSide;
    tokenAmount: string;
    unitPriceSompi: string[];
  }): void {
    const enqueueAll = (): void => {
      for (const price of input.unitPriceSompi) {
        this.enqueueRestingLimitAction({
          covenantId: input.covenantId,
          wrapperId: input.wrapperId,
          side: input.side,
          tokenAmount: input.tokenAmount,
          unitPriceSompi: price,
        });
      }
    };
    if (input.side !== 'sell') {
      enqueueAll();
      this.clearLadderTicketFields();
      return;
    }
    const totalAmount = this.scaledTicketTokenAmount(
      input.tokenAmount,
      input.unitPriceSompi.length,
    );
    if (!totalAmount) {
      this.tradeError.set('A valid token amount is required first.');
      return;
    }
    this.clearLadderTicketFields();
    let cancelled = false;
    void this.tokenConsolidationFlow
      .execute({
        tokenId: input.covenantId,
        title: WALLET_OPERATION_LABELS.prepareBalance,
        asset: 'orderbook',
        tokenAmount: totalAmount,
        tokenLabel: this.displayName(),
        wrappedMarketId: input.wrapperId,
        consolidateOnly: true,
        onCancelled: () => {
          cancelled = true;
        },
      })
      .then(() => {
        if (cancelled) {
          return;
        }
        enqueueAll();
      })
      .catch((error: unknown) =>
        this.tradeError.set(
          error instanceof Error ? error.message : 'Order request failed.',
        ),
      );
  }

  private scaledTicketTokenAmount(
    tokenAmount: string,
    count: number,
  ): string | undefined {
    const base = parseKcc20DisplayAmountToBaseUnits(
      tokenAmount,
      this.tokenDecimals(),
    );
    if (base === null || base <= 0n || count <= 0) {
      return undefined;
    }
    return formatKcc20BaseUnitsForDisplay(
      base * BigInt(count),
      this.tokenDecimals(),
    );
  }

  /**
   * Duplicate ladders already queued N orders; leave the ticket empty so the
   * previous amount / price / count / totals don't look like another draft.
   * Single limit orders still keep typed fields (#238).
   */
  private clearLadderTicketFields(): void {
    this.quoteAmount.set('');
    this.setOrderUnitPriceSompi('');
    this.ladderCount.set(0);
    this.selectedOrderId.set(undefined);
    this.selectedOrderSide.set(undefined);
    this.quote.set(undefined);
    this.lastQuoteRequestKey = undefined;
    this.quoteLoading.set(false);
    this.tradeMessage.set(undefined);
    this.tradeError.set(undefined);
  }

  private enqueueRestingLimitOrder(input: {
    covenantId: string;
    wrapperId: string;
    side: QuoteSide;
    tokenAmount: string;
    unitPriceSompi: string;
  }): void {
    const actionRequest = this.restingLimitActionRequest(input);
    if (input.side === 'sell') {
      void this.tokenConsolidationFlow
        .execute({
          ...actionRequest,
          asset: 'orderbook',
          tokenAmount: input.tokenAmount,
          tokenLabel: this.displayName(),
          wrappedMarketId: input.wrapperId,
          onQueued: (actionId: string) =>
            this.trackOrderTicketAction(actionId, input.covenantId),
        })
        .catch((error: unknown) =>
          this.tradeError.set(
            error instanceof Error ? error.message : 'Order request failed.',
          ),
        );
      return;
    }
    this.enqueueRestingLimitAction(input);
  }

  private enqueueRestingLimitAction(input: {
    covenantId: string;
    wrapperId: string;
    side: QuoteSide;
    tokenAmount: string;
    unitPriceSompi: string;
  }): void {
    const actionId = this.walletOperationQueue.enqueueAction(
      this.restingLimitActionRequest(input),
    );
    this.trackOrderTicketAction(actionId, input.covenantId);
  }

  private restingLimitActionRequest(input: {
    covenantId: string;
    wrapperId: string;
    side: QuoteSide;
    tokenAmount: string;
    unitPriceSompi: string;
  }) {
    return {
      tokenId: input.covenantId,
      title: limitOrderLabel(input.side),
      build: () =>
        this.tokenActionApi.buildOrder(input.covenantId, {
          wrappedMarketId: input.wrapperId,
          side: input.side,
          tokenAmount: input.tokenAmount,
          unitPriceSompi: input.unitPriceSompi,
        }),
      onSubmitted: (task: Kcc20WalletOperationTask) =>
        this.handleOrderTicketSubmitted(task),
    };
  }

  /** True for the buy/sell ticket's own build calls (`buildOrder`'s
   *  create/fill order, whatever the backend's exact per-side operation
   *  name is — see `orderOperationKind`), as opposed to mint/wrap/unwrap/
   *  deploy/cancel, which have their own separate flows and shouldn't wipe
   *  the trade ticket.
   */
  private isOrderTicketOperation(
    operation: Kcc20UnsignedTokenActionPayload,
  ): boolean {
    return this.isOrderTicketKind(
      this.orderOperationKind(operation.payload.operation),
    );
  }

  private isOrderTicketKind(
    kind: 'create' | 'fill' | 'cancel' | 'sweep' | undefined,
  ): boolean {
    return kind === 'create' || kind === 'fill';
  }

  /**
   * Readies the ticket for the next order once one is submitted or finishes
   * indexing.
   *
   * Deliberately KEEPS what the user typed — the amount, and in limit mode the
   * unit price — so placing another order at the same price does not mean
   * retyping the whole ticket (#238). Side and limit/market mode were already
   * preserved so the ticket does not jump tabs; this extends that to the
   * inputs. The fee-ticket toggle is left alone too: a deliberate opt-out
   * should not silently flip back on for the next order.
   *
   * Only genuinely consumed or stale state is cleared: the resting order that
   * was just filled, the now-stale quote, and the previous order's messages.
   *
   * Submit itself does not auto-requote: a live quote total compared against
   * a post-spend wallet caused a false "insufficient balance" toast before
   * change UTXOs settled. Market mode also drops the matched price — that
   * figure is a COPY of the quote, not user input. Limit keeps the typed
   * unit price; once the order indexes, `refreshAfterOperation` requotes
   * with the settle window already open so a marketable limit can Fill now.
   */
  private resetOrderTicketFields(): void {
    this.selectedOrderId.set(undefined);
    this.selectedOrderSide.set(undefined);
    this.quote.set(undefined);
    this.lastQuoteRequestKey = undefined;
    this.quoteLoading.set(false);
    this.tradeMessage.set(undefined);
    this.tradeError.set(undefined);
    if (this.orderMode() === 'market') {
      this.orderUnitPriceKas.set('');
      this.orderUnitPriceSompi.set('');
    }
  }

  private handleOrderTicketSubmitted(task: Kcc20WalletOperationTask): void {
    if (
      !this.walletOperationTaskBelongsToToken(
        task,
        this.covenantId().toLowerCase(),
      )
    ) {
      return;
    }
    if (this.resetOrderTicketOnSubmitTaskIds.has(task.id)) {
      return;
    }
    this.resetOrderTicketOnSubmitTaskIds.add(task.id);
    // Open the settle window at the exact tracker handoff, before balance
    // signals can re-evaluate the retained buy amount against a transiently
    // low post-spend wallet balance.
    this.walletManager.beginBalanceSettleWindow();
    this.resetOrderTicketFields();
  }

  private trackOrderTicketAction(actionId: string, tokenId: string): void {
    const normalizedTokenId = tokenId.toLowerCase();
    this.orderTicketActionTokenIds.update((tracked) => {
      const next = new Map(tracked);
      next.set(actionId, normalizedTokenId);
      return next;
    });
  }

  setOrderMode(mode: OrderMode): void {
    this.orderMode.set(mode);
    this.selectedOrderId.set(undefined);
    this.selectedOrderSide.set(undefined);
    this.quote.set(undefined);
    this.tradeError.set(undefined);
    if (mode === 'market') {
      this.setOrderUnitPriceSompi('');
      this.requestQuoteRefresh(true);
    }
  }

  onQuoteAmountChange(value: string): void {
    this.quoteAmount.set(this.clampTradeAmountInput(value));
    // Drop stale matched-order details immediately. Otherwise the ticket keeps
    // the previous quote's price/totals and briefly recomputes them against the
    // newly typed amount (new amount × old quote price) until the next /quote
    // response arrives.
    this.clearStaleQuoteForCurrentAmount();
    // Always go through the pipeline so a cleared/invalid amount cancels any
    // pending debounce and in-flight /quote instead of leaving a stale request.
    this.requestQuoteRefresh();
  }

  onQuoteAmountBlur(): void {
    const clamped = this.clampTradeAmountDecimals(this.quoteAmount());
    if (clamped !== this.quoteAmount()) {
      this.quoteAmount.set(clamped);
    }
    this.clearStaleQuoteForCurrentAmount();
    this.requestQuoteRefresh(true);
  }

  setActionTab(tab: TokenActionTab): void {
    this.actionTab.set(tab);
  }

  setLowerTab(tab: TokenLowerTab): void {
    this.lowerTab.set(tab);
    if (tab === 'tokenActivity' && !this.tokenActivityHydrated) {
      void this.loadTokenOpsActivity();
    }
    if (tab === 'holders' && !this.holdersHydrated) {
      void this.loadHolders();
    }
  }

  openOrderColumns(
    sideTemplate: TokenOpenOrderCellTemplate,
    amountTemplate: TokenOpenOrderCellTemplate,
    priceTemplate: TokenOpenOrderCellTemplate,
    totalTemplate: TokenOpenOrderCellTemplate,
    timeTemplate: TokenOpenOrderCellTemplate,
    orderIdTemplate: TokenOpenOrderCellTemplate,
    cancelTemplate: TokenOpenOrderCellTemplate,
  ): TableColumn<Kcc20Order>[] {
    return [
      {
        key: 'side',
        label: 'Type',
        width: '11%',
        sortable: true,
        template: sideTemplate,
      },
      {
        key: 'tokenAmount',
        label: 'Amount',
        width: '15%',
        template: amountTemplate,
      },
      {
        key: 'unitPriceSompi',
        label: 'Price (KAS)',
        width: '15%',
        template: priceTemplate,
      },
      {
        key: 'total',
        label: 'Total (KAS)',
        width: '14%',
        template: totalTemplate,
      },
      {
        key: 'createdAtMs',
        label: 'Created',
        width: '16%',
        sortable: true,
        defaultSortDirection: 'desc',
        template: timeTemplate,
      },
      {
        key: 'orderId',
        label: 'Order',
        width: '16%',
        sortable: true,
        template: orderIdTemplate,
      },
      {
        key: 'action',
        label: '',
        width: '13%',
        template: cancelTemplate,
      },
    ];
  }

  onOpenOrdersSortChange(event: {
    column: string;
    direction: 'asc' | 'desc';
  }): void {
    const sortBy = TOKEN_OPEN_ORDER_COLUMN_SORT_BY[event.column];
    if (!sortBy) {
      return;
    }
    this.openOrdersSortBy.set(sortBy);
    this.openOrdersSortDirection.set(event.direction);
    void this.loadOpenOrders();
  }

  loadMoreOpenOrders(): void {
    this.maybeLoadMoreOpenOrders();
  }

  trackOpenOrder(_: number, order: Kcc20Order): string {
    return order.orderId;
  }

  displayOpenOrderSide(order: Kcc20Order): string {
    return kcc20SideLabel(order.side || order.marketSide);
  }

  /** Activity-style buy/sell direction for open-order row tinting. */
  openOrderDirection(order: Kcc20Order): 'buy' | 'sell' | undefined {
    const label = kcc20SideLabel(order.side || order.marketSide);
    if (label === 'Buy') return 'buy';
    if (label === 'Sell') return 'sell';
    return undefined;
  }

  openOrderAmount(order: Kcc20Order): string {
    return order.remainingTokenAmount || order.tokenAmount || '0';
  }

  openOrderUnitPriceSompi(order: Kcc20Order): string | null | undefined {
    return order.unitPriceSompi || order.priceSompi;
  }

  /**
   * Resting order notional in sompi: prefer locked bid value when present,
   * otherwise amount × unit price (same scale math as the trade ticket).
   */
  openOrderTotalSompi(order: Kcc20Order): string | undefined {
    const bidValue = order.bidValueSompi?.trim();
    if (bidValue && /^\d+$/.test(bidValue) && bidValue !== '0') {
      return bidValue;
    }
    const unitPriceSompi = this.parseSompi(this.openOrderUnitPriceSompi(order));
    let amountBaseUnits: bigint;
    try {
      amountBaseUnits = BigInt(this.openOrderAmount(order) || '0');
    } catch {
      return undefined;
    }
    if (
      unitPriceSompi === undefined ||
      amountBaseUnits <= 0n ||
      unitPriceSompi <= 0n
    ) {
      return undefined;
    }
    const scale = 10n ** BigInt(this.tokenDecimals());
    return ((unitPriceSompi * amountBaseUnits) / scale).toString();
  }

  /** Total (KAS) for open orders — 2 decimals, no buy/sell tint. */
  openOrderTotalLabel(order: Kcc20Order): string {
    return formatSompi(this.openOrderTotalSompi(order), 2).replace(/ KAS$/, '');
  }

  canCancelOpenOrder(order: Kcc20Order): boolean {
    return !!this.cancelOpenOrderContext(order);
  }

  buildCancelOpenOrder(order: Kcc20Order): void {
    const context = this.cancelOpenOrderContext(order);
    if (!context) {
      this.tradeError.set('Cannot resolve the wrapped market for this order.');
      return;
    }

    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    const orderId = order.orderId;
    const { completion } = this.walletOperationQueue.enqueueActionAndWait({
      tokenId: context.canonicalTokenId,
      title: WALLET_OPERATION_LABELS.cancelOrder,
      build: () =>
        this.tokenActionApi.buildCancel(context.canonicalTokenId, {
          wrappedMarketId: context.wrappedMarketId,
          side: context.side,
          targetOrderId: orderId,
        }),
    });
    void completion.then((result) => {
      if (result.status !== 'indexed') {
        return;
      }
      // Drop the cancelled row immediately, then self-heal against indexer lag
      // the same way create/fill wait for the book/activity to catch up.
      this.removeOpenOrderLocally(orderId);
      void this.refreshOpenOrdersWithSelfHeal(orderId);
      // Also refresh the market-level trading summary directly: the generic
      // wallet-operation-task effect covers this too, but only when the
      // completed task's operation name matches orderOperationKind() and its
      // tokenId matches this token, so don't rely on it alone for the top
      // "Open orders" stat.
      void this.refreshTradingSummaryWithSelfHeal();
    });
  }

  private cancelOpenOrderContext(order: Kcc20Order):
    | {
        canonicalTokenId: string;
        wrappedMarketId: string;
        side: 'ask' | 'bid';
      }
    | undefined {
    const side = this.openOrderMarketSide(order);
    const wrappedMarketId = (
      order.wrappedMarketIdHex ||
      this.wrappedMarketId() ||
      order.wrapperIdHex ||
      order.tokenIdHex
    )?.toLowerCase();
    const canonicalTokenId = (
      order.canonicalTokenIdHex || this.covenantId()
    )?.toLowerCase();
    if (!wrappedMarketId || !side || !canonicalTokenId) {
      return undefined;
    }
    return { canonicalTokenId, wrappedMarketId, side };
  }

  private openOrderMarketSide(order: Kcc20Order): 'ask' | 'bid' | undefined {
    const raw = String(order.side || order.marketSide || '').toLowerCase();
    if (raw === 'ask' || raw === 'sell') return 'ask';
    if (raw === 'bid' || raw === 'buy') return 'bid';
    return undefined;
  }

  private quoteIncludesOwnRestingOrder(quote?: Kcc20Quote | null): boolean {
    const ownOrderIds = this.ownRestingOrderIds();
    if (!quote || !ownOrderIds.size) {
      return false;
    }
    const orderIds = [
      quote.orderId,
      ...(quote.fills ?? []).map((fill) => fill.orderId),
    ];
    return orderIds.some((orderId) => {
      const normalized = this.normalizedOrderId(orderId);
      return !!normalized && ownOrderIds.has(normalized);
    });
  }

  private orderBelongsToOwner(order: Kcc20Order, owner: string): boolean {
    return (
      this.normalizedOwner(order.owner) === owner ||
      this.normalizedOwner(order.seller) === owner ||
      this.normalizedOwner(order.buyer) === owner
    );
  }

  private normalizedOwner(owner?: string | null): string | undefined {
    const normalized = owner?.trim().toLowerCase();
    return normalized && /^[a-f0-9]{64}$/.test(normalized)
      ? normalized
      : undefined;
  }

  private normalizedOrderId(orderId?: string | null): string | undefined {
    const normalized = orderId?.trim().toLowerCase();
    return normalized || undefined;
  }

  private isOpenOrder(order: Kcc20Order): boolean {
    return String(order.status || '').toLowerCase() === 'open';
  }

  private resetOpenOrders(): void {
    this.openOrdersLoadSequence += 1;
    this.openOrders.set([]);
    this.openOrdersTotal.set(0);
    this.openOrdersLoading.set(false);
    this.openOrdersLoadingMore.set(false);
    this.openOrdersHasNextPage.set(false);
    this.openOrdersError.set(undefined);
    this.suppressedOpenOrderIds.set(new Set());
  }

  private removeOpenOrderLocally(orderId: string): void {
    this.suppressedOpenOrderIds.update((ids) => {
      if (ids.has(orderId)) {
        return ids;
      }
      const next = new Set(ids);
      next.add(orderId);
      return next;
    });
    const before = this.openOrders();
    const next = before.filter((order) => order.orderId !== orderId);
    if (next.length !== before.length) {
      this.openOrders.set(next);
      this.openOrdersTotal.update((total) => Math.max(0, total - 1));
    }
    if (this.selectedOrderId() === orderId) {
      this.selectedOrderId.set(undefined);
      this.selectedOrderSide.set(undefined);
    }
  }

  private pruneSuppressedOpenOrders(): void {
    const suppressed = this.suppressedOpenOrderIds();
    if (!suppressed.size) {
      return;
    }
    const present = new Set(this.openOrders().map((order) => order.orderId));
    const next = new Set(
      [...suppressed].filter((orderId) => present.has(orderId)),
    );
    if (next.size !== suppressed.size) {
      this.suppressedOpenOrderIds.set(next);
    }
  }

  private openOrdersSignature(): string {
    return this.displayedOpenOrders()
      .map((order) => order.orderId)
      .sort()
      .join('|');
  }

  private async loadOpenOrders(options?: {
    silent?: boolean;
    append?: boolean;
  }): Promise<void> {
    const owner = this.owner();
    const covenantId = this.covenantId();
    if (!owner || !covenantId) {
      this.resetOpenOrders();
      return;
    }

    const append = !!options?.append && !options?.silent;
    if (append) {
      if (
        this.openOrdersLoading() ||
        this.openOrdersLoadingMore() ||
        !this.openOrdersHasNextPage()
      ) {
        return;
      }
      this.openOrdersLoadingMore.set(true);
    } else {
      const silent = !!options?.silent;
      if (!silent) {
        this.openOrdersLoading.set(true);
      }
      this.openOrdersLoadingMore.set(false);
      this.openOrdersError.set(undefined);
    }

    const loadId = append
      ? this.openOrdersLoadSequence
      : ++this.openOrdersLoadSequence;
    const offset = append ? this.openOrders().length : 0;
    const response = await this.optional(() =>
      firstValueFrom(
        this.tradingApi.getOwnerOrders(owner, {
          status: 'open',
          tokenId: covenantId,
          limit: this.openOrdersPageSize,
          offset,
          sortBy: this.openOrdersSortBy(),
          sortDirection: this.openOrdersSortDirection(),
        }),
      ),
    );
    if (
      loadId !== this.openOrdersLoadSequence ||
      owner !== this.owner() ||
      covenantId !== this.covenantId()
    ) {
      return;
    }

    const items = response?.items ?? [];
    if (response) {
      const merged = append ? [...this.openOrders(), ...items] : items;
      this.openOrders.set(merged);
      this.openOrdersTotal.set(response.total ?? merged.length);
      this.openOrdersHasNextPage.set(
        response.total != null
          ? merged.length < response.total
          : items.length >= this.openOrdersPageSize,
      );
      this.pruneSuppressedOpenOrders();
      this.openOrdersError.set(undefined);
    } else if (!append) {
      this.openOrdersError.set(
        'Open orders could not be loaded. Try again once the indexer responds.',
      );
    }

    this.openOrdersLoading.set(false);
    this.openOrdersLoadingMore.set(false);
  }

  private maybeLoadMoreOpenOrders(): void {
    if (
      this.openOrdersLoading() ||
      this.openOrdersLoadingMore() ||
      !this.openOrdersHasNextPage()
    ) {
      return;
    }
    void this.loadOpenOrders({ append: true });
  }

  /**
   * Silent open-orders refresh with the same indexer-lag self-heal used for
   * the order book / activity after create/fill/cancel. When
   * `expectAbsentOrderId` is set (cancel path), keep polling until that id is
   * gone even if another order changed the list signature first.
   */
  private async refreshOpenOrdersWithSelfHeal(
    expectAbsentOrderId?: string,
  ): Promise<void> {
    const baseline = this.openOrdersSignature();
    await this.loadOpenOrders({ silent: true });
    if (this.openOrdersStillNeedHeal(baseline, expectAbsentOrderId)) {
      this.pollOpenOrdersUntilChanged(baseline, expectAbsentOrderId);
    }
  }

  private openOrdersStillNeedHeal(
    baselineSignature: string,
    expectAbsentOrderId?: string,
  ): boolean {
    if (expectAbsentOrderId) {
      return this.openOrders().some(
        (order) => order.orderId === expectAbsentOrderId,
      );
    }
    return this.openOrdersSignature() === baselineSignature;
  }

  private pollOpenOrdersUntilChanged(
    baselineSignature: string,
    expectAbsentOrderId?: string,
    attempt = 0,
  ): void {
    const delaysMs = [1000, 2000, 3500, 5000];
    if (attempt >= delaysMs.length) return;
    const requestId = this.loadSequence;
    setTimeout(() => {
      void (async () => {
        if (requestId !== this.loadSequence) return;
        await this.loadOpenOrders({ silent: true });
        if (
          requestId === this.loadSequence &&
          this.openOrdersStillNeedHeal(baselineSignature, expectAbsentOrderId)
        ) {
          this.pollOpenOrdersUntilChanged(
            baselineSignature,
            expectAbsentOrderId,
            attempt + 1,
          );
        }
      })();
    }, delaysMs[attempt]);
  }

  /**
   * work here because the page scrolls inside the `.route-content` container
   * (not the document) and some content (e.g. the Holders list) is only rendered
   * when its tab is active. So we activate the owning tab, then scroll the section
   * into view after Angular has rendered it. The anchor's href is kept as a
   * semantic/no-JS fallback.
   */
  scrollToSection(
    section:
      | 'orderbook'
      | 'trade'
      | 'stats'
      | 'tradingActivity'
      | 'tokenActivity'
      | 'holders',
    event?: Event,
  ): void {
    // Suppress the native fragment jump; it scrolls the document, not the
    // `.route-content` scroll container, and can miss lazily-rendered targets.
    event?.preventDefault();

    if (section === 'orderbook') {
      this.setActionTab('book');
    } else if (section === 'tradingActivity') {
      this.setLowerTab('tradingActivity');
    } else if (section === 'tokenActivity') {
      this.setLowerTab('tokenActivity');
    } else if (section === 'holders') {
      this.setLowerTab('holders');
    }

    // Tab buttons carry stable ids (list bodies are lazily rendered).
    const targetId =
      section === 'tradingActivity'
        ? 'token-trading-activity'
        : section === 'tokenActivity'
          ? 'token-token-activity'
          : `token-${section}`;

    // Keep the address bar in sync for deep-linking / copy-link. We suppressed
    // the native fragment jump above, so update the hash via replaceState
    // (not `location.hash`, which would re-scroll the document) instead.
    if (typeof history !== 'undefined') {
      history.replaceState(history.state, '', `#${targetId}`);
    }

    setTimeout(() => {
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

      document.getElementById(targetId)?.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  /**
   * Holders feed (`GET /tokens/{id}/holders`).
   * - Default / refresh: replace from offset 0.
   * - append: next page (infinite scroll / Load more).
   */
  private async loadHolders(
    options: { refresh?: boolean; append?: boolean } = {},
  ): Promise<void> {
    const covenantId = this.covenantId()?.trim();
    if (!covenantId) {
      return;
    }
    const append = !!options.append && !options.refresh;
    if (append) {
      if (
        this.holdersLoading() ||
        this.holdersLoadingMore() ||
        !this.holdersHasNextPage()
      ) {
        return;
      }
      this.holdersLoadingMore.set(true);
    } else {
      this.holdersLoading.set(true);
      this.holdersLoadingMore.set(false);
    }

    const generation = ++this.holdersLoadGeneration;
    const limit = TokenPageComponent.HOLDERS_PAGE_SIZE;
    const offset = append ? this.holders().length : 0;
    try {
      const response = await this.optional(() =>
        firstValueFrom(
          this.tokenApi.getTokenHolders(covenantId, {
            limit,
            offset,
            sortBy: 'amount',
            sortDirection: 'desc',
            refresh: options.refresh,
          }),
        ),
      );
      if (
        generation !== this.holdersLoadGeneration ||
        covenantId !== this.covenantId()
      ) {
        return;
      }
      if (!response) {
        return;
      }
      this.holdersHydrated = true;
      const items = response.items ?? [];
      const next = append ? [...this.holders(), ...items] : items;
      this.holders.set(next);
      const envelopeTotal =
        typeof response.total === 'number' && Number.isFinite(response.total)
          ? response.total
          : undefined;
      const knownTotal =
        envelopeTotal ?? this.tokenDetail()?.holderCount ?? undefined;
      if (knownTotal !== undefined && knownTotal !== null) {
        this.holdersTotal.set(knownTotal);
      }
      // Prefer known total; otherwise a full page means "maybe more".
      this.holdersHasNextPage.set(
        knownTotal !== undefined && knownTotal !== null
          ? next.length < knownTotal
          : items.length >= limit,
      );
      if (
        typeof response.total === 'number' &&
        this.tokenDetail()?.holderCount == null
      ) {
        this.patchTokenDetail({
          covenantId,
          holderCount: response.total,
        });
      }
    } finally {
      if (generation === this.holdersLoadGeneration) {
        this.holdersLoading.set(false);
        this.holdersLoadingMore.set(false);
      }
    }
  }

  private maybeLoadMoreHolders(): void {
    if (
      this.holdersLoading() ||
      this.holdersLoadingMore() ||
      !this.holdersHasNextPage()
    ) {
      return;
    }
    void this.loadHolders({ append: true });
  }

  /** Explicit "Load more" under the Holders list. */
  loadMoreHolders(): void {
    this.maybeLoadMoreHolders();
  }

  setWrapMode(mode: WrapMode): void {
    this.wrapMode.set(mode);
  }

  /** Percent buttons: size immediately and quote without waiting. */
  setQuoteAmountPreset(percent: number): void {
    this.applyQuoteAmountPercent(percent, true);
  }

  /**
   * Range `input` while dragging. The amount field follows the thumb, but
   * /quote waits for the shared debounce (same as typing) so a drag does
   * not fire a request per tick.
   */
  onQuoteAmountSliderInput(percent: number): void {
    this.applyQuoteAmountPercent(percent, false);
  }

  /**
   * Range `change` on release (mouseup / touchend / key commit). Cancel the
   * pending debounce and quote the settled amount now.
   */
  onQuoteAmountSliderCommit(percent: number): void {
    this.applyQuoteAmountPercent(percent, true);
  }

  private applyQuoteAmountPercent(
    percent: number,
    immediateQuote: boolean,
  ): void {
    this.tradeError.set(undefined);
    const presetPercent = this.normalizePresetPercent(percent);
    if (presetPercent === undefined) {
      return;
    }

    if (this.quoteSide() === 'buy') {
      const base = this.maxBuyableTokens();
      if (!Number.isFinite(base) || base <= 0) {
        this.tradeError.set(this.buyPresetBlockedReason());
        return;
      }
      const value = presetPercent === 100 ? base : (base * presetPercent) / 100;
      this.quoteAmount.set(this.trimTradeAmount(value));
      this.clearStaleQuoteForCurrentAmount();
      this.requestQuoteRefresh(immediateQuote);
      return;
    }

    // Filling a specific order is capped by that order's remaining fillable
    // size (the quote's `availableAmount`); a brand-new sell is capped by
    // wallet balance, and in market mode also by resting bid liquidity.
    const capAmount = this.activeFillOrderId()
      ? this.quote()?.availableAmount
      : this.maxSellableTokenBaseUnits()?.toString();
    const amount = this.tokenPresetAmount(capAmount, presetPercent);
    if (!amount) {
      this.tradeError.set(
        this.owner()
          ? this.orderMode() === 'market' && !this.marketBookFillableBaseUnits()
            ? 'No bid liquidity available to size a sell percentage.'
            : `No available ${this.tradeTicker()} balance to sell.`
          : 'Connect a wallet to use a percentage preset.',
      );
      return;
    }
    this.quoteAmount.set(this.clampTradeAmountDecimals(amount));
    this.clearStaleQuoteForCurrentAmount();
    this.requestQuoteRefresh(immediateQuote);
  }

  setWrapAmountPreset(percent: 25 | 50 | 75 | 100): void {
    const amount = this.tokenPresetAmount(
      this.ownerCanonicalBalance(),
      percent,
    );
    if (amount) {
      this.wrapAmount.set(amount);
    }
  }

  setUnwrapAmountPreset(percent: 25 | 50 | 75 | 100): void {
    const amount = this.tokenPresetAmount(this.ownerWrappedBalance(), percent);
    if (amount) {
      this.unwrapAmount.set(amount);
    }
  }

  /**
   * Applies percentage presets to token amounts that may arrive either as
   * indexer base units or as an already-displayable decimal string.
   */
  private tokenPresetAmount(
    rawAmount: string | null | undefined,
    percent: number,
  ): string | undefined {
    const presetPercent = this.normalizePresetPercent(percent);
    if (presetPercent === undefined) {
      return undefined;
    }

    const raw = rawAmount?.trim();
    if (!raw) {
      return undefined;
    }

    if (/^\d+$/.test(raw)) {
      const baseUnits = BigInt(raw);
      if (baseUnits <= 0n) {
        return undefined;
      }
      const value =
        presetPercent === 100
          ? baseUnits
          : (baseUnits * BigInt(presetPercent)) / 100n;
      return formatKcc20BaseUnitsForDisplay(value, this.tokenDecimals());
    }

    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return undefined;
    }
    const value =
      presetPercent === 100 ? numeric : (numeric * presetPercent) / 100;
    return this.trimDecimal(value);
  }

  private normalizePresetPercent(percent: number): number | undefined {
    if (!Number.isFinite(percent)) {
      return undefined;
    }
    return Math.max(0, Math.min(100, Math.round(percent)));
  }

  /**
   * The max amount of this token the wallet can buy with its available KAS
   * (KCC20-47): spend = balance − network reserve, with room reserved
   * up front for the protocol fee (bps of notional) so "Max" doesn't leave
   * the order under-funded once the fee is added on top, divided by the
   * sizing unit price. Limit mode uses the typed ticket price; market mode
   * uses best ask (no unit-price field). Returns 0 when no usable price /
   * balance is available so the preset is a no-op rather than producing a
   * bogus amount.
   */
  private maxBuyableTokens(): number {
    const balanceKas = this.walletManager.walletKaspaBalance();
    const unitPriceSompi = this.parseSompi(this.buySizingUnitPriceSompi());
    if (
      !Number.isFinite(balanceKas) ||
      balanceKas <= 0 ||
      unitPriceSompi === undefined ||
      unitPriceSompi <= 0n
    ) {
      return 0;
    }

    const balanceSompi = BigInt(kasToSompi(balanceKas.toFixed(8)));
    const byBalanceBaseUnits = calculateKcc20MaximumBuyTokenBaseUnits({
      availableSompi: balanceSompi,
      unitPriceSompi,
      priceScale: 10n ** BigInt(this.tokenDecimals()),
      networkReserveSompi: TokenPageComponent.BUY_NETWORK_RESERVE_SOMPI,
      protocolFeeBps: this.protocolFeeBps(),
      minimumProtocolFeeSompi: KCC20_MIN_PROTOCOL_FEE_SOMPI,
    });
    const byBalance = Number(
      formatKcc20BaseUnitsForDisplay(byBalanceBaseUnits, this.tokenDecimals()),
    );
    if (!Number.isFinite(byBalance) || byBalance <= 0) return 0;
    if (this.orderMode() !== 'market') {
      return byBalance;
    }
    // Market buys cannot exceed resting ask size — otherwise /quote returns
    // valid:false ("not enough matching open order liquidity…").
    const byBook = this.marketBookFillableDisplayTokens();
    if (byBook === undefined) {
      return 0;
    }
    return Math.min(byBalance, byBook);
  }

  /**
   * Sell preset / slider cap: wallet orderbook balance, and for market mode the
   * lesser of that and resting bid liquidity.
   */
  private maxSellableTokenBaseUnits(): bigint | undefined {
    const walletRaw = this.ownerWrappedBalance()?.trim();
    const wallet =
      walletRaw && /^\d+$/.test(walletRaw) ? BigInt(walletRaw) : undefined;
    if (this.orderMode() !== 'market') {
      return wallet !== undefined && wallet > 0n ? wallet : undefined;
    }
    const book = this.marketBookFillableBaseUnits();
    if (book === undefined) {
      return undefined;
    }
    if (wallet === undefined) {
      return book;
    }
    const capped = wallet < book ? wallet : book;
    return capped > 0n ? capped : undefined;
  }

  /** Total resting size on the relevant book side, in token base units. */
  private marketBookFillableBaseUnits(): bigint | undefined {
    const book = this.orderbook();
    if (!book) {
      return undefined;
    }
    const levels =
      this.quoteSide() === 'buy'
        ? book.askDepth?.length
          ? book.askDepth
          : undefined
        : book.bidDepth?.length
          ? book.bidDepth
          : undefined;
    const rows =
      levels ??
      (this.quoteSide() === 'buy'
        ? this.depthAsksDisplay().map((row) => row.level)
        : this.depthBids().map((row) => row.level));
    let total = 0n;
    for (const level of rows) {
      total += parsePositiveBigInt(level.tokenAmount) ?? 0n;
    }
    return total > 0n ? total : undefined;
  }

  private marketBookFillableDisplayTokens(): number | undefined {
    const base = this.marketBookFillableBaseUnits();
    if (base === undefined) {
      return undefined;
    }
    const display = Number(
      formatKcc20BaseUnitsForDisplay(base, this.tokenDecimals()),
    );
    return Number.isFinite(display) && display > 0 ? display : undefined;
  }

  /**
   * Unit price used to convert Available KAS → token amount for buy presets /
   * the amount slider. Limit mode uses the typed ticket price. Market mode has
   * no price field — size against best ask, then last trade / close so the
   * slider and % buttons stay usable when depth is thin.
   */
  private buySizingUnitPriceSompi(): string | undefined {
    if (this.orderMode() === 'limit') {
      const typed = this.orderUnitPriceSompi().trim();
      return this.isPositiveInteger(typed) ? typed : undefined;
    }
    const candidates = [
      this.selectedMarket()?.bestAskUnitPriceSompi,
      this.cheapestAskUnitPriceSompi(),
      this.stats()?.closeUnitPriceSompi,
      this.candles().at(-1)?.closeUnitPriceSompi,
      this.selectedMarket()?.bestBidUnitPriceSompi,
    ];
    for (const candidate of candidates) {
      const price = candidate?.trim();
      if (price && this.isPositiveInteger(price)) {
        return price;
      }
    }
    return undefined;
  }

  /** Lowest ask in depth / raw orders (not the UI-reversed display order). */
  private cheapestAskUnitPriceSompi(): string | undefined {
    const book = this.orderbook();
    const levels = book?.askDepth?.length
      ? book.askDepth
      : this.depthAsksDisplay().map((row) => row.level);
    let best: bigint | undefined;
    for (const level of levels ?? []) {
      const price = parsePositiveBigInt(level.unitPriceSompi);
      if (price === undefined) {
        continue;
      }
      if (best === undefined || price < best) {
        best = price;
      }
    }
    return best?.toString();
  }

  private buyPresetBlockedReason(): string {
    if (this.orderMode() === 'limit' && !this.buySizingUnitPriceSompi()) {
      return 'Enter a unit price first, then use a percentage preset.';
    }
    if (this.orderMode() === 'market' && !this.marketBookFillableBaseUnits()) {
      return 'No ask liquidity available to size a buy percentage.';
    }
    if (this.orderMode() === 'market' && !this.buySizingUnitPriceSompi()) {
      return 'No market price available to size a buy percentage.';
    }
    return 'Not enough available KAS balance to buy at this price.';
  }

  private estimatedProtocolFeeSompi(notional: bigint): bigint {
    return calculateKcc20ProtocolFeeSompi(
      notional,
      this.protocolFeeBps(),
      KCC20_MIN_PROTOCOL_FEE_SOMPI,
    );
  }

  setOrderUnitPriceKas(value: string): void {
    this.orderUnitPriceKas.set(value);
    this.orderUnitPriceSompi.set(kasToSompi(value));
    if (this.orderMode() === 'limit') {
      this.quote.set(undefined);
      this.lastQuoteRequestKey = undefined;
      this.requestQuoteRefresh();
    }
  }

  private setOrderUnitPriceSompi(value: string | null | undefined): void {
    const unitPriceSompi = (value || '').trim();
    this.orderUnitPriceSompi.set(unitPriceSompi);
    this.orderUnitPriceKas.set(
      unitPriceSompi ? sompiToKas(unitPriceSompi) : '',
    );
  }

  isSelectedDepthLevel(
    row: Kcc20DepthDisplayRow,
    side: OrderbookSide,
  ): boolean {
    const selectedOrder = this.selectedDepthOrder(side);
    if (selectedOrder) {
      return orderUnitPriceSompi(selectedOrder) === row.level.unitPriceSompi;
    }
    return (
      !this.selectedOrderId() &&
      this.selectedOrderSide() === side &&
      this.orderUnitPriceSompi().trim() === row.level.unitPriceSompi
    );
  }

  async selectOrderbookLevel(row: Kcc20DepthDisplayRow, side: OrderbookSide) {
    const selectedOrder = this.singleOrderForDepthLevel(row, side);
    this.orderMode.set('limit');
    this.quoteSide.set(side === 'ask' ? 'buy' : 'sell');
    this.setOrderUnitPriceSompi(
      orderUnitPriceSompi(selectedOrder) ?? row.level.unitPriceSompi,
    );
    if (row.level.orderCount === 1 && !selectedOrder) {
      this.selectedOrderId.set(undefined);
      this.selectedOrderSide.set(undefined);
      this.quoteAmount.set('');
      this.quote.set(undefined);
      this.quoteLoading.set(false);
      this.tradeMessage.set(undefined);
      this.tradeError.set(
        'Exact order details are still loading. Refresh the orderbook and try again.',
      );
      return;
    }
    this.selectedOrderId.set(selectedOrder?.orderId);
    this.selectedOrderSide.set(side);
    this.quoteAmount.set(
      this.clampTradeAmountDecimals(
        formatKcc20BaseUnitsValue(
          orderRemainingTokenAmount(selectedOrder) ?? row.cumulativeTokenAmount,
          this.tokenDecimals(),
        ),
      ),
    );
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    this.refreshQuote();
  }

  private selectedDepthOrder(side: OrderbookSide): Kcc20Order | undefined {
    const selectedOrderId = this.selectedOrderId();
    if (!selectedOrderId) {
      return undefined;
    }
    const orders =
      side === 'ask' ? this.orderbook()?.asks : this.orderbook()?.bids;
    return orders?.find((order) => order.orderId === selectedOrderId);
  }

  private singleOrderForDepthLevel(
    row: Kcc20DepthDisplayRow,
    side: OrderbookSide,
  ): Kcc20Order | undefined {
    if (row.level.orderCount !== 1) {
      return undefined;
    }
    const orders =
      side === 'ask' ? this.orderbook()?.asks : this.orderbook()?.bids;
    const matches = (orders ?? []).filter((order) => {
      const sideValue = (order.marketSide || order.side || '').toLowerCase();
      return (
        sideValue === side &&
        order.status === 'open' &&
        orderUnitPriceSompi(order) === row.level.unitPriceSompi &&
        !!orderRemainingTokenAmount(order)
      );
    });
    return matches.length === 1 ? matches[0] : undefined;
  }

  async setChartInterval(intervalMs: Kcc20ChartIntervalMs): Promise<void> {
    this.chartIntervalMs.set(intervalMs);
    await this.loadCandles();
  }

  async loadCandles(): Promise<void> {
    const marketId = this.marketId();
    if (!marketId) {
      this.candles.set([]);
      this.chartLoading.set(false);
      return;
    }
    this.chartLoading.set(true);

    try {
      const intervalMs = this.chartIntervalMs();
      const candles = await firstValueFrom(
        this.tradingApi.getCandles(marketId, {
          intervalMs,
          limit: candleLimitFor(intervalMs),
        }),
      );
      if (marketId !== this.marketId()) return;
      this.candles.set(candles);
    } catch {
      if (marketId !== this.marketId()) return;
      this.candles.set([]);
    } finally {
      // Guard like the other section loaders so a stale candle load cannot
      // clear the spinner of a newer in-flight load for a different market.
      if (marketId === this.marketId()) {
        this.chartLoading.set(false);
      }
    }
  }

  /** User panned past loaded history in the price chart -> page older buckets in. */
  async loadOlderCandles(range: { from: number; to: number }): Promise<void> {
    const marketId = this.marketId();
    if (!marketId) return;
    try {
      const older = await firstValueFrom(
        this.tradingApi.getCandles(marketId, {
          intervalMs: this.chartIntervalMs(),
          from: range.from,
          to: range.to,
          limit: 400,
        }),
      );
      if (marketId !== this.marketId() || !older.length) return;
      this.candles.update((current) => [...older, ...current]);
    } catch {
      // Best-effort: a failed history page just leaves the chart at its
      // current left edge; the user can pan again to retry.
    }
  }

  /** Public entry used by template blur / orderbook selection. */
  refreshQuote(): void {
    this.requestQuoteRefresh(true);
  }

  async loadMetadataPermission(): Promise<void> {
    const covenantId = this.covenantId();
    const permission = await this.loadMetadataPermissionValue(covenantId);
    if (covenantId !== this.covenantId()) return;
    this.metadataPermission.set(permission);
  }

  async loadOwnerHoldings(
    canonicalTokenId = this.covenantId(),
    marketId = this.marketId(),
  ): Promise<void> {
    const holdingsLoadId = ++this.ownerHoldingsLoadSequence;
    const owner = this.owner();
    if (!owner) {
      this.ownerTokenBalance.set(undefined);
      this.ownerPosition.set(undefined);
      this.ownerHoldingsWarnings.set([]);
      this.ownerHoldingsLoading.set(false);
      return;
    }

    this.ownerHoldingsLoading.set(true);
    try {
      const holdings = await firstValueFrom(
        this.holdingsService.loadTokenHoldings(
          owner,
          canonicalTokenId,
          marketId,
        ),
      );
      // Portfolio balance is keyed by canonical token id only. Do NOT gate on
      // marketId here: an early holdings fetch often starts before the trading
      // summary resolves the wrapper/market, and discarding that response left
      // the page stuck with empty balances even when /portfolio/.../balance
      // returned healthy native/orderbook amounts.
      if (
        holdingsLoadId !== this.ownerHoldingsLoadSequence ||
        owner !== this.owner() ||
        canonicalTokenId.toLowerCase() !== this.covenantId().toLowerCase()
      ) {
        return;
      }
      this.ownerTokenBalance.set(holdings.balance.data);
      this.ownerPosition.set(holdings.position.data);
      this.ownerHoldingsWarnings.set(holdings.warnings);
      this.syncWrapPrompt();
      // Holdings often resolve after the user already typed a sell amount and
      // kicked off /quote. Cancel that in-flight lookup once we know wrap is
      // required, so the Sell button does not keep spinning.
      if (this.quoteSide() === 'sell' && this.needsWrapToTrade()) {
        this.requestQuoteRefresh(true);
      }
    } catch {
      if (
        holdingsLoadId !== this.ownerHoldingsLoadSequence ||
        owner !== this.owner()
      ) {
        return;
      }
      this.ownerTokenBalance.set(undefined);
      this.ownerPosition.set(undefined);
      this.ownerHoldingsWarnings.set([
        'Token holdings could not be loaded. Refresh again in a few seconds.',
      ]);
    } finally {
      if (holdingsLoadId === this.ownerHoldingsLoadSequence) {
        this.ownerHoldingsLoading.set(false);
      }
    }
  }

  buildOrder(): void {
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    const wrapperId = this.wrappedMarketId();
    if (!this.owner()) {
      this.tradeError.set('Connect wallet before building an order.');
      return;
    }

    const wrapperBlock = this.wrapperTradingBlockedReason();
    if (wrapperBlock) {
      this.tradeError.set(wrapperBlock);
      return;
    }

    if (!wrapperId || !this.orderDraftReady()) {
      this.tradeError.set(
        'A known wrapped trading mode, token amount, and unit price are required first.',
      );
      return;
    }

    // Defensive: the build button is already disabled while this is truthy,
    // and the balance warning renders reactively from `orderBalanceError()`
    // itself (which clears the moment the balance is sufficient). Don't copy
    // it into the sticky `tradeError` signal, which would linger on screen
    // after the wallet connects / funds arrive.
    if (this.orderBalanceError()) {
      return;
    }
    if (this.orderMinValueError()) {
      return;
    }
    if (this.tryEnqueueLadderLimitOrders(wrapperId, this.covenantId())) {
      return;
    }
    const marketBlock = this.marketQuoteBlockedReason();
    if (marketBlock) {
      this.tradeError.set(marketBlock);
      return;
    }
    const limitBlock = this.limitQuoteBlockedReason();
    if (limitBlock) {
      this.tradeError.set(limitBlock);
      return;
    }
    const feeTicketBlock = this.feeTicketUsageMessage();
    if (feeTicketBlock) {
      this.tradeError.set(feeTicketBlock);
      return;
    }

    const fillOrderId = this.activeFillOrderId();
    const sweepQuote = this.activeSweepQuote();
    const side = this.quoteSide();
    const tokenAmount = this.quoteAmount().trim();
    const covenantId = this.covenantId();
    const quoteMarketId = this.marketId();
    const useFeeTicket = this.feeTicketActive();
    const unitPriceSompi = (
      fillOrderId || sweepQuote
        ? this.activeFillUnitPriceSompi() || this.quote()?.unitPriceSompi
        : this.orderUnitPriceSompi()
    )?.trim();
    if (!unitPriceSompi && !sweepQuote) {
      this.tradeError.set('A valid unit price is required first.');
      return;
    }
    const sweepMode = this.orderMode();
    const sweepLimitUnitPriceSompi =
      sweepMode === 'limit' ? this.orderUnitPriceSompi().trim() : undefined;
    const sweepDraft = sweepQuote
      ? this.buildSweepDraft(
          wrapperId,
          side,
          tokenAmount,
          sweepQuote,
          sweepMode,
          sweepLimitUnitPriceSompi,
        )
      : undefined;
    // Keep this branch's sell consolidation path, but use develop's fresh
    // sweep rebuild so market fills are re-quoted at execution time.
    const actionRequest = {
      tokenId: covenantId,
      title: sweepDraft
        ? sweepDraft.mode === 'market'
          ? marketOrderLabel(side)
          : WALLET_OPERATION_LABELS.fillOrder
        : fillOrderId
          ? WALLET_OPERATION_LABELS.fillOrder
          : limitOrderLabel(side),
      build: () =>
        sweepDraft
          ? this.buildFreshSweep(
              covenantId,
              quoteMarketId || covenantId,
              wrapperId,
              side,
              tokenAmount,
              sweepDraft.mode,
              sweepDraft.limitUnitPriceSompi,
            )
          : fillOrderId
            ? this.tokenActionApi.buildFill(covenantId, {
                wrappedMarketId: wrapperId,
                side,
                targetOrderId: fillOrderId,
                tokenAmount,
                unitPriceSompi: unitPriceSompi || '',
                useFeeTicket,
              })
            : this.tokenActionApi.buildOrder(covenantId, {
                wrappedMarketId: wrapperId,
                side,
                tokenAmount,
                unitPriceSompi: unitPriceSompi || '',
              }),
      onSubmitted: (task: Kcc20WalletOperationTask) =>
        this.handleOrderTicketSubmitted(task),
    };
    if (side === 'sell') {
      void this.tokenConsolidationFlow
        .execute({
          ...actionRequest,
          asset: 'orderbook',
          tokenAmount,
          tokenLabel: this.displayName(),
          wrappedMarketId: wrapperId,
          onQueued: (actionId: string) =>
            this.trackOrderTicketAction(actionId, covenantId),
        })
        .catch((error: unknown) =>
          this.tradeError.set(
            error instanceof Error ? error.message : 'Order request failed.',
          ),
        );
      return;
    }
    const actionId = this.walletOperationQueue.enqueueAction(actionRequest);
    this.trackOrderTicketAction(actionId, covenantId);
  }

  private buildSweepDraft(
    wrappedMarketId: string,
    side: 'buy' | 'sell',
    tokenAmount: string,
    quote: Kcc20Quote,
    mode: OrderMode,
    limitUnitPriceSompi?: string,
  ): Kcc20SweepActionDraft {
    const feeTicketId = this.feeTicketRootIdForQuote(quote);
    return {
      wrappedMarketId,
      side,
      tokenAmount,
      mode,
      ...(mode === 'limit'
        ? { limitUnitPriceSompi: limitUnitPriceSompi ?? '' }
        : {}),
      ...(feeTicketId ? { feeTicketId } : {}),
      expectedFills: (quote.fills ?? []).map((fill) => ({
        orderId: fill.orderId,
        tokenAmount: fill.tokenAmount,
        unitPriceSompi: fill.unitPriceSompi,
      })),
      ...(quote.buyerPaysSompi
        ? { maxBuyerPaysSompi: quote.buyerPaysSompi }
        : {}),
      ...(quote.sellerReceivesSompi
        ? { minSellerReceivesSompi: quote.sellerReceivesSompi }
        : {}),
    };
  }

  private buildFreshSweep(
    covenantId: string,
    quoteMarketId: string,
    wrappedMarketId: string,
    side: QuoteSide,
    tokenAmount: string,
    mode: OrderMode,
    limitUnitPriceSompi?: string,
  ): Observable<Kcc20UnsignedTokenActionPayload> {
    return this.tradingApi
      .getQuote(quoteMarketId, {
        side,
        amount: tokenAmount,
        mode,
        ...(mode === 'limit'
          ? { limitUnitPriceSompi: limitUnitPriceSompi ?? '' }
          : {}),
      })
      .pipe(
        switchMap((quote) => {
          const blockedReason = sweepQuoteBlockedReason(side, quote);
          if (blockedReason) {
            throw new Error(
              `Market changed before execution. ${blockedReason}`,
            );
          }
          return this.tokenActionApi.buildSweep(
            covenantId,
            this.buildSweepDraft(
              wrappedMarketId,
              side,
              tokenAmount,
              quote,
              mode,
              limitUnitPriceSompi,
            ),
          );
        }),
      );
  }

  private feeTicketRootIdForQuote(
    quote: Kcc20Quote | null | undefined,
  ): string | undefined {
    if (!this.canApplyFeeTicketToQuote(quote)) {
      return undefined;
    }
    if (this.isMultiSweepQuote(quote)) {
      return feeTicketRootIdForSweepQuote(
        quote,
        this.activeWrapper()?.feeTicketId,
      );
    }
    const rootId = normalizeFeeTicketRootId(quote.feeTicketRootId);
    if (
      !quote.feeTicketEligible ||
      quote.feeTicketDiscountedProtocolFeeSompi == null ||
      !rootId
    ) {
      return undefined;
    }
    return rootId;
  }

  private canApplyFeeTicketToQuote(
    quote: Kcc20Quote | null | undefined,
  ): quote is Kcc20Quote {
    if (
      !kcc20FeeTicketFeatureEnabled() ||
      !this.useFeeTicket() ||
      !quote?.valid ||
      this.effectiveFeeTicketDiscountedProtocolFeeSompi(quote) == null
    ) {
      return false;
    }

    const available = this.availableFeeTicketCount();
    if (available === undefined || available === null) {
      return false;
    }

    const required = Math.max(
      feeTicketRequiredCountForExecution(quote, this.activeFillOrderId()),
      1,
    );
    return available >= required;
  }

  private async loadFeeTicketBalance(): Promise<void> {
    if (!kcc20FeeTicketFeatureEnabled()) {
      this.feeTicketAvailable.set(undefined);
      this.feeTicketBalanceLoading.set(false);
      return;
    }
    const owner = this.owner();
    if (!owner) {
      this.feeTicketAvailable.set(undefined);
      return;
    }
    this.feeTicketBalanceLoading.set(true);
    try {
      const available = await firstValueFrom(
        this.rewardsApi.getMyAvailableFeeTickets(),
      );
      if (owner !== this.owner()) {
        return;
      }
      this.feeTicketAvailable.set(available);
    } catch {
      if (owner !== this.owner()) {
        return;
      }
      this.feeTicketAvailable.set(undefined);
    } finally {
      if (owner === this.owner()) {
        this.feeTicketBalanceLoading.set(false);
      }
    }
  }

  buildMint(): void {
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    if (!this.owner()) {
      this.tradeError.set('Connect wallet before building a mint.');
      return;
    }

    if (!this.mintDraftReady()) {
      this.tradeError.set(
        this.mintAmountQuote()?.belowMinimumKas
          ? (this.mintAdjustedAmountMessage() ??
              `Minimum of ${KCC20_MIN_PAID_MINT_KAS_LABEL} KAS`)
          : (this.mintAmountCapError() ??
              this.mintRemainingSupplyError() ??
              'Enter a mint amount for a mintable token first.'),
      );
      return;
    }

    // Below-minimum is blocked above; only dust/exact-sompi snaps are submitted.
    const tokenAmount =
      this.mintAmountQuote()?.adjustedTokenAmount ?? this.mintAmount().trim();
    this.walletOperationQueue.enqueueAction({
      tokenId: this.covenantId(),
      title: WALLET_OPERATION_LABELS.mint,
      build: () =>
        this.tokenActionApi.buildMint(this.covenantId(), { tokenAmount }),
    });
  }

  setPublicMintActive(active: boolean): void {
    if (
      this.mintAvailabilityUpdating() ||
      !this.publicMintControlSupported() ||
      !this.publicMintOwner() ||
      active === this.publicMintActive()
    ) {
      return;
    }

    const covenantId = this.covenantId();
    const run = () => {
      if (covenantId !== this.covenantId()) {
        throw new Error('Open the original token page to retry this update.');
      }
      this.setPendingPublicMintAvailability(covenantId, active);
      const { completion } = this.mintAvailabilityFlow.enqueueActionAndWait({
        active,
        retry: run,
        tokenId: covenantId,
        title: mintAvailabilityLabel(active),
        build: () =>
          this.tokenActionApi.buildMintAvailability(covenantId, { active }),
      });
      void completion
        .then(async (result) => {
          let projectionRetryStarted = false;
          try {
            if (
              result.status === 'indexed' &&
              covenantId === this.covenantId()
            ) {
              await this.refreshTokenDetail();
              if (this.publicMintActive() !== active) {
                this.pollPublicMintAvailability(covenantId, active);
                projectionRetryStarted = true;
              }
            }
          } finally {
            if (!projectionRetryStarted) {
              this.clearPendingPublicMintAvailability(covenantId);
            }
          }
        })
        .catch(() => this.clearPendingPublicMintAvailability(covenantId));
    };
    run();
  }

  buildWrap(): void {
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    const wrapperId = this.wrappedMarketId();
    if (!this.owner()) {
      this.tradeError.set('Connect wallet before wrapping to trade.');
      return;
    }

    if (!wrapperId || !this.wrapDraftReady()) {
      this.tradeError.set(
        'A known wrapped trading mode and token amount are required first.',
      );
      return;
    }

    const tokenAmount = this.wrapAmount().trim();
    void this.tokenConsolidationFlow
      .execute({
        tokenId: this.covenantId(),
        asset: 'native',
        tokenAmount,
        tokenLabel: this.displayName(),
        wrappedMarketId: wrapperId,
        title: WALLET_OPERATION_LABELS.wrap,
        build: () =>
          this.wrapperFlow.buildWrap(this.covenantId(), wrapperId, tokenAmount),
      })
      .catch((error: unknown) =>
        this.tradeError.set(
          error instanceof Error ? error.message : 'Wrap request failed.',
        ),
      );
  }

  buildUnwrap(): void {
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    const wrapperId = this.wrappedMarketId();
    if (!this.owner()) {
      this.tradeError.set('Connect wallet before unwrapping to hold.');
      return;
    }

    if (!wrapperId || !this.unwrapDraftReady()) {
      this.tradeError.set(
        'A known wrapped trading mode and token amount are required first.',
      );
      return;
    }

    const tokenAmount = this.unwrapAmount().trim();
    void this.tokenConsolidationFlow
      .execute({
        tokenId: this.covenantId(),
        asset: 'orderbook',
        operation: 'unwrap',
        tokenAmount,
        tokenLabel: this.displayName(),
        wrappedMarketId: wrapperId,
        title: WALLET_OPERATION_LABELS.unwrap,
        build: () =>
          this.tokenActionApi.buildUnwrap(this.covenantId(), {
            wrappedMarketId: wrapperId,
            tokenAmount,
          }),
        buildForTokenAmount: (chunkTokenAmount) =>
          this.tokenActionApi.buildUnwrap(this.covenantId(), {
            wrappedMarketId: wrapperId,
            tokenAmount: chunkTokenAmount,
          }),
      })
      .catch((error: unknown) =>
        this.tradeError.set(
          error instanceof Error ? error.message : 'Unwrap request failed.',
        ),
      );
  }

  buildWrapperMarketDeploy(): void {
    this.tradeError.set(undefined);
    this.tradeMessage.set(undefined);
    if (!this.owner()) {
      this.tradeError.set('Connect wallet before building a wrapper deploy.');
      return;
    }

    if (!this.wrapperDeployReady()) {
      this.tradeError.set(
        'Wrapper deploy needs no existing wrapper and a valid root amount.',
      );
      return;
    }

    const wrappedRootAmount = this.wrapperDeployRootAmount().trim() || '0';
    this.walletOperationQueue.enqueueAction({
      tokenId: this.covenantId(),
      title: WALLET_OPERATION_LABELS.createMarket,
      build: () =>
        this.tokenActionApi.buildWrapperMarketDeploy(this.covenantId(), {
          wrappedRootAmount,
        }),
    });
  }

  formatSompi = formatSompi;
  formatCount = formatCount;
  formatDateMs = formatDateMs;
  formatDateOnlyMs = formatDateOnlyMs;
  formatTimeOnlyMs = formatTimeOnlyMs;

  formatTokenBaseAmount(value: string | null | undefined): string {
    return value
      ? formatKcc20BaseUnitsValueGrouped(
          value,
          this.tokenDecimals(),
          '--',
          TokenPageComponent.TRADE_AMOUNT_MAX_DECIMALS,
        )
      : '--';
  }

  tokenScaledSompiLabel(value: string | number | null | undefined): string {
    const scaled = scaleKcc20DecimalBaseValue(value, this.tokenDecimals());
    if (scaled === undefined) {
      return '--';
    }
    return `${formatPrettyNumber(sompiToKas(scaled), { maxDecimals: 0 })} KAS`;
  }

  private walletOperationTaskBelongsToToken(
    task: Kcc20WalletOperationTask,
    covenantId: string,
  ): boolean {
    return task.tokenId?.toLowerCase() === covenantId;
  }

  private async handleWalletOperationTaskCompletion(
    task: Kcc20WalletOperationTask,
  ): Promise<void> {
    // Lifecycle status (indexed/failed) is owned by the Wallet operations
    // overlay now, so this background completion sets no inline message — and
    // must not clear tradeError/tradeMessage either, or a prior action finishing
    // could wipe a validation error or handoff note the user is currently seeing
    // for a different action they're mid-way through.

    if (task.status !== 'indexed') {
      logWalletOperation('token-page.taskCompletion.notIndexed', {
        id: task.id,
        operation: task.operation,
        status: task.status,
      });
      return;
    }

    // Still refresh the data a successful action changed (balances, order book,
    // position, activity) — that visible update is the in-context confirmation.
    logWalletOperation('token-page.taskCompletion.refreshing', {
      id: task.id,
      operation: task.operation,
    });
    if (
      this.isOrderTicketKind(this.orderOperationKind(task.operation)) &&
      !this.resetOrderTicketOnSubmitTaskIds.has(task.id)
    ) {
      this.resetOrderTicketOnSubmitTaskIds.add(task.id);
      this.resetOrderTicketFields();
    }
    await this.refreshAfterOperation(task.operation);
  }

  /**
   * Refreshes only the sections a successfully indexed action can change, so
   * unaffected content stays on screen instead of resetting to a full-page
   * skeleton:
   *  - mint / wrap / unwrap → token detail (holders, supply, total tradable),
   *    my position, and a one-shot silent trading-activity check (these ops
   *    do not produce a trade row, so do not poll the empty feed);
   *  - create / fill / cancel / sweep order → trading summary, order book
   *    (self-heal), my position, fee tickets, activity (self-heal), open
   *    orders (self-heal), clear a stale fill selection, and refresh the
   *    live quote so market/limit tickets track the new book;
   *  - verify token → token detail and edit permission only (verification is
   *    metadata-only and produces no trading activity row);
   *  - deploy wrapper → a brand-new market is created, so reload the page.
   * The balance/trade-changing actions above also produce a new on-chain
   * event, so the activity feed is refreshed alongside them. Each targeted
   * refresh keeps the current data on screen until the fresh copy arrives
   * (stale-while-revalidate); the refreshes whose endpoint supports it (token
   * detail, order book, activity) also bypass the backend cache via
   * `refresh: true`, while the live quote and position reads use their
   * endpoints' default freshness.
   */
  /**
   * The backend names order-book wallet operations per side — e.g.
   * `create-buy-order` / `create-sell-order` / `fill-ask-order` — rather
   * than the plain `create-order`/`fill-order`/`cancel-order` some call
   * sites here originally assumed. Matching on the verb prefix instead of
   * the exact string keeps every side variant recognized without having to
   * enumerate each one (and re-enumerate again if more get added).
   */
  private orderOperationKind(
    operation: string,
  ): 'create' | 'fill' | 'cancel' | 'sweep' | undefined {
    const match =
      /^(create|fill|cancel)-(?:(?:buy|sell|ask|bid)-)?order$/.exec(
        operation,
      ) || /^sweep-(?:ask|bid)-orders$/.exec(operation);
    if (!match) {
      return undefined;
    }
    return (match[1] || 'sweep') as 'create' | 'fill' | 'cancel' | 'sweep';
  }

  private async refreshAfterOperation(operation: string): Promise<void> {
    const orderKind = this.orderOperationKind(operation);
    logWalletOperation('token-page.refreshAfterOperation', {
      operation,
      orderKind,
    });
    if (orderKind) {
      // A create/fill/cancel/sweep just moved KAS — the wallet extension's
      // reported balance can read as a transient false low until change
      // UTXOs settle (see WalletManager.walletBalanceConfirmed). Reopen the
      // settle window so orderBalanceError treats that window as loading
      // instead of firing a spurious "insufficient balance" warning.
      this.walletManager.beginBalanceSettleWindow();
      await Promise.all([
        this.refreshTradingSummaryWithSelfHeal(),
        this.refreshOrderbookWithSelfHeal(),
        this.loadOwnerHoldings(),
        this.loadFeeTicketBalance(),
        this.refreshActivityWithSelfHeal(),
        // Create/fill/cancel/sweep all change resting liquidity — keep the
        // open-orders tab and market quote in sync with the same self-heal
        // pattern as the book/activity feeds.
        this.refreshOpenOrdersWithSelfHeal(),
      ]);
      this.clearStaleSelectedOrder();
      // Resting limits can submit from typed price × amount. Marketable
      // limits (price still crosses the refreshed book) need a live quote
      // for `orderDraftReady` / Fill now. Requote after the book refresh,
      // with the settle window already open so a transient false-low wallet
      // cannot toast "insufficient balance".
      if (this.orderMode() === 'limit') {
        this.requestQuoteRefresh(true);
      }
      return;
    }

    switch (operation) {
      case 'mint-token':
      case 'wrap':
      case 'unwrap': {
        // Same UTXO-settling false-low risk as the order branch above.
        this.walletManager.beginBalanceSettleWindow();
        // "Indexed" only confirms the wallet operation's on-chain actions
        // settled — the separate supply/holder aggregation this panel reads
        // from can lag a few seconds behind that, even on a cache-bypassing
        // (`refresh: true`) read. Snapshot the current numbers so the
        // background poll below can tell a genuine update apart from
        // "still the same not-yet-aggregated numbers".
        const baseline = this.tokenDetailSupplySignature();
        await Promise.all([
          this.refreshTokenDetail(),
          this.loadOwnerHoldings(),
          // Mint/wrap/unwrap never write a trading fill. Recheck the feed
          // once, silently, so an empty "No trading activity yet" state
          // does not flash a skeleton or get polled for a row that will
          // not appear.
          this.loadActivity({ refresh: true, silent: true }),
        ]);
        this.pollTokenDetailUntilChanged(baseline);
        return;
      }
      case 'set-mint-availability':
        await this.refreshTokenDetail();
        return;
      case 'deploy-wrapper-market':
        await this.load();
        return;
      case 'verify-token':
        await Promise.all([
          this.refreshTokenDetail(),
          this.loadMetadataPermission(),
        ]);
        return;
      default:
        await Promise.all([
          this.refreshTokenDetail(),
          this.loadOwnerHoldings(),
          this.refreshActivityWithSelfHeal(),
        ]);
    }
  }

  /**
   * A cheap signature of the top of the activity feed, used to tell "the
   * refresh below actually returned a new event" apart from "the indexer's
   * activity aggregation hasn't caught up yet and returned the same rows".
   */
  private activityFeedSignature(): string {
    return this.activity()
      .slice(0, 5)
      .map(
        (event) =>
          `${event.txidHex}:${event.orderId ?? ''}:${event.blockTimeMs}`,
      )
      .join('|');
  }

  private orderbookSignature(): string {
    const orderbook = this.orderbook();
    return [
      ...(orderbook?.asks ?? []).slice(0, 10),
      ...(orderbook?.bids ?? []).slice(0, 10),
    ]
      .map(
        (order) =>
          `${order.orderId}:${order.remainingTokenAmount ?? order.tokenAmount}:${order.status ?? ''}`,
      )
      .join('|');
  }

  private async refreshTradingSummaryWithSelfHeal(): Promise<void> {
    const requestId = this.loadSequence;
    const covenantId = this.covenantId();
    const healId = ++this.tradingSummaryHealSequence;
    await this.loadTradingSummary(
      requestId,
      covenantId,
      { refresh: true },
      healId,
    );
    this.pollTradingSummaryWithBackoff(requestId, covenantId, healId);
  }

  private pollTradingSummaryWithBackoff(
    requestId: number,
    covenantId: string,
    healId: number,
    attempt = 0,
  ): void {
    const delaysMs = [1000, 2000, 3500, 5000];
    if (attempt >= delaysMs.length) return;
    setTimeout(() => {
      void (async () => {
        if (
          requestId !== this.loadSequence ||
          covenantId !== this.covenantId() ||
          healId !== this.tradingSummaryHealSequence
        ) {
          return;
        }
        await this.loadTradingSummary(
          requestId,
          covenantId,
          { refresh: true },
          healId,
        );
        if (
          requestId === this.loadSequence &&
          covenantId === this.covenantId() &&
          healId === this.tradingSummaryHealSequence
        ) {
          this.pollTradingSummaryWithBackoff(
            requestId,
            covenantId,
            healId,
            attempt + 1,
          );
        }
      })();
    }, delaysMs[attempt]);
  }

  private async refreshOrderbookWithSelfHeal(): Promise<void> {
    const baseline = this.orderbookSignature();
    await this.loadOrderbook({ refresh: true, silent: true });
    if (this.orderbookSignature() === baseline) {
      this.pollOrderbookUntilChanged(baseline);
    }
  }

  /** Drop a ticket fill target that no longer exists on the live book. */
  private clearStaleSelectedOrder(): void {
    const selectedId = this.selectedOrderId();
    if (!selectedId) {
      return;
    }
    const orderbook = this.orderbook();
    const stillListed = [
      ...(orderbook?.asks ?? []),
      ...(orderbook?.bids ?? []),
    ].some((order) => order.orderId === selectedId);
    if (!stillListed) {
      this.selectedOrderId.set(undefined);
      this.selectedOrderSide.set(undefined);
    }
  }

  private pollOrderbookUntilChanged(
    baselineSignature: string,
    attempt = 0,
  ): void {
    const delaysMs = [1000, 2000, 3500, 5000];
    if (attempt >= delaysMs.length) return;
    const requestId = this.loadSequence;
    setTimeout(() => {
      void (async () => {
        if (requestId !== this.loadSequence) return;
        await this.loadOrderbook({ refresh: true, silent: true });
        if (
          requestId === this.loadSequence &&
          this.orderbookSignature() === baselineSignature
        ) {
          this.pollOrderbookUntilChanged(baselineSignature, attempt + 1);
        }
      })();
    }, delaysMs[attempt]);
  }

  /**
   * Refreshes the activity feed and, if the just-completed operation's own
   * event hasn't shown up yet (the activity aggregation can lag a beat
   * behind the order book / balances, which read from a different, faster
   * path), quietly retries with backoff in the background — the same
   * self-heal pattern used for the mint/wrap supply numbers in
   * {@link pollTokenDetailUntilChanged}. This is what closes the "order book
   * updated but activity didn't" gap without the user needing to do
   * anything.
   */
  private async refreshActivityWithSelfHeal(): Promise<void> {
    const baseline = this.activityFeedSignature();
    await this.loadActivity({ refresh: true, silent: true });
    if (this.activityFeedSignature() === baseline) {
      this.pollActivityUntilChanged(baseline);
    }
  }

  private pollActivityUntilChanged(
    baselineSignature: string,
    attempt = 0,
  ): void {
    const delaysMs = [1500, 2500, 3500, 5000, 7000];
    if (attempt >= delaysMs.length) return;
    const requestId = this.loadSequence;
    setTimeout(() => {
      void (async () => {
        if (requestId !== this.loadSequence) return;
        await this.loadActivity({ refresh: true, silent: true });
        if (
          requestId === this.loadSequence &&
          this.activityFeedSignature() === baselineSignature
        ) {
          this.pollActivityUntilChanged(baselineSignature, attempt + 1);
        }
      })();
    }, delaysMs[attempt]);
  }

  /**
   * Re-fetches token identity, trading summary, supply summary, holder
   * summary, and wrap/unwrap/mint action readiness without the legacy
   * consolidated page-data route, keeping existing data on screen until each
   * split response arrives.
   */
  private async refreshTokenDetail(): Promise<void> {
    const covenantId = this.covenantId();
    const requestId = this.loadSequence;
    await Promise.all([
      this.loadTokenIdentityForRequest(requestId, covenantId, {
        refresh: true,
      }),
      this.loadTradingSummary(requestId, covenantId, { refresh: true }),
      this.loadTokenSummariesForRequest(requestId, covenantId, {
        refresh: true,
      }),
      this.loadTokenActionsForRequest(requestId, covenantId, {
        refresh: true,
      }),
    ]);
  }

  /** Snapshot of the fields the mint/wrap panel displays, to detect whether a
   * re-fetch actually returned fresh data or just the same stale numbers. */
  private tokenDetailSupplySignature(): string {
    const detail = this.tokenDetail();
    return JSON.stringify({
      minted: detail?.supply?.mintedSupply,
      remaining: detail?.supply?.remainingMintSupply,
      wrapperReserve: detail?.supply?.wrapperReserveSupply,
      orderbookTradeable: detail?.supply?.orderbookTradeableSupply,
      holderCount: detail?.holderCount,
    });
  }

  /**
   * Background, best-effort self-heal (never awaited by the caller — the
   * success toast already fired off the first refresh above). A just-indexed
   * mint/wrap/unwrap can land before the backend's supply/holder aggregation
   * has caught up, so even a `refresh: true` re-fetch right after "indexed"
   * can still return the pre-operation numbers. If the signature still
   * matches the pre-operation baseline, quietly retry with backoff for up to
   * ~30s total before giving up — this is what heals the "minted supply
   * didn't move" case without the user needing to do anything.
   */
  private pollTokenDetailUntilChanged(
    baselineSignature: string,
    attempt = 0,
  ): void {
    const delaysMs = [2000, 3000, 4000, 5000, 6000, 8000];
    if (attempt >= delaysMs.length) return;
    const requestId = this.loadSequence;
    setTimeout(() => {
      void (async () => {
        if (requestId !== this.loadSequence) return;
        await this.refreshTokenDetail();
        if (
          requestId === this.loadSequence &&
          this.tokenDetailSupplySignature() === baselineSignature
        ) {
          this.pollTokenDetailUntilChanged(baselineSignature, attempt + 1);
        }
      })();
    }, delaysMs[attempt]);
  }

  private setPendingPublicMintAvailability(
    covenantId: string,
    active: boolean,
  ): void {
    const pending = new Map(this.pendingPublicMintAvailability());
    pending.set(covenantId, active);
    this.pendingPublicMintAvailability.set(pending);
  }

  private clearPendingPublicMintAvailability(covenantId: string): void {
    const current = this.pendingPublicMintAvailability();
    if (!current.has(covenantId)) return;
    const pending = new Map(current);
    pending.delete(covenantId);
    this.pendingPublicMintAvailability.set(pending);
  }

  /** Keep the optimistic toggle until the indexed token summary reflects it. */
  private pollPublicMintAvailability(
    covenantId: string,
    expectedActive: boolean,
    attempt = 0,
  ): void {
    const delaysMs = [1500, 2500, 3500, 5000, 7000, 9000];
    if (attempt >= delaysMs.length) {
      this.clearPendingPublicMintAvailability(covenantId);
      return;
    }
    const requestId = this.loadSequence;
    setTimeout(() => {
      void (async () => {
        if (
          requestId !== this.loadSequence ||
          covenantId !== this.covenantId()
        ) {
          this.clearPendingPublicMintAvailability(covenantId);
          return;
        }
        try {
          await this.refreshTokenDetail();
        } catch {
          this.clearPendingPublicMintAvailability(covenantId);
          return;
        }
        if (this.publicMintActive() === expectedActive) {
          this.clearPendingPublicMintAvailability(covenantId);
          return;
        }
        this.pollPublicMintAvailability(
          covenantId,
          expectedActive,
          attempt + 1,
        );
      })();
    }, delaysMs[attempt]);
  }

  /** Bare KAS price (no unit suffix) for orderbook rows under the "Price (KAS)" header. */
  priceBare(value: string | number | null | undefined): string {
    return formatSompi(value).replace(/ KAS$/, '');
  }

  async toggleWatch(): Promise<void> {
    const owner = this.owner();
    const tokenId = this.covenantId();
    if (!owner || this.watchButtonDisabled()) {
      return;
    }
    try {
      await this.watchlistState.toggle(
        owner,
        tokenId,
        this.requestId('watchlist-toggle'),
      );
    } catch {
      this.notificationService.error(
        'Watchlist',
        'Could not update watchlist.',
      );
    }
  }
  shortId = shortId;
  tokenLabel = tokenLabel;

  holderDisplayLabel(holder: Kcc20TokenHolder): string {
    if (this.isWrapperReserveHolder(holder)) {
      return 'Wrapper reserve';
    }
    if (holder.ownerLabel) {
      return holder.ownerLabel;
    }
    if (holder.ownerAddress) {
      return holder.ownerAddress;
    }
    return (
      holder.displayOwner || shortId(holder.owner ?? holder.ownerIdentifier)
    );
  }

  /** Kaspa addresses (kaspa:/kaspatest:/…) truncate via the wallet pipe. */
  holderLabelUsesAddressTruncate(holder: Kcc20TokenHolder): boolean {
    if (holder.ownerLabel || this.isWrapperReserveHolder(holder)) {
      return false;
    }
    const address = holder.ownerAddress?.trim();
    return !!address && isKaspaAddressFormat(address);
  }

  /** Full value copied when the row's copy control is clicked. */
  holderCopyValue(holder: Kcc20TokenHolder): string | null {
    if (this.isWrapperReserveHolder(holder)) {
      return (
        this.tokenDetail()?.wrapper?.wrapperAddress?.trim() ||
        holder.owner ||
        holder.ownerIdentifier ||
        null
      );
    }
    if (holder.ownerAddress?.trim()) {
      return holder.ownerAddress.trim();
    }
    if ((holder.owner ?? holder.ownerIdentifier)?.trim()) {
      return (holder.owner ?? holder.ownerIdentifier).trim();
    }
    return null;
  }

  headerIdCopied(kind: 'token' | 'market'): boolean {
    return this.copiedHeaderId() === kind;
  }

  copyHeaderId(kind: 'token' | 'market', value: string): void {
    if (!navigator.clipboard || !value) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.copiedHeaderId.set(kind);
      setTimeout(() => {
        if (this.copiedHeaderId() === kind) {
          this.copiedHeaderId.set(null);
        }
      }, 1500);
    });
  }

  holderCopyCopied(rank: number): boolean {
    return this.copiedHolderRank() === rank;
  }

  copyHolderValue(rank: number, value: string): void {
    if (!navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.copiedHolderRank.set(rank);
      setTimeout(() => {
        if (this.copiedHolderRank() === rank) {
          this.copiedHolderRank.set(null);
        }
      }, 1500);
    });
  }

  activityWhoCopyCopied(key: string): boolean {
    return this.copiedActivityWhoKey() === key;
  }

  copyActivityWhoValue(key: string, value: string): void {
    if (!navigator.clipboard) {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.copiedActivityWhoKey.set(key);
      setTimeout(() => {
        if (this.copiedActivityWhoKey() === key) {
          this.copiedActivityWhoKey.set(null);
        }
      }, 1500);
    });
  }

  holderOwnerTitle(holder: Kcc20TokenHolder): string {
    const copyValue = this.holderCopyValue(holder);
    if (copyValue && copyValue !== this.holderDisplayLabel(holder)) {
      return `${this.holderDisplayLabel(holder)} · ${copyValue}`;
    }
    return copyValue || this.holderDisplayLabel(holder);
  }

  holderShareLabel(holder: Kcc20TokenHolder): string {
    if (!holder.sharePercent) {
      return '--';
    }
    const share = Number(holder.sharePercent);
    if (!Number.isFinite(share)) {
      return '--';
    }
    if (share > 0 && share < 0.01) {
      return '<0.01%';
    }
    return `${share.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    })}%`;
  }

  private isWrapperReserveHolder(holder: Kcc20TokenHolder): boolean {
    const wrapper = this.tokenDetail()?.wrapper;
    const ownerIdentifier =
      typeof (holder.owner ?? holder.ownerIdentifier) === 'string'
        ? (holder.owner ?? holder.ownerIdentifier).toLowerCase()
        : '';
    if (!ownerIdentifier) {
      return false;
    }
    const matchesWrapper = [
      wrapper?.wrapperId,
      wrapper?.marketId,
      wrapper?.activeCovenantId,
    ].some((id) => id?.toLowerCase() === ownerIdentifier);
    return (
      matchesWrapper &&
      ((holder.ownerScheme ?? holder.identifierType) === 4 ||
        holder.ownerKind === 'covenant-id/v1' ||
        holder.identifierTypeName === 'covenant-id/v1' ||
        (holder.ownerLabel || '').toLowerCase() === 'wrapper reserve')
    );
  }

  /**
   * Activity columns (KCC20-43): the token column is dropped (the user is
   * already on this token's page); what's left is a clear action label, who
   * did it, the token amount + price breakdown (unit price, total value),
   * the transaction id, and the time.
   */
  activityColumns(
    actionTemplate: TokenActivityCellTemplate,
    whoTemplate: TokenActivityCellTemplate,
    amountTemplate: TokenActivityCellTemplate,
    unitPriceTemplate: TokenActivityCellTemplate,
    totalTemplate: TokenActivityCellTemplate,
    txTemplate: TokenActivityCellTemplate,
    timeTemplate: TokenActivityCellTemplate,
  ): TableColumn<TokenActivityRow>[] {
    return [
      {
        key: 'action',
        label: 'Action',
        width: '16%',
        template: actionTemplate,
      },
      {
        key: 'who',
        label: 'Who',
        width: '11%',
        template: whoTemplate,
      },
      {
        key: 'amount',
        label: 'Amount',
        width: '15%',
        template: amountTemplate,
      },
      {
        key: 'unitPrice',
        label: 'Price (KAS)',
        width: '14%',
        template: unitPriceTemplate,
      },
      {
        key: 'totalValue',
        label: 'Total (KAS)',
        width: '14%',
        template: totalTemplate,
      },
      {
        key: 'txid',
        label: 'Tx',
        width: '9%',
        template: txTemplate,
      },
      {
        key: 'timeMs',
        label: 'Time',
        width: '16%',
        sortable: true,
        defaultSortDirection: 'desc',
        template: timeTemplate,
      },
    ];
  }

  trackActivity(_: number, item: TokenActivityRow): string {
    return item.key;
  }

  /** Compact tx preview — full id is on the explorer link / aria-label. */
  activityTxShort(txid?: string | null): string {
    if (!txid) {
      return '--';
    }
    if (txid.length <= 10) {
      return txid;
    }
    return `${txid.slice(0, 4)}…${txid.slice(-4)}`;
  }

  private async optional<T>(load: () => Promise<T>): Promise<T | undefined> {
    try {
      return await load();
    } catch {
      return undefined;
    }
  }

  private toTokenLink(
    label: string,
    href: string | undefined,
    icon: TokenSocialIcon,
  ): TokenSocialLink | undefined {
    const trimmed = href?.trim();
    return trimmed && isValidMetadataUrl(trimmed)
      ? { label, href: trimmed, icon }
      : undefined;
  }

  private positionCanonicalBalance(): string | undefined {
    const position = this.ownerPosition();
    if (
      position?.canonicalTokenIdHex?.toLowerCase() !==
      this.covenantId().toLowerCase()
    ) {
      return undefined;
    }
    return position.nativeBalance ?? position.canonicalBalance;
  }

  private addTokenAmounts(
    ...amounts: Array<string | null | undefined>
  ): string | undefined {
    let total = 0n;
    let hasAmount = false;
    for (const amount of amounts) {
      const trimmed = amount?.trim();
      if (!trimmed || !/^\d+$/.test(trimmed)) {
        continue;
      }
      total += BigInt(trimmed);
      hasAmount = true;
    }
    return hasAmount ? total.toString() : undefined;
  }

  private syncWrapPrompt(): void {
    if (
      this.needsWrapToTrade() &&
      this.quoteSide() === 'sell' &&
      this.actionTab() === 'book' &&
      !this.activeFillOrderId()
    ) {
      this.wrapMode.set('wrap');
      this.actionTab.set('wrap');
      this.tradeMessage.set(WRAP_TO_TRADE_MESSAGE);
    }
  }

  /**
   * Fetches one page (`limit`/`offset`) of merged activity across the
   * covenant and (if trading is wrapped) market ids, plus one extra row
   * past the page boundary so the caller can tell whether a next page
   * actually exists (a full page alone doesn't prove that — the last page
   * can land exactly on `limit` rows). The backend has no single unified
   * activity endpoint across both ids, so each source is fetched from its
   * own start (offset 0) through this page's combined end plus that extra
   * row, merged, sorted, and only then sliced to the requested window.
   * Paging each source independently at the *same* offset would be wrong:
   * rows from one source that sort into this page could sit behind the
   * other source's per-source offset boundary and get skipped entirely.
   * Returns up to `limit + 1` rows — the caller trims to `limit` for
   * display and treats the extra row as the next-page signal.
   */
  private async loadScopedActivity(
    covenantId: string,
    marketId: string | undefined,
    scope: Extract<Kcc20ActivityScope, 'trading' | 'token'>,
    options: { limit: number; offset?: number; refresh?: boolean },
  ): Promise<Kcc20Activity[] | undefined> {
    const ids = Array.from(new Set([marketId, covenantId].filter(Boolean)));
    const offset = options.offset ?? 0;
    const perSourceLimit = offset + options.limit + 1;
    const batches = await Promise.all(
      ids.map((id) =>
        this.optional(() =>
          firstValueFrom(
            this.tradingApi.getActivity(id as string, {
              limit: perSourceLimit,
              offset: 0,
              scope,
              refresh: options.refresh,
            }),
          ),
        ),
      ),
    );

    // Every request failed (all `optional()` returned undefined): signal "could
    // not load" so the caller can keep stale rows, rather than reporting [].
    if (batches.every((batch) => batch === undefined)) {
      return undefined;
    }

    const merged = new Map<string, Kcc20Activity>();
    for (const event of batches.flatMap((batch) => batch ?? [])) {
      const key = [
        event.txidHex,
        event.orderId ?? '',
        event.entrypoint ?? event.kind,
        event.tokenAmount,
      ].join(':');
      merged.set(key, event);
    }

    return Array.from(merged.values())
      .sort((a, b) => b.blockTimeMs - a.blockTimeMs)
      .slice(offset, offset + options.limit + 1);
  }

  private toActivityRow(event: Kcc20Activity): TokenActivityRow {
    const entrypoint = (event.entrypoint || '').toLowerCase();
    const kind = (event.kind || '').toLowerCase();
    const orderSide = (event.orderSide || '').toLowerCase();
    const takerSide = (event.takerSide || '').toLowerCase();
    const actionSource = `${entrypoint}:${kind}:${orderSide}:${takerSide}`;
    const type = kcc20ActivityType(actionSource);
    const semanticSide = kcc20ActivitySemanticSide(type, {
      orderSide,
      takerSide,
      entrypoint,
      kind,
    });

    const whoFull = activityDisplayOwner(event);
    const whoUsesAddressTruncate = !!whoFull && isKaspaAddressFormat(whoFull);
    // Mint rows have no trade price to report — the indexer doesn't stamp
    // one on mint events — so use the token's current mint price per token
    // instead; the total is still derived from unit price x amount below.
    const unitPriceSompi =
      (type === 'Mint'
        ? this.tokenDetail()?.mintPolicy?.mintPricePerTokenSompi
        : undefined) ||
      event.unitPriceSompi ||
      this.deriveUnitPriceSompi(event);
    // Always derive the displayed total from the unit price x amount we're
    // also displaying, rather than trusting `event.priceSompi` blindly — the
    // two must agree on screen, and computing both from the same unit price
    // guarantees that regardless of what the backend's own total reflects.
    // Only fall back to the backend's raw total when we have no unit price
    // to derive one from at all.
    const totalSompi =
      this.deriveTotalSompi(unitPriceSompi, event.tokenAmount) ??
      event.priceSompi;

    return {
      key: [
        event.txidHex,
        event.orderId ?? '',
        event.entrypoint ?? event.kind,
        event.tokenAmount,
      ].join(':'),
      tokenId: event.tokenIdHex,
      type,
      action: kcc20ActivityAction(type, semanticSide),
      direction: kcc20ActivityDirection(type, semanticSide),
      who: whoFull ? activityDisplayOwnerLabel(whoFull) : '--',
      whoFull: whoFull || '--',
      whoUsesAddressTruncate,
      side: semanticSide || '--',
      amount: formatKcc20BaseUnitsValueGrouped(
        event.tokenAmount,
        this.tokenDecimals(),
        '--',
        2,
      ),
      unitPrice: this.formatKasFromSompi(unitPriceSompi, 4),
      totalValue: this.formatKasFromSompi(totalSompi, 2),
      txid: event.txidHex,
      timeMs: event.blockTimeMs,
    };
  }

  /**
   * Fallback for activity rows the backend didn't stamp a `unitPriceSompi`
   * on (older events, edge cases) — derived from the total value and token
   * amount it did send. `tokenAmount` is base units, so scale by decimals to
   * get a per-whole-token price instead of a per-base-unit one.
   */
  private deriveUnitPriceSompi(event: Kcc20Activity): string | undefined {
    if (!event.priceSompi || !event.tokenAmount) {
      return undefined;
    }
    try {
      const totalSompi = BigInt(event.priceSompi);
      const amountBaseUnits = BigInt(event.tokenAmount);
      if (amountBaseUnits <= 0n) {
        return undefined;
      }
      const scale = 10n ** BigInt(this.tokenDecimals());
      return ((totalSompi * scale) / amountBaseUnits).toString();
    } catch {
      return undefined;
    }
  }

  /** Inverse of {@link deriveUnitPriceSompi}: unit price x amount, in sompi. */
  private deriveTotalSompi(
    unitPriceSompi: string | undefined,
    tokenAmountBaseUnits: string,
  ): string | undefined {
    if (!unitPriceSompi || !tokenAmountBaseUnits) {
      return undefined;
    }
    try {
      const unitPrice = BigInt(unitPriceSompi);
      const amountBaseUnits = BigInt(tokenAmountBaseUnits);
      const scale = 10n ** BigInt(this.tokenDecimals());
      return ((unitPrice * amountBaseUnits) / scale).toString();
    } catch {
      return undefined;
    }
  }

  /** Bare (no "KAS" suffix), decimal-capped, comma-grouped KAS amount from a sompi string. */
  private formatKasFromSompi(
    sompi: string | undefined,
    maxDecimals: number,
  ): string {
    if (!sompi) {
      return '--';
    }
    return formatPrettyNumber(sompiToKas(sompi), { maxDecimals });
  }

  /** Covenant explorer link for a transaction id, network-aware. */
  explorerTxUrl(txid: string): string {
    return kaspaExplorerTxUrl(txid);
  }

  private async loadMetadataPermissionValue(
    covenantId: string,
  ): Promise<Kcc20TokenMetadataPermission | undefined> {
    if (!this.owner()) {
      return undefined;
    }

    return this.optional(() =>
      firstValueFrom(this.metadataApi.getPermissions(covenantId)),
    );
  }

  private quoteRequestSnapshot():
    | {
        key: string;
        marketId: string;
        side: QuoteSide;
        amount: string;
        orderId: string | undefined;
        mode: OrderMode;
        limitUnitPriceSompi: string | undefined;
      }
    | undefined {
    const marketId = this.marketId();
    const amount = this.quoteAmount().trim();
    if (
      !marketId ||
      !this.wrapperTradingReady() ||
      !this.isPositiveDecimal(amount)
    ) {
      return undefined;
    }
    const side = this.quoteSide();
    // No orderbook balance yet — skip /quote entirely so the ticket does not spin
    // and then restate wrap-to-trade after a useless market lookup.
    if (side === 'sell' && this.needsWrapToTrade()) {
      return undefined;
    }
    const orderId = this.selectedOrderId();
    const mode = this.orderMode();
    const limitUnitPriceSompi =
      mode === 'limit' ? this.orderUnitPriceSompi().trim() : undefined;
    if (
      mode === 'limit' &&
      !this.isPositiveInteger(limitUnitPriceSompi || '')
    ) {
      return undefined;
    }
    return {
      key: `${marketId}|${side}|${amount}|${orderId ?? mode}|${limitUnitPriceSompi ?? ''}`,
      marketId,
      side,
      amount,
      orderId,
      mode,
      limitUnitPriceSompi,
    };
  }

  /**
   * True when the live quote still describes the amount currently in the
   * ticket. Used to avoid rendering matched-order totals from a previous
   * amount while a newer quote is in flight.
   */
  private quoteMatchesCurrentAmount(): boolean {
    const quote = this.quote();
    const ticketAmount = this.quoteAmount().trim();
    const quotedAmount = quote?.requestedAmount?.trim();
    if (!quote || !ticketAmount || !quotedAmount) {
      return false;
    }
    if (quote.side !== this.quoteSide()) {
      return false;
    }
    const decimals = this.tokenDecimals();
    const ticketBase = parseKcc20DisplayAmountToBaseUnits(
      ticketAmount,
      decimals,
    );
    const quotedBase = parseKcc20DisplayAmountToBaseUnits(
      quotedAmount,
      decimals,
    );
    return (
      ticketBase !== null && quotedBase !== null && ticketBase === quotedBase
    );
  }

  private clearStaleQuoteForCurrentAmount(): void {
    if (this.quoteMatchesCurrentAmount()) {
      return;
    }
    if (this.quote()) {
      this.quote.set(undefined);
    }
    this.lastQuoteRequestKey = undefined;
    // Market mode copies unit price from the quote into the ticket. Clear that
    // copy too, or the summary falls back to "new amount × old matched price".
    if (this.orderMode() === 'market' && !this.selectedOrderId()) {
      this.orderUnitPriceKas.set('');
      this.orderUnitPriceSompi.set('');
    }
  }

  private requestQuoteRefresh(immediate = false): void {
    // Flip to loading as soon as a refresh is requested, not just once the
    // debounce elapses and the HTTP call actually starts. Otherwise the
    // debounce window has `quote()` already cleared (by
    // `clearStaleQuoteForCurrentAmount`) but `quoteLoading()` still false,
    // which briefly satisfies `marketQuoteBlockedReason`'s "no quote yet"
    // branch and flashes a "Refresh the market quote…" toast on every
    // keystroke until the request itself takes over the loading state.
    //
    // Skip that when the current ticket already matches the last fetched
    // quote (e.g. a blur with no actual amount change): that request gets
    // deduped by `distinctUntilChanged` before it ever reaches the branch
    // in the pipeline below that would flip `quoteLoading` back off, which
    // would otherwise leave the Build button stuck loading/disabled.
    //
    // Empty / invalid tickets must not enter the loading state at all: the
    // pipeline dedupes repeated "nothing quotable" snapshots, so a second
    // refresh (page load + mode toggle, blur, etc.) would set loading true
    // and never clear it — infinite spinner on Buy/Sell with no amount.
    const snapshot = this.quoteRequestSnapshot();
    if (!snapshot) {
      this.quote.set(undefined);
      this.quoteLoading.set(false);
      this.lastQuoteRequestKey = undefined;
      this.quoteRefresh$.next(immediate ? 0 : this.quoteRefreshDebounceMs);
      return;
    }
    const alreadyFresh =
      snapshot.key === this.lastQuoteRequestKey && !!this.quote();
    if (!alreadyFresh) {
      this.quoteLoading.set(true);
    }
    this.quoteRefresh$.next(immediate ? 0 : this.quoteRefreshDebounceMs);
  }

  private isPositiveDecimal(value: string): boolean {
    return isPositiveKcc20DisplayAmount(value, this.tokenDecimals());
  }

  /** True for indexer/base-unit balance strings (not display-decimal inputs). */
  private isPositiveBaseAmount(value: string | null | undefined): boolean {
    const raw = value?.trim();
    return !!raw && /^\d+$/.test(raw) && BigInt(raw) > 0n;
  }

  private isPositiveInteger(value: string): boolean {
    return /^\d+$/.test(value.trim()) && BigInt(value.trim()) > 0n;
  }

  private isNonNegativeInteger(value: string): boolean {
    return /^\d+$/.test(value.trim()) && BigInt(value.trim()) >= 0n;
  }

  private parseSompi(value: string | null | undefined): bigint | undefined {
    const raw = value?.trim();
    return raw && /^\d+$/.test(raw) ? BigInt(raw) : undefined;
  }

  private effectiveFeeTicketDiscountedProtocolFeeSompi(
    quote: Kcc20Quote | null | undefined,
  ): string | undefined {
    const explicit = quote?.feeTicketDiscountedProtocolFeeSompi?.trim();
    if (explicit && /^\d+$/.test(explicit)) {
      return explicit;
    }
    if (
      quote &&
      this.isMultiSweepQuote(quote) &&
      feeTicketDiscountedProtocolFeeSompiForSweepQuote(
        quote,
        this.activeWrapper()?.feeTicketId,
      ) !== undefined
    ) {
      return '0';
    }
    return undefined;
  }

  private quoteWithEffectiveFeeTicketDiscount(
    quote: Kcc20Quote | null | undefined,
  ): Kcc20Quote | null | undefined {
    const discountedProtocolFeeSompi =
      this.effectiveFeeTicketDiscountedProtocolFeeSompi(quote);
    if (!quote || discountedProtocolFeeSompi === undefined) {
      return quote;
    }
    if (
      quote.feeTicketDiscountedProtocolFeeSompi === discountedProtocolFeeSompi
    ) {
      return quote;
    }
    return {
      ...quote,
      feeTicketDiscountedProtocolFeeSompi: discountedProtocolFeeSompi,
    };
  }

  private isHex64(value: string): boolean {
    return /^[a-fA-F0-9]{64}$/.test(value);
  }

  private trimDecimal(value: number): string {
    return value.toFixed(8).replace(/\.?0+$/, '');
  }

  /** Preset/slider amount string capped at trade-ticket decimal precision. */
  private trimTradeAmount(value: number): string {
    if (!Number.isFinite(value) || value <= 0) {
      return '';
    }
    return this.clampTradeAmountDecimals(
      value.toFixed(TokenPageComponent.TRADE_AMOUNT_MAX_DECIMALS),
    );
  }

  /**
   * Cap a finished trade-amount string at 3 decimals (trailing zeros stripped).
   * Used for presets, blur, and orderbook prefill — not while the user is
   * mid-typing a trailing dot.
   */
  private clampTradeAmountDecimals(value: string): string {
    const raw = value.trim();
    if (!raw || !/^\d*\.?\d*$/.test(raw)) {
      return raw;
    }
    const [wholePart = '0', fractionPart] = raw.split('.');
    if (fractionPart === undefined) {
      return wholePart || '0';
    }
    const clipped = fractionPart
      .slice(0, TokenPageComponent.TRADE_AMOUNT_MAX_DECIMALS)
      .replace(/0+$/, '');
    return clipped ? `${wholePart || '0'}.${clipped}` : wholePart || '0';
  }

  /** Live input clamp: keep at most 3 fraction digits, allow a trailing `.`. */
  private clampTradeAmountInput(value: string): string {
    const raw = value.trim();
    if (!raw) {
      return raw;
    }
    if (!/^\d*\.?\d*$/.test(raw)) {
      return raw;
    }
    if (raw.endsWith('.') && !raw.slice(0, -1).includes('.')) {
      const whole = raw.slice(0, -1) || '0';
      return `${whole}.`;
    }
    return this.clampTradeAmountDecimals(raw);
  }

  private requestId(scope: string): string {
    return `kcc20-fe-${scope}-${Date.now()}`;
  }

  private syncWatchlistState(): void {
    const owner = this.owner();
    if (!owner) {
      this.watchlistState.clear();
      return;
    }
    void this.watchlistState
      .load(owner, this.requestId('watchlist-load'))
      .catch(() =>
        this.notificationService.error('Watchlist', 'Watchlist unavailable.'),
      );
  }

  private isMultiSweepQuote(
    quote: Kcc20Quote | null | undefined,
  ): quote is Kcc20Quote & {
    executionMode: 'multi';
    fills: NonNullable<Kcc20Quote['fills']>;
  } {
    return sweepQuoteBlockedReason(this.quoteSide(), quote) === undefined;
  }
}

export function sweepQuoteBlockedReason(
  side: QuoteSide,
  quote: Kcc20Quote | null | undefined,
): string | undefined {
  if (
    !quote?.valid ||
    quote.side !== side ||
    quote.executionMode !== 'multi' ||
    !Array.isArray(quote.fills) ||
    quote.fills.length < 2
  ) {
    return 'This amount spans multiple orders, but the route is not ready yet.';
  }
  if (
    side === 'sell' &&
    quote.fills.length > KCC20_MAX_SINGLE_HOLDER_SWEEP_BID_LEGS
  ) {
    return `Market sell can fill at most ${KCC20_MAX_SINGLE_HOLDER_SWEEP_BID_LEGS} bids per transaction. Enter a smaller amount and refresh the quote.`;
  }
  return undefined;
}

/** Map backend quote validation strings to ticket-facing copy. */
export function humanizeQuoteError(
  error: string | null | undefined,
): string | undefined {
  const raw = error?.trim();
  if (!raw) {
    return undefined;
  }
  const normalized = raw.toLowerCase();
  if (normalized.includes('matching open order liquidity')) {
    return 'Not enough open liquidity for this amount. Reduce the size or tap Max.';
  }
  return raw;
}

export function normalizeFeeTicketRootId(
  value: string | null | undefined,
): string | undefined {
  const normalized = value?.trim().toLowerCase();
  if (
    !normalized ||
    !/^[a-f0-9]{64}$/.test(normalized) ||
    /^0{64}$/.test(normalized)
  ) {
    return undefined;
  }
  return normalized;
}

export function feeTicketRootIdForSweepQuote(
  quote: Kcc20Quote | null | undefined,
  wrapperFeeTicketId: string | null | undefined,
): string | undefined {
  if (
    !quote?.valid ||
    quote.executionMode !== 'multi' ||
    !Array.isArray(quote.fills) ||
    quote.fills.length < 2
  ) {
    return undefined;
  }

  const wrapperRootId = normalizeFeeTicketRootId(wrapperFeeTicketId);
  const fillRootIds = quote.fills
    .map((fill) => normalizeFeeTicketRootId(fill.feeTicketId))
    .filter((rootId): rootId is string => !!rootId);

  if (wrapperRootId) {
    if (fillRootIds.length === 0) {
      return wrapperRootId;
    }
    return fillRootIds.length === quote.fills.length &&
      fillRootIds.every((rootId) => rootId === wrapperRootId)
      ? wrapperRootId
      : undefined;
  }

  if (fillRootIds.length !== quote.fills.length) {
    return undefined;
  }
  const [firstRootId] = fillRootIds;
  return firstRootId && fillRootIds.every((rootId) => rootId === firstRootId)
    ? firstRootId
    : undefined;
}

export function feeTicketDiscountedProtocolFeeSompiForSweepQuote(
  quote: Kcc20Quote | null | undefined,
  wrapperFeeTicketId: string | null | undefined,
): string | undefined {
  const protocolFeeSompi = quote?.protocolFeeSompi?.trim();
  if (
    !protocolFeeSompi ||
    !/^\d+$/.test(protocolFeeSompi) ||
    !feeTicketRootIdForSweepQuote(quote, wrapperFeeTicketId)
  ) {
    return undefined;
  }
  return '0';
}

/**
 * Whether a quote request that matches the previous one can be skipped.
 *
 * Identical inputs alone are NOT enough: after an order is placed the ticket
 * keeps the user's amount and limit price (#238), so the request key is
 * unchanged while the quote itself has been dropped as stale. Skipping on the
 * key alone left a populated ticket with no quote and a dead order button.
 * Only skip when we still hold the quote the repeat request would produce.
 */
export function shouldSkipRepeatQuoteRequest(
  previousKey: string | undefined,
  nextKey: string | undefined,
  hasQuote: boolean,
): boolean {
  if (previousKey !== nextKey) {
    return false;
  }
  // An absent key means the ticket has nothing quotable in it, so there is no
  // request to re-issue — a repeat is redundant whether or not a quote is held.
  // Only a real repeated request needs the quote check.
  return nextKey === undefined || hasQuote;
}

export function quoteFillOrderId(
  side: QuoteSide,
  quote:
    | Pick<
        Kcc20Quote,
        | 'valid'
        | 'side'
        | 'orderId'
        | 'unitPriceSompi'
        | 'requestedAmount'
        | 'executionMode'
      >
    | null
    | undefined,
  unitPriceSompi: string | null | undefined,
  requestedAmount?: string | null | undefined,
  decimals = KCC20_FIXED_DECIMALS,
): string | undefined {
  const quotedOrderId = quote?.orderId?.trim();
  const quotedUnitPrice = quote?.unitPriceSompi?.trim();
  const ticketUnitPrice = unitPriceSompi?.trim();
  const quotedAmount = quote?.requestedAmount?.trim();
  const ticketAmount = requestedAmount?.trim();
  if (
    !quote?.valid ||
    quote.side !== side ||
    quote.executionMode === 'multi' ||
    !quotedOrderId ||
    !quotedUnitPrice ||
    !ticketUnitPrice ||
    !/^(0|[1-9][0-9]*)$/.test(quotedUnitPrice) ||
    !/^(0|[1-9][0-9]*)$/.test(ticketUnitPrice) ||
    quotedUnitPrice === '0' ||
    ticketUnitPrice === '0'
  ) {
    return undefined;
  }
  if (ticketAmount && quotedAmount) {
    const ticketAmountBaseUnits = parseKcc20DisplayAmountToBaseUnits(
      ticketAmount,
      normalizeKcc20Decimals(decimals),
    );
    const quotedAmountBaseUnits = parseKcc20DisplayAmountToBaseUnits(
      quotedAmount,
      normalizeKcc20Decimals(decimals),
    );
    if (
      ticketAmountBaseUnits === null ||
      quotedAmountBaseUnits === null ||
      ticketAmountBaseUnits !== quotedAmountBaseUnits
    ) {
      return undefined;
    }
  }

  const matched = BigInt(quotedUnitPrice);
  const limit = BigInt(ticketUnitPrice);
  const crosses = side === 'buy' ? limit >= matched : limit <= matched;
  return crosses ? quotedOrderId : undefined;
}

/**
 * Fallback readiness check used only for the brief window before
 * `/tokens/:id/page-actions` has resolved (see `wrapActionReady`). Mirrors
 * the backend's wrap rule, which — unlike unwrap — allows building against a
 * still-`pendingReveal` / unrevealed wrapper as long as it's already
 * `enabled`, `buildable`, and has an active wrapper UTXO. First wrap reveals
 * the wrapper, so this must not require `activeCovenantId`.
 */
export function wrapperWrapHeuristicReady(
  wrapper: Kcc20WrapperSummary | null | undefined,
): boolean {
  return wrapperReadyToBuild(wrapper);
}

/**
 * Fallback readiness check used only for the brief window before
 * `/tokens/:id/page-actions` has resolved (see `unwrapActionReady`). Unlike
 * wrap, the backend never allows unwrapping while the wrapper is still
 * `pendingReveal`, regardless of `buildable`.
 */
export function wrapperUnwrapHeuristicReady(
  wrapper: Kcc20WrapperSummary | null | undefined,
): boolean {
  if (!wrapper) {
    return false;
  }
  return (
    wrapper.enabled &&
    !!wrapper.activeCovenantId &&
    wrapper.pendingReveal !== true &&
    wrapper.buildable !== false
  );
}
